import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { useMonthlyPerformance } from '../overviewV9/hooks/useMonthlyPerformance';
import { HedeflenenCirTrafikTable } from '../overviewV9/components/historical/HedeflenenCirTrafikTable';

export default function BrandingPlanHigh5() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || (typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null);
  const monthly = useMonthlyPerformance(brandId, 'all', null, null);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10">

      {/* ───────── Page Header ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="bg-[#111] p-6 text-white flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-5">
            <div className="bg-white rounded-lg px-4 py-2">
              <img src="/high5logo.svg" alt="High5" className="h-9 w-auto object-contain" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">High5</h1>
              <p className="text-sm text-neutral-400 mt-0.5">Konkur Hazırlık Dokümanı — Dijital Pazarlama Stratejisi</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center px-3 py-1 bg-white/10 text-xs font-medium rounded-full border border-white/20">high5.com.tr</span>
            <span className="inline-flex items-center px-3 py-1 bg-white/10 text-xs font-medium rounded-full border border-white/20">Premium Spor Retail</span>
          </div>
        </div>
      </div>

      {/* ───────── Brand Snapshot ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Marka Özeti</h2>
        </div>
        <div className="p-6">
          <p className="text-sm text-gray-700 leading-relaxed mb-6">
            <strong>High5</strong>, premium spor markaları ve deneyim odaklı yaklaşımıyla koşu, yoga, fitness ve outdoor tutkunlarına özel ayrıcalıklar sunan bir perakende platformudur. 
            Bünyesinde <strong>On, ASICS, HOKA, Brooks, Salomon, Skechers</strong> gibi dünya çapında lider performans markalarını barındırır.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            {['On', 'ASICS', 'HOKA', 'Brooks', 'Salomon', 'Skechers'].map(brand => (
              <div key={brand} className="rounded-lg border p-3 text-center">
                <span className="text-sm font-bold text-gray-900">{brand}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-lg border p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Rakipler</p>
              <p className="text-sm text-gray-900 font-medium">SPX · The Moose Bay · Alpinist · Intersport · Sportive</p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Konumlandırma</p>
              <p className="text-sm text-gray-900 font-medium">Premium niş perakende — performans odaklı spor yaşam markası</p>
            </div>
          </div>
        </div>
      </div>

      {/* ───────── Hedef Kitle Özellikleri ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Hedef Kitle Özellikleri</h2>
        </div>
        <div className="p-6">
          {/* Demografik & Profesyonel Profil */}
          <div className="rounded-lg border p-5 mb-5">
            <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wider">Demografik & Profesyonel Profil</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-sm text-gray-700">
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Yaş Aralığı</p>
                <p><strong>25–45</strong> ağırlıklı, çekirdek segment <strong>28–38</strong></p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Cinsiyet</p>
                <p>Kadın <strong>55%</strong> — Erkek <strong>45%</strong></p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Gelir Segmenti</p>
                <p>Üst-orta ve üst gelir grubu (<strong>A, B+</strong>)</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Eğitim</p>
                <p>Üniversite mezunu ve üstü — <strong>beyaz yaka</strong> çalışan profili</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Lokasyon</p>
                <p>İstanbul, Ankara, İzmir, Antalya — büyükşehir merkezli</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Meslek Profili</p>
                <p>Yazılım, finans, hukuk, sağlık, kreatif sektör profesyonelleri, girişimciler</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            {/* Psikografik */}
            <div className="rounded-lg border p-5">
              <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider">Psikografik Profil</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>· Sağlıklı yaşamı <strong>bir kimlik</strong> olarak benimser — sadece spor değil, yaşam tarzı</li>
                <li>· Marka bilinçli — <strong>ürünün hikayesi</strong>, teknolojisi ve arkasındaki felsefe önemli</li>
                <li>· Fiyat hassasiyeti düşük; <strong>"en ucuz"</strong> değil <strong>"en doğru"</strong> olanı arar</li>
                <li>· Sosyal medyada aktif, <strong>topluluk aidiyeti</strong> ve ortak değerler arar</li>
                <li>· Sürdürülebilirlik, bilinçli tüketim, çevre duyarlılığı taşır</li>
                <li>· <strong>Self-improvement</strong> odaklı — kendine yatırımı harcama değil, gelişim olarak görür</li>
                <li>· Minimalist / "less is more" yaklaşımı — az ama kaliteli ürün tercih eder</li>
              </ul>
            </div>
            {/* Davranışsal */}
            <div className="rounded-lg border p-5">
              <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider">Satın Alma Davranışı</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>· Ortalama sepet tutarı <strong>₺3.000–₺8.000</strong> aralığında</li>
                <li>· Yılda <strong>3–5 kez</strong> premium spor ürünü satın alır</li>
                <li>· Sezon başı ve yeni koleksiyon lansmanlarında satın alma pikleri</li>
                <li>· Satın alma öncesi <strong>detaylı araştırma</strong> — YouTube review, Reddit, blog karşılaştırması</li>
                <li>· Mobil ağırlıklı trafik (<strong>%70+</strong>), Instagram ve YouTube'dan etkilenir</li>
                <li>· <strong>Arkadaş/topluluk referansı</strong> satın alma kararında çok etkili</li>
                <li>· İade oranı düşük — bilinçli seçim yapar, araştırarak alır</li>
                <li>· Sadakat eğilimi yüksek — memnun kaldığı markadan tekrar alır</li>
              </ul>
            </div>
          </div>

          {/* Hedeflenebilir İlgi Alanları — Genişletilmiş */}
          <div className="rounded-lg border p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-1 uppercase tracking-wider">Hedeflenebilir İlgi Alanları & Sinyaller</h3>
            <p className="text-xs text-gray-400 mb-4">Dijital reklamlarda targeting, lookalike ve custom audience oluşturmada kullanılabilir sinyaller</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <p className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wider">Spor & Aktivite</p>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li>· Koşu (yol koşusu, trail running, maraton)</li>
                  <li>· Yoga, pilates, barre, reformer pilates</li>
                  <li>· CrossFit, fonksiyonel antrenman, HIIT</li>
                  <li>· Hiking, trekking, dağcılık</li>
                  <li>· Yüzme, bisiklet, triatlon</li>
                  <li>· Boutique spor salonu üyelikleri (Base, Reformist, Fizy vb.)</li>
                </ul>
              </div>

              <div>
                <p className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wider">Uygulama & Teknoloji</p>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li>· <strong>Strava</strong>, Nike Run Club, Garmin Connect kullanıcıları</li>
                  <li>· Apple Watch / Garmin / COROS akıllı saat sahipleri</li>
                  <li>· Calm, Headspace, Seven gibi wellness uygulamaları</li>
                  <li>· MyFitnessPal, Yazio gibi beslenme takip uygulamaları</li>
                  <li>· Komoot, AllTrails gibi rota planlama uygulamaları</li>
                </ul>
              </div>

              <div>
                <p className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wider">Beslenme & Yaşam Tarzı</p>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li>· Sağlıklı beslenme videosu izleyenler</li>
                  <li>· <strong>Yağsız fıstık ezmesi</strong>, protein bar, granola arayanlar</li>
                  <li>· Organik market alışverişi yapanlar (Whole Foods, Bi Organik vb.)</li>
                  <li>· Supplement araştıranlar (protein tozu, kreatin, vitamin)</li>
                  <li>· Meal-prep, clean eating, makro hesaplama içerik takipçileri</li>
                  <li>· Avokado, açaí bowl, cold-press juice arayanlar</li>
                </ul>
              </div>

              <div>
                <p className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wider">Lokasyon Sinyalleri</p>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li>· <strong>Spor merkezleri</strong> yakınında sık bulunanlar</li>
                  <li>· Koşu parkurları çevresinde yaşayanlar (Caddebostan, Bebek, Konyaaltı)</li>
                  <li>· AVM'lerin premium katlarında zaman geçirenler</li>
                  <li>· Outdoor mağazaları ziyaret edenler</li>
                  <li>· Maslak, Levent, Bağdat Caddesi, Alsancak bölgesi sakinleri</li>
                  <li>· Doğa parkurları / milli park ziyaretçileri</li>
                </ul>
              </div>

              <div>
                <p className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wider">Etkinlik & Topluluk</p>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li>· İstanbul Maratonu, Runatolia, RunTurkey katılımcıları</li>
                  <li>· Salomon Cappadocia Ultra Trail takipçileri</li>
                  <li>· parkrun Türkiye haftalık koşucuları</li>
                  <li>· Yerel koşu kulüpleri (Adım Adım, Rungang, Nike Run Club İst.)</li>
                  <li>· Yoga/wellness retreat katılımcıları</li>
                  <li>· Outdoor festivaller (Freeride, Summit vb.)</li>
                </ul>
              </div>

              <div>
                <p className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wider">Medya & İçerik Tüketimi</p>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li>· YouTube: ayakkabı review, "HOKA vs On", running vlog</li>
                  <li>· Instagram: #runningcommunity, #trailrunning, #athleisure</li>
                  <li>· Podcast: koşu, wellness, biohacking, girişimcilik</li>
                  <li>· Blog/Dergi: Runner's World, Trail Magazine, GQ Fitness</li>
                  <li>· TikTok: sneaker unboxing, "what I eat in a day" fitness edition</li>
                  <li>· Reddit: r/RunningShoeGeeks, r/trailrunning</li>
                </ul>
              </div>

              <div>
                <p className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wider">Arama Sinyalleri (Search Intent)</p>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li>· "en iyi koşu ayakkabısı 2026"</li>
                  <li>· "HOKA Bondi 9 vs On Cloudmonster"</li>
                  <li>· "pronasyon ayakkabısı tavsiye"</li>
                  <li>· "salomon trail ayakkabı"</li>
                  <li>· "maraton hazırlık programı"</li>
                  <li>· "premium spor giyim"</li>
                  <li>· "koşucu diz ağrısı ayakkabı"</li>
                </ul>
              </div>

              <div>
                <p className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wider">Satın Alma & Marka Sinyalleri</p>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li>· On, HOKA, ASICS, Brooks, Salomon marka arayanlar</li>
                  <li>· SPX, The Moose Bay, Sportive müşterileri (conquest)</li>
                  <li>· Nike, Adidas, New Balance'dan <strong>premium'a geçiş</strong> yapanlar</li>
                  <li>· Son 30 günde spor ayakkabı kategorisi ziyaret edenler</li>
                  <li>· Garmin / akıllı saat satın almış kişiler</li>
                  <li>· Spor etkinliğine kayıt yaptırmış kişiler</li>
                </ul>
              </div>

              <div>
                <p className="text-xs font-bold text-gray-900 mb-2 uppercase tracking-wider">Yaşam Tarzı Sinyalleri</p>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li>· Beyaz yaka, ofis çalışanı profili</li>
                  <li>· Co-working space kullanıcıları</li>
                  <li>· Specialty coffee mekanlarında check-in yapanlar</li>
                  <li>· Tatil planlamasını doğa/aktif tatil üzerine yapanlar</li>
                  <li>· Premium araç kiralama / elektrikli araç ilgisi</li>
                  <li>· Kindle / kitap okuma uygulaması kullanıcıları</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ───────── Alışveriş Intent Sinyalleri ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Çapraz Alışveriş Intent Sinyalleri</h2>
          <p className="text-xs text-gray-400 mt-1">Bu markalardan / kategorilerden alışveriş yapmış veya ilgi gösteren kullanıcılar, High5 hedef kitlesine yüksek oranda örtüşür</p>
        </div>
        <div className="p-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-1/5">Kategori</th>
                <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-2/5">Marka / Platform Örnekleri</th>
                <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-2/5">Intent Sinyali</th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 font-medium text-gray-900">Premium Moda</td>
                <td className="py-3 px-4">Beymen, Vakko, Network, İpekyol, Machka, Aker</td>
                <td className="py-3 px-4">Premium giyime bütçe ayırıyor — kalite/marka algısı yüksek, fiyat hassasiyeti düşük</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 font-medium text-gray-900">Lüks E-ticaret</td>
                <td className="py-3 px-4">Beymen Club, Trendyol Butik, Farfetch, Net-a-Porter, Mytheresa</td>
                <td className="py-3 px-4">Online premium alışveriş alışkanlığı var — dijital dönüşüme açık müşteri</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 font-medium text-gray-900">Athleisure & Spor Moda</td>
                <td className="py-3 px-4">Lululemon, Nike Premium, Adidas Originals, The North Face, Arc'teryx</td>
                <td className="py-3 px-4">Performans + stil arayışı — tam High5 ürün gamına uygun profil</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 font-medium text-gray-900">Spor Ekipman & Teknoloji</td>
                <td className="py-3 px-4">Garmin, Apple Watch Ultra, COROS, Suunto, Wahoo, Theragun</td>
                <td className="py-3 px-4">Performans takibi yapan ciddi sporcu — yüksek LTV potansiyeli</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 font-medium text-gray-900">Sağlıklı Yaşam & Beslenme</td>
                <td className="py-3 px-4">Eataly, Bi Organik, The Protein Works, iHerb, Supplementler</td>
                <td className="py-3 px-4">Sağlığına yatırım yapıyor — wellness ekosisteminin parçası</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 font-medium text-gray-900">Premium Kahve & Yaşam</td>
                <td className="py-3 px-4">Petra Roasting, Kronotrop, MOC, Starbucks Reserve, Nespresso</td>
                <td className="py-3 px-4">Detaylara dikkat eden, deneyim arayan, premium yaşam tarzı benimseyen profil</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 font-medium text-gray-900">Seyahat & Deneyim</td>
                <td className="py-3 px-4">Jolly Tur Premium, Booking.com 4-5★, Airbnb Plus, Ski tatili</td>
                <td className="py-3 px-4">Deneyim odaklı harcama yapıyor — aktif tatil ve outdoor ilgisi yüksek</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 font-medium text-gray-900">Otomotiv</td>
                <td className="py-3 px-4">Tesla, Volvo, BMW, Mini Cooper, VW ID serisi, Togg</td>
                <td className="py-3 px-4">Üst gelir grubu teyidi — teknoloji meraklısı, çevre duyarlı</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 font-medium text-gray-900">Kozmetik & Kişisel Bakım</td>
                <td className="py-3 px-4">Sephora, MAC, Kiehl's, Aesop, Rituals, Dermalogica</td>
                <td className="py-3 px-4">Kendine bakım ve kaliteli ürünlere yatırım alışkanlığı — premium alışverişe yatkın</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 font-medium text-gray-900">Ev & Yaşam</td>
                <td className="py-3 px-4">Mudo Concept, Karaca Premium, Nespresso, Dyson, iRobot</td>
                <td className="py-3 px-4">Yaşam kalitesine önem veren, teknoloji yatırımı yapan hane profili</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 font-medium text-gray-900">Eğitim & Gelişim</td>
                <td className="py-3 px-4">Masterclass, Coursera, Udemy Business, Blinkist, Kindle Unlimited</td>
                <td className="py-3 px-4">Kendine yatırım yapan, gelişim odaklı — bilinçli tüketici profili</td>
              </tr>
              <tr>
                <td className="py-3 px-4 font-medium text-gray-900">Rakip Spor Perakende</td>
                <td className="py-3 px-4">SPX, The Moose Bay, Alpinist, Intersport, Sportive, Decathlon</td>
                <td className="py-3 px-4"><strong>Doğrudan conquest hedefi</strong> — aynı ürün ihtiyacı, High5'a çekilebilir</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ───────── Buyer Personas ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Buyer Personas</h2>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">

          {/* Persona 1 */}
          <div className="rounded-xl border overflow-hidden">
            <div className="bg-[#111] px-5 py-4 text-white">
              <h3 className="font-bold text-base">Urban Runner Emre</h3>
              <p className="text-neutral-400 text-xs mt-0.5">32 · Yazılım Müdürü · İstanbul</p>
            </div>
            <div className="p-5 space-y-3 text-sm text-gray-700">
              <p>Haftada 4 gün koşar, İstanbul Maratonu'na katılır. Strava'da aktif, koşu topluluklarının üyesi. Ayakkabıda teknoloji ve performansı önemser.</p>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Motivasyon</p>
                <p>Performans artışı, sakatlık önleme. YouTube review'ları ve uzman tavsiyeleriyle karar verir.</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Yaşam Tarzı Profili</p>
                <div className="flex flex-wrap gap-1.5">
                  {['Veja V-10 günlük', 'Garmin Forerunner 265', 'Cihangir\'de 3+1', 'The North Face mont', 'Specialty coffee', 'Strava segmentleri', 'Runkeeper Premium', 'Yılda 2 maraton'].map(t => <span key={t} className="px-2 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-700 rounded-full">{t}</span>)}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {['HOKA', 'On', 'ASICS', 'Brooks'].map(b => <span key={b} className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 rounded">{b}</span>)}
              </div>
              <p className="text-xs text-gray-500">YouTube · Google Search · Strava · Instagram Reels</p>
            </div>
          </div>

          {/* Persona 2 */}
          <div className="rounded-xl border overflow-hidden">
            <div className="bg-[#111] px-5 py-4 text-white">
              <h3 className="font-bold text-base">Wellness Zeynep</h3>
              <p className="text-neutral-400 text-xs mt-0.5">29 · Marka Yöneticisi · İzmir</p>
            </div>
            <div className="p-5 space-y-3 text-sm text-gray-700">
              <p>Haftada 3 yoga, 2 pilates. Hafta sonu sahil koşusu. Spor giyimi günlük hayatında da kullanır (athleisure). İçerik üreticisi.</p>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Motivasyon</p>
                <p>Stil + performans dengesi. Estetik, renk seçenekleri ve marka hikayesi karar verici.</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Yaşam Tarzı Profili</p>
                <div className="flex flex-wrap gap-1.5">
                  {['Lululemon tayt', 'Açaí bowl kahvaltısı', 'Alaçatı tatili', 'Apple Watch Ultra', 'Reformer Pilates', 'Oatly süt', 'Veja Campo beyaz', 'Pinterest board\'ları'].map(t => <span key={t} className="px-2 py-0.5 text-[10px] font-medium bg-pink-50 text-pink-700 rounded-full">{t}</span>)}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {['On', 'HOKA', 'ASICS', 'Skechers'].map(b => <span key={b} className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 rounded">{b}</span>)}
              </div>
              <p className="text-xs text-gray-500">Instagram Keşfet · Pinterest · TikTok · Google Shopping</p>
            </div>
          </div>

          {/* Persona 3 */}
          <div className="rounded-xl border overflow-hidden">
            <div className="bg-[#111] px-5 py-4 text-white">
              <h3 className="font-bold text-base">Trail Explorer Cem</h3>
              <p className="text-neutral-400 text-xs mt-0.5">38 · Doktor · Ankara</p>
            </div>
            <div className="p-5 space-y-3 text-sm text-gray-700">
              <p>Trail running ve hiking tutkunu. Kaçkar Ultra, Likya Yolu, Cappadocia Ultra Trail katılımcısı. Ekipmana yatırım yapar, detaycı.</p>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Motivasyon</p>
                <p>Arazi tutunması, dayanıklılık, su geçirmezlik. Teknik spec karşılaştırması yapar.</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Yaşam Tarzı Profili</p>
                <div className="flex flex-wrap gap-1.5">
                  {['Patagonia polar', 'Suunto saat', 'Decathlon çadır', 'Salomon XT-6 günlük', 'Yılda 6+ trail yarışı', 'Kamp malzemesi koleksiyonu', 'Jeep Renegade', 'Wikiloc uygulaması'].map(t => <span key={t} className="px-2 py-0.5 text-[10px] font-medium bg-emerald-50 text-emerald-700 rounded-full">{t}</span>)}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {['Salomon', 'HOKA', 'Brooks', 'ASICS'].map(b => <span key={b} className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 rounded">{b}</span>)}
              </div>
              <p className="text-xs text-gray-500">YouTube vlogs · Google Search · Koşu forumları · Strava</p>
            </div>
          </div>

          {/* Persona 4 */}
          <div className="rounded-xl border overflow-hidden">
            <div className="bg-[#111] px-5 py-4 text-white">
              <h3 className="font-bold text-base">Premium Fitness Deniz</h3>
              <p className="text-neutral-400 text-xs mt-0.5">34 · Girişimci · İstanbul</p>
            </div>
            <div className="p-5 space-y-3 text-sm text-gray-700">
              <p>Boutique spor salonu üyesi (Reformer, CrossFit). Günlük giyimde de spor markalarını tercih eder. Garmin/Apple Watch kullanır.</p>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Motivasyon</p>
                <p>Çok yönlü kullanım — spor salonu, ofis casual, weekend. Konfor + prestij birlikteliği.</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Yaşam Tarzı Profili</p>
                <div className="flex flex-wrap gap-1.5">
                  {['Ralph Lauren kazak', 'İstinyePark AVM', 'Senede 4 yurtdışı', 'Tesla / BMW i4', 'Soho House üyelik', 'Nusret / Zuma akşam yemeği', 'On Roger Advantage', 'Beymen Club alışverişi'].map(t => <span key={t} className="px-2 py-0.5 text-[10px] font-medium bg-amber-50 text-amber-700 rounded-full">{t}</span>)}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {['On', 'ASICS', 'Skechers', 'HOKA'].map(b => <span key={b} className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 rounded">{b}</span>)}
              </div>
              <p className="text-xs text-gray-500">Instagram · Google PMAX · Podcast · Spor salonu ortaklıkları</p>
            </div>
          </div>

          {/* Persona 5 */}
          <div className="rounded-xl border overflow-hidden">
            <div className="bg-[#111] px-5 py-4 text-white">
              <h3 className="font-bold text-base">Yeni Başlayan Elif</h3>
              <p className="text-neutral-400 text-xs mt-0.5">27 · Avukat · Ankara</p>
            </div>
            <div className="p-5 space-y-3 text-sm text-gray-700">
              <p>Koşuya yeni başladı, ilk 10K hedefliyor. Doğru ayakkabı seçimi konusunda araştırma yapıyor. Arkadaş çevresinden etkileniyor.</p>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Motivasyon</p>
                <p>&quot;İlk premium ayakkabım&quot; heyecanı. Güvenilir bilgi ve danışmanlık arıyor. Sakatlık korkusu.</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Yaşam Tarzı Profili</p>
                <div className="flex flex-wrap gap-1.5">
                  {['Zara / Massimo Dutti ofis', 'Starbucks Reserve', 'Couch to 5K uygulaması', 'Spotify koşu playlistleri', 'Nike Run Club denemiş', 'Karakoy brunch', 'Airbnb tatili', 'Instagram story koşucu'].map(t => <span key={t} className="px-2 py-0.5 text-[10px] font-medium bg-violet-50 text-violet-700 rounded-full">{t}</span>)}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {['ASICS', 'Brooks', 'Skechers', 'On'].map(b => <span key={b} className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 rounded">{b}</span>)}
              </div>
              <p className="text-xs text-gray-500">Google Search · Instagram · Blog · Koşu kulübü referansı</p>
            </div>
          </div>

          {/* Persona 6 */}
          <div className="rounded-xl border overflow-hidden">
            <div className="bg-[#111] px-5 py-4 text-white">
              <h3 className="font-bold text-base">Hediye Arayan Mehmet</h3>
              <p className="text-neutral-400 text-xs mt-0.5">42 · Üst Düzey Yönetici · İstanbul</p>
            </div>
            <div className="p-5 space-y-3 text-sm text-gray-700">
              <p>Eşine/çocuğuna premium spor ayakkabı/giyim hediye etmek istiyor. Kendisi sporla ilgili değil ama kaliteli ve prestijli bir şey arıyor.</p>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Motivasyon</p>
                <p>Hediye kartı, güzel paketleme, marka prestiji. Hızlı ve güvenilir teslimat kritik.</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Yaşam Tarzı Profili</p>
                <div className="flex flex-wrap gap-1.5">
                  {['Vakko gömlek', 'Nişantaşı ofis', 'Mercedes E-Serisi', 'Turkcell Platinum', 'Akmerkez / Zorlu Center', 'Yılda 3+ iş seyahati', 'Beymen hediye kartı', 'Golf kulübü üyeliği'].map(t => <span key={t} className="px-2 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-600 rounded-full">{t}</span>)}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {['On', 'HOKA', 'ASICS'].map(b => <span key={b} className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 rounded">{b}</span>)}
              </div>
              <p className="text-xs text-gray-500">Google Search · Google Shopping · Meta Retargeting</p>
            </div>
          </div>

          {/* Persona 7 */}
          <div className="rounded-xl border overflow-hidden">
            <div className="bg-[#111] px-5 py-4 text-white">
              <h3 className="font-bold text-base">Outdoor Çift: Ayşe &amp; Burak</h3>
              <p className="text-neutral-400 text-xs mt-0.5">30 &amp; 33 · Mimar &amp; Fotoğrafçı · Antalya</p>
            </div>
            <div className="p-5 space-y-3 text-sm text-gray-700">
              <p>Hafta sonları birlikte trekking yapan çift. Tatillerini doğa yürüyüşleri etrafında planlıyor. İkisi de ekipman konusunda titiz.</p>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Motivasyon</p>
                <p>Çift alışverişi — iki kişilik sepet. Doğa deneyimi, dayanıklılık, görsel estetik (fotoğraf paylaşımı).</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Yaşam Tarzı Profili</p>
                <div className="flex flex-wrap gap-1.5">
                  {['Arc\'teryx ceket', 'Dacia Duster', 'Olympos kamp', 'Sony A7 fotoğraf makinesi', 'Wikiloc rotaları', 'Airbnb dağ evi', 'Osprey sırt çantası', 'Kahvaltıda köy pazarı'].map(t => <span key={t} className="px-2 py-0.5 text-[10px] font-medium bg-teal-50 text-teal-700 rounded-full">{t}</span>)}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {['Salomon', 'HOKA', 'On', 'Brooks'].map(b => <span key={b} className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 rounded">{b}</span>)}
              </div>
              <p className="text-xs text-gray-500">Instagram · YouTube · Outdoor etkinlik takvimi · Blog</p>
            </div>
          </div>

          {/* Persona 8 */}
          <div className="rounded-xl border overflow-hidden">
            <div className="bg-[#111] px-5 py-4 text-white">
              <h3 className="font-bold text-base">Comeback Murat</h3>
              <p className="text-neutral-400 text-xs mt-0.5">45 · Finans Direktörü · İstanbul</p>
            </div>
            <div className="p-5 space-y-3 text-sm text-gray-700">
              <p>Sağlık kontrolünden sonra aktif yaşama dönmeye karar verdi. Yürüyüşle başlayıp koşuya geçiş yapıyor. Konfor birincil kriter.</p>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Motivasyon</p>
                <p>Sağlık odaklı — doktor tavsiyesi ile hareket. Eklem dostu, yastıklaması yüksek ürünler arar.</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Yaşam Tarzı Profili</p>
                <div className="flex flex-wrap gap-1.5">
                  {['Polo Ralph Lauren polo', 'Etiler / Ulus konut', 'Acıbadem check-up', 'Volvo XC60', 'Haftasonu Belgrad Ormanı', 'Diyet programı takibi', 'Fitbit Sense', 'Eataly market alışverişi'].map(t => <span key={t} className="px-2 py-0.5 text-[10px] font-medium bg-orange-50 text-orange-700 rounded-full">{t}</span>)}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {['ASICS', 'HOKA', 'Skechers', 'Brooks'].map(b => <span key={b} className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 rounded">{b}</span>)}
              </div>
              <p className="text-xs text-gray-500">Google Search · YouTube · Sağlık blogları · Doktor referansı</p>
            </div>
          </div>

          {/* Persona 9 */}
          <div className="rounded-xl border overflow-hidden">
            <div className="bg-[#111] px-5 py-4 text-white">
              <h3 className="font-bold text-base">Sneakerhead Kaan</h3>
              <p className="text-neutral-400 text-xs mt-0.5">24 · Üniversite Öğrencisi (MBA) · İstanbul</p>
            </div>
            <div className="p-5 space-y-3 text-sm text-gray-700">
              <p>Sneaker kültürü ile büyüdü ama Nike/Adidas'tan sıyrılmak istiyor. On Roger, HOKA Bondi gibi &quot;farklı&quot; premium modelleri takip ediyor.</p>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Motivasyon</p>
                <p>Farklılık ve statü. &quot;Herkes Nike giyiyor, ben On giyiyorum&quot; hissi. Limited edition'lar, yeni renkler.</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Yaşam Tarzı Profili</p>
                <div className="flex flex-wrap gap-1.5">
                  {['Veja Recife', 'Karaköy vintage cafe', 'Supreme / Stüssy', 'Spotify Discover Weekly', 'StockX takibi', 'Boğaziçi kampüs', 'iPad Pro notlar', 'Depop / Grailed alışverişi'].map(t => <span key={t} className="px-2 py-0.5 text-[10px] font-medium bg-indigo-50 text-indigo-700 rounded-full">{t}</span>)}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {['On', 'HOKA', 'Salomon', 'ASICS'].map(b => <span key={b} className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 rounded">{b}</span>)}
              </div>
              <p className="text-xs text-gray-500">TikTok · Instagram · Reddit · Sneaker forumları</p>
            </div>
          </div>

        </div>
      </div>

      {/* ───────── SWOT Analysis ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">SWOT Analizi</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-200 rounded-xl overflow-hidden border border-gray-200">

            {/* Strengths */}
            <div className="bg-white p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900">Güçlü Yanlar</h3>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">S</span>
              </div>
              <ul className="space-y-3">
                {[
                  ['Premium Marka Portföyü', 'On, HOKA, ASICS, Brooks, Salomon, Skechers — Türkiye\'de performans sporu markalarının en güçlü çatı altı koleksiyonlarından biri. Her biri kendi segmentinde lider.'],
                  ['Niş Konumlandırma', 'Mass-market\'ten net ayrışma. "Her şeyi satan mağaza" değil, "ne sattığını bilen mağaza" algısı. Bu konumlandırma sadakat ve güven yaratır.'],
                  ['Deneyim Odaklı Marka Kimliği', 'Sadece ürün satışı değil, yaşam tarzı önerisi. Koşu, outdoor, wellness ekosisteminin bir parçası olma vaadi — ürün ötesi değer.'],
                  ['Hedef Kitle Netliği', 'Koşu, trail, fitness, outdoor — bilinçli ve yüksek gelirli segmente net odak. Dağılmayan, tutarlı bir müşteri profili.'],
                  ['Multi-Sport Genişlik', 'Koşu, trail, hiking, yoga, fitness, tenis — tek bir spor dalına bağımlı değil, çapraz satış ve sepet büyütme fırsatı yüksek.'],
                  ['Premium Fiyatlandırma Avantajı', 'Yüksek AOV (ortalama sipariş değeri) ve marj oranları — aynı trafik hacminde daha yüksek gelir potansiyeli.'],
                ].map(([title, desc], i) => (
                  <li key={i}>
                    <p className="text-sm font-semibold text-gray-900">{title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                  </li>
                ))}
              </ul>
            </div>

            {/* Weaknesses */}
            <div className="bg-white p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900">Zayıf Yanlar</h3>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">W</span>
              </div>
              <ul className="space-y-3">
                {[
                  ['Düşük Marka Bilinirliği', 'Sportive, Intersport gibi kitlesel rakiplere kıyasla düşük unaided brand awareness. "Koşu ayakkabısı al" denince akla gelen ilk mağaza henüz değil.'],
                  ['Yüksek Fiyat Bariyeri', 'Ortalama ₺5.000+ fiyat noktası geniş kitlelere ulaşımı doğal olarak sınırlar. Ekonomik daralma dönemlerinde premium segment ilk daralan alan.'],
                  ['Sınırlı Ürün Piramidi', 'Sadece premium markalar — giriş seviyesi veya mid-range arayan potansiyel müşteriyi kaybetme riski. "Bir gün High5\'tan alırım" algısı oluşturma zorluğu.'],
                  ['Fiziksel Mağaza Ağı', 'Sportive\'in 200+, Intersport\'un 100+ mağazasına karşı çok sınırlı fiziksel temas noktası. Ayakkabıyı deneyimlemek isteyen müşteri için engel.'],
                  ['SEO & İçerik Birikimi', 'Uzun süredir piyasada olan rakiplere kıyasla organik arama görünürlüğü ve içerik kütüphanesi (blog, review, karşılaştırma) henüz yetersiz olabilir.'],
                  ['Sadakat Programı Eksikliği', 'Sportive Club gibi köklü sadakat programlarına karşı, tekrar satın almayı teşvik eden bir loyalty mekanizması eksik veya yeni.'],
                  ['Kategori Sınırlılığı', 'Aksesuar, giyim, ekipman derinliği ayakkabıya kıyasla sığ kalabilir — tam gardrop çözümü sunma kapasitesi sınırlı.'],
                ].map(([title, desc], i) => (
                  <li key={i}>
                    <p className="text-sm font-semibold text-gray-900">{title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                  </li>
                ))}
              </ul>
            </div>

            {/* Opportunities */}
            <div className="bg-white p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900">Fırsatlar</h3>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">O</span>
              </div>
              <ul className="space-y-3">
                {[
                  ['Koşu Kültürü Patlaması', 'Türkiye\'de maraton ve trail koşusu katılımları yılda %30+ büyüyor. parkrun, RunTurkey, Salomon Ultra Trail gibi etkinlikler kitleselleşiyor — High5\'ın doğal sahne alanı.'],
                  ['Topluluk Pazarlaması (Community-Led Growth)', 'Koşu kulüpleri (Adım Adım, Rungang), trail grupları, yoga stüdyoları ile ortaklıklar — düşük maliyetle yüksek güven ve organik erişim.'],
                  ['İçerik & Ambassador Ekosistemi', 'Performans sporu micro-influencer\'ları ile uzun vadeli ambassador programları. Gerçek sporcu testimonial\'ları reklam kreatifleri için altın değerinde.'],
                  ['Athleisure Megatrend', 'Spor giyimin günlük hayata geçişi hızlanıyor. On, HOKA, Salomon lifestyle koleksiyonları büyüyor — "ofise de giyilebilir" performans ürünü talebi artıyor.'],
                  ['Etkinlik Sponsorlukları & Deneyim', 'İstanbul Maratonu, Salomon Cappadocia, Runatolia — High5\'ı doğrudan hedef kitle ile buluşturan, denenebilir deneyim alanları. Pop-up mağaza + etkinlik format fırsatı.'],
                  ['Veriye Dayalı Kişiselleştirme', 'CRM ve 1st-party data ile kişiye özel ürün önerisi, ayak tipi danışmanlığı, koşu programı eşleştirmesi — rakiplerin henüz güçlü olmadığı alan.'],
                  ['Sosyal Kanıt & UGC Potansiyeli', 'Koşu sonrası paylaşım kültürü güçlü — müşterinin kendi içeriğini üretmesi (Strava screenshot, finish line fotoğrafı, unboxing) marka için ücretsiz ve güvenilir reklam kaynağı.'],
                  ['Omnichannel Deneyim Farkı', 'Online sipariş + mağazada dene/teslim al modeli ile rakiplerin sunamadığı hibrit deneyim. Premium müşteri fiziksel temas istiyor — bu avantaja dönüştürülebilir.'],
                ].map(([title, desc], i) => (
                  <li key={i}>
                    <p className="text-sm font-semibold text-gray-900">{title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                  </li>
                ))}
              </ul>
            </div>

            {/* Threats */}
            <div className="bg-white p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900">Tehditler</h3>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">T</span>
              </div>
              <ul className="space-y-3">
                {[
                  ['Kitlesel Rakiplerin Bütçe Gücü', 'Sportive, Intersport — çok daha yüksek medya bütçeleri, 200+ mağaza ve yıllardır biriken marka bilinirliği. Awareness yarışında asimetrik dezavantaj.'],
                  ['Ekonomik Baskı & Premium Daralma', 'Enflasyon ve TRY değer kaybı — premium segment ekonomik daralmada ilk etkilenen alan. Tüketici "downgrade" yaparak Sportive/Decathlon\'a kayabilir.'],
                  ['Agresif Niş Rakipler', 'SPX, The Moose Bay, Alpinist — aynı premium segmentte agresif dijital büyüme, benzer marka portföyü, benzer hedef kitle. Pastayı paylaşan oyuncu sayısı artıyor.'],
                  ['Trendyol & Marketplace Baskısı', 'Trendyol Premium / Butik gibi marketplace\'lerin premium segmente girmesi, fiyat karşılaştırmasını kolaylaştırır ve price-match baskısı oluşturur.'],
                  ['Fiyat Şeffaflığı & Karşılaştırma', 'Akakçe, Cimri gibi fiyat karşılaştırma siteleri ve Google Shopping, tüketicinin en ucuz seçeneği anında bulmasını sağlıyor — mağaza sadakati yerine fiyat odaklı karar verme artıyor.'],
                ].map(([title, desc], i) => (
                  <li key={i}>
                    <p className="text-sm font-semibold text-gray-900">{title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </div>

      {/* ───────── Rekabet Haritası ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Rekabet Haritası</h2>
        </div>
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="border-b-2 border-gray-900">
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Rakip</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Segment</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Güçlü Yanı</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Zayıf Yanı</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-gray-900 uppercase tracking-wider">Tehdit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  { name: 'SPX', segment: 'Premium niş', strong: 'Aynı marka portföyü, güçlü dijital varlık', weak: 'Topluluk bağı ve fiziksel deneyim sınırlı', threat: 'Yüksek' },
                  { name: 'Sportive', segment: 'Mass-market', strong: '200+ mağaza, yüksek bilinirlik', weak: 'Premium deneyim eksik, marka seçiciliği düşük', threat: 'Orta' },
                  { name: 'Intersport', segment: 'Multi-sport retail', strong: 'Global marka, geniş ürün yelpazesi', weak: 'Niş odak yok, premium hikaye anlatımı zayıf', threat: 'Orta' },
                  { name: 'The Moose Bay', segment: 'Outdoor premium', strong: 'Outdoor & trail odaklı sadık kitle', weak: 'Dar segment — koşu/fitness tarafı zayıf', threat: 'Orta' },
                  { name: 'Alpinist', segment: 'Outdoor / trekking', strong: 'Teknik outdoor ekipman uzmanlığı', weak: 'Lifestyle / athleisure segmentinde yok', threat: 'Düşük' },
                ].map((comp, i) => (
                  <tr key={i}>
                    <td className="px-4 py-3 text-sm font-bold text-gray-900">{comp.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{comp.segment}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{comp.strong}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{comp.weak}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-bold rounded ${
                        comp.threat === 'Yüksek' ? 'bg-gray-900 text-white' :
                        comp.threat === 'Orta' ? 'bg-gray-200 text-gray-800' :
                        'bg-gray-100 text-gray-500'
                      }`}>{comp.threat}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ───────── Keyword Çalışması ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Keyword Çalışması</h2>
          <p className="text-xs text-gray-400 mt-1">Google Ads & SEO hedefleme için kategori bazlı anahtar kelime haritası</p>
        </div>
        <div className="p-6 space-y-8">

          {/* Generic Keywords */}
          <div>
            <div className="mb-3">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Generic Keywords</h3>
              <p className="text-xs text-gray-400 mt-0.5">Marka içermeyen, kategori bazlı genel arama terimleri — yüksek hacim, yüksek rekabet</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-900">
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Keyword</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Kategori</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Tahmini Hacim</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Intent</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 divide-y divide-gray-100">
                  {[
                    ['koşu ayakkabısı', 'Koşu', 'Çok Yüksek', 'Ticari'],
                    ['spor ayakkabı', 'Genel Spor', 'Çok Yüksek', 'Ticari'],
                    ['trail koşu ayakkabısı', 'Trail', 'Yüksek', 'Ticari'],
                    ['en iyi koşu ayakkabısı', 'Koşu', 'Yüksek', 'Araştırma'],
                    ['en iyi koşu ayakkabısı 2026', 'Koşu', 'Yüksek', 'Araştırma'],
                    ['maraton ayakkabısı', 'Koşu', 'Orta', 'Ticari'],
                    ['pronasyon ayakkabısı', 'Koşu', 'Orta', 'Araştırma'],
                    ['yürüyüş ayakkabısı', 'Outdoor', 'Çok Yüksek', 'Ticari'],
                    ['trekking ayakkabısı', 'Outdoor', 'Yüksek', 'Ticari'],
                    ['outdoor ayakkabı', 'Outdoor', 'Yüksek', 'Ticari'],
                    ['fitness ayakkabısı', 'Fitness', 'Yüksek', 'Ticari'],
                    ['cross training ayakkabı', 'Fitness', 'Orta', 'Ticari'],
                    ['yoga matı', 'Yoga', 'Yüksek', 'Ticari'],
                    ['koşu tişörtü', 'Giyim', 'Orta', 'Ticari'],
                    ['spor tayt', 'Giyim', 'Yüksek', 'Ticari'],
                    ['koşu çorabı', 'Aksesuar', 'Orta', 'Ticari'],
                    ['sporcu beslenme', 'Yaşam Tarzı', 'Orta', 'Bilgi'],
                    ['koşuya nasıl başlanır', 'Koşu', 'Yüksek', 'Bilgi'],
                    ['düz taban koşu ayakkabısı', 'Koşu', 'Orta', 'Araştırma'],
                    ['su geçirmez koşu ayakkabısı', 'Koşu / Outdoor', 'Orta', 'Ticari'],
                    ['premium spor giyim', 'Genel', 'Düşük', 'Ticari'],
                    ['online spor mağazası', 'Genel', 'Orta', 'Navigasyon'],
                  ].map(([kw, cat, vol, intent], i) => (
                    <tr key={i}>
                      <td className="py-2.5 px-4 font-medium text-gray-900">{kw}</td>
                      <td className="py-2.5 px-4 text-gray-500">{cat}</td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${
                          vol === 'Çok Yüksek' ? 'bg-gray-900 text-white' :
                          vol === 'Yüksek' ? 'bg-gray-700 text-white' :
                          vol === 'Orta' ? 'bg-gray-200 text-gray-700' :
                          'bg-gray-100 text-gray-400'
                        }`}>{vol}</span>
                      </td>
                      <td className="py-2.5 px-4 text-center text-xs text-gray-500">{intent}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Brand + Generic Keywords */}
          <div>
            <div className="mb-3">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Brand + Generic Keywords</h3>
              <p className="text-xs text-gray-400 mt-0.5">Taşıdığımız markaların adıyla birlikte aranan terimler — yüksek satın alma niyeti, düşük-orta rekabet</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-900">
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Keyword</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Marka</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Tahmini Hacim</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Intent</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 divide-y divide-gray-100">
                  {[
                    ['hoka koşu ayakkabısı', 'HOKA', 'Yüksek', 'Ticari'],
                    ['hoka bondi 9', 'HOKA', 'Yüksek', 'Ticari'],
                    ['hoka clifton 10', 'HOKA', 'Yüksek', 'Ticari'],
                    ['hoka speedgoat', 'HOKA', 'Orta', 'Ticari'],
                    ['hoka mach 7', 'HOKA', 'Orta', 'Ticari'],
                    ['on koşu ayakkabısı', 'On', 'Yüksek', 'Ticari'],
                    ['on cloudmonster', 'On', 'Yüksek', 'Ticari'],
                    ['on cloudsurfer', 'On', 'Orta', 'Ticari'],
                    ['on cloudrunner', 'On', 'Orta', 'Ticari'],
                    ['on cloud 6', 'On', 'Orta', 'Ticari'],
                    ['asics gel kayano 31', 'ASICS', 'Yüksek', 'Ticari'],
                    ['asics gel nimbus', 'ASICS', 'Yüksek', 'Ticari'],
                    ['asics novablast', 'ASICS', 'Orta', 'Ticari'],
                    ['asics gt 2000', 'ASICS', 'Orta', 'Ticari'],
                    ['brooks ghost 16', 'Brooks', 'Orta', 'Ticari'],
                    ['brooks glycerin 21', 'Brooks', 'Orta', 'Ticari'],
                    ['brooks adrenaline gts', 'Brooks', 'Orta', 'Ticari'],
                    ['salomon speedcross', 'Salomon', 'Yüksek', 'Ticari'],
                    ['salomon ultra glide', 'Salomon', 'Orta', 'Ticari'],
                    ['salomon xt-6', 'Salomon', 'Yüksek', 'Ticari'],
                    ['salomon trail ayakkabı', 'Salomon', 'Yüksek', 'Ticari'],
                    ['skechers max cushioning', 'Skechers', 'Orta', 'Ticari'],
                    ['skechers go run', 'Skechers', 'Orta', 'Ticari'],
                  ].map(([kw, brand, vol, intent], i) => (
                    <tr key={i}>
                      <td className="py-2.5 px-4 font-medium text-gray-900">{kw}</td>
                      <td className="py-2.5 px-4 text-gray-500">{brand}</td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${
                          vol === 'Çok Yüksek' ? 'bg-gray-900 text-white' :
                          vol === 'Yüksek' ? 'bg-gray-700 text-white' :
                          vol === 'Orta' ? 'bg-gray-200 text-gray-700' :
                          'bg-gray-100 text-gray-400'
                        }`}>{vol}</span>
                      </td>
                      <td className="py-2.5 px-4 text-center text-xs text-gray-500">{intent}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Competitor Brand Keywords (Conquest) */}
          <div>
            <div className="mb-3">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Competitor / Conquest Keywords</h3>
              <p className="text-xs text-gray-400 mt-0.5">Rakip marka ve mağaza adlarıyla yapılan aramalar — düşük hacim ama çok yüksek çalma potansiyeli</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-900">
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Keyword</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Hedef Rakip</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Tahmini Hacim</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Strateji</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 divide-y divide-gray-100">
                  {[
                    ['spx spor', 'SPX', 'Orta', 'Conquest'],
                    ['spx koşu ayakkabısı', 'SPX', 'Düşük', 'Conquest'],
                    ['the moose bay', 'The Moose Bay', 'Düşük', 'Conquest'],
                    ['sportive koşu ayakkabısı', 'Sportive', 'Yüksek', 'Conquest'],
                    ['sportive indirim', 'Sportive', 'Yüksek', 'Conquest'],
                    ['intersport online', 'Intersport', 'Orta', 'Conquest'],
                    ['alpinist outdoor', 'Alpinist', 'Düşük', 'Conquest'],
                    ['decathlon koşu', 'Decathlon', 'Yüksek', 'Conquest'],
                    ['nike koşu ayakkabısı', 'Nike DTC', 'Çok Yüksek', 'Upgrade'],
                    ['adidas ultraboost', 'Adidas DTC', 'Yüksek', 'Upgrade'],
                    ['new balance koşu', 'New Balance', 'Yüksek', 'Upgrade'],
                  ].map(([kw, target, vol, strategy], i) => (
                    <tr key={i}>
                      <td className="py-2.5 px-4 font-medium text-gray-900">{kw}</td>
                      <td className="py-2.5 px-4 text-gray-500">{target}</td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${
                          vol === 'Çok Yüksek' ? 'bg-gray-900 text-white' :
                          vol === 'Yüksek' ? 'bg-gray-700 text-white' :
                          vol === 'Orta' ? 'bg-gray-200 text-gray-700' :
                          'bg-gray-100 text-gray-400'
                        }`}>{vol}</span>
                      </td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${
                          strategy === 'Conquest' ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-700'
                        }`}>{strategy}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Comparison & Research Keywords */}
          <div>
            <div className="mb-3">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Karşılaştırma & Araştırma Keywords</h3>
              <p className="text-xs text-gray-400 mt-0.5">Satın alma öncesi araştırma yapan kullanıcılar — blog/SEO içerik fırsatı + Search Ads</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-900">
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Keyword</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Tip</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Tahmini Hacim</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">İçerik Fırsatı</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 divide-y divide-gray-100">
                  {[
                    ['hoka vs on', 'Karşılaştırma', 'Yüksek', 'Blog + Video'],
                    ['hoka bondi vs clifton', 'Karşılaştırma', 'Orta', 'Blog'],
                    ['asics kayano vs nimbus', 'Karşılaştırma', 'Orta', 'Blog'],
                    ['on cloudmonster vs hoka bondi', 'Karşılaştırma', 'Orta', 'Blog + Video'],
                    ['brooks ghost vs asics nimbus', 'Karşılaştırma', 'Düşük', 'Blog'],
                    ['salomon speedcross vs xt-6', 'Karşılaştırma', 'Düşük', 'Blog'],
                    ['en iyi trail ayakkabısı 2026', 'Listicle', 'Yüksek', 'Blog + SEO'],
                    ['en iyi maraton ayakkabısı', 'Listicle', 'Orta', 'Blog + SEO'],
                    ['pronasyon nedir hangi ayakkabı', 'Eğitim', 'Orta', 'Blog + Rehber'],
                    ['ayak tipine göre ayakkabı seçimi', 'Eğitim', 'Orta', 'Blog + Rehber'],
                    ['koşu ayakkabısı kaç km dayanır', 'Eğitim', 'Orta', 'Blog'],
                    ['ilk maraton hazırlık programı', 'Eğitim', 'Orta', 'Blog + E-mail'],
                    ['carbon plaka koşu ayakkabısı nedir', 'Eğitim', 'Düşük', 'Blog'],
                    ['kış koşusu ekipman listesi', 'Sezonsal', 'Orta', 'Blog + Ürün'],
                    ['trail koşuya nasıl başlanır', 'Eğitim', 'Düşük', 'Blog + Rehber'],
                  ].map(([kw, type, vol, content], i) => (
                    <tr key={i}>
                      <td className="py-2.5 px-4 font-medium text-gray-900">{kw}</td>
                      <td className="py-2.5 px-4 text-gray-500">{type}</td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${
                          vol === 'Çok Yüksek' ? 'bg-gray-900 text-white' :
                          vol === 'Yüksek' ? 'bg-gray-700 text-white' :
                          vol === 'Orta' ? 'bg-gray-200 text-gray-700' :
                          'bg-gray-100 text-gray-400'
                        }`}>{vol}</span>
                      </td>
                      <td className="py-2.5 px-4 text-center text-xs text-gray-500">{content}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Long-Tail & Niche Keywords */}
          <div>
            <div className="mb-3">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Long-Tail & Niş Keywords</h3>
              <p className="text-xs text-gray-400 mt-0.5">Düşük hacimli ama çok yüksek dönüşüm potansiyeli — spesifik ihtiyaç, net satın alma niyeti</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-900">
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Keyword</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Niş</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Tahmini Hacim</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Dönüşüm Pot.</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 divide-y divide-gray-100">
                  {[
                    ['geniş ayak koşu ayakkabısı', 'Ayak Tipi', 'Düşük', 'Çok Yüksek'],
                    ['plantar fasit koşu ayakkabısı', 'Sağlık', 'Düşük', 'Çok Yüksek'],
                    ['diz ağrısı için koşu ayakkabısı', 'Sağlık', 'Düşük', 'Çok Yüksek'],
                    ['ağır kilo koşu ayakkabısı', 'Vücut Tipi', 'Düşük', 'Yüksek'],
                    ['kadın trail koşu ayakkabısı', 'Cinsiyet + Niş', 'Orta', 'Yüksek'],
                    ['yağmurlu hava koşu ayakkabısı gore-tex', 'Hava Durumu', 'Düşük', 'Yüksek'],
                    ['hoka bondi fiyat', 'Fiyat Araştırma', 'Yüksek', 'Yüksek'],
                    ['on cloudmonster nereden alınır', 'Satış Noktası', 'Orta', 'Çok Yüksek'],
                    ['asics kayano 31 inceleme', 'Review', 'Orta', 'Yüksek'],
                    ['100 km ultra maraton ayakkabısı', 'Ultra Niş', 'Düşük', 'Çok Yüksek'],
                    ['salomon xt-6 günlük kullanım', 'Lifestyle', 'Düşük', 'Yüksek'],
                    ['koşu ayakkabısı 42 numara erkek', 'Beden + Cinsiyet', 'Orta', 'Yüksek'],
                  ].map(([kw, niche, vol, conversion], i) => (
                    <tr key={i}>
                      <td className="py-2.5 px-4 font-medium text-gray-900">{kw}</td>
                      <td className="py-2.5 px-4 text-gray-500">{niche}</td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${
                          vol === 'Çok Yüksek' ? 'bg-gray-900 text-white' :
                          vol === 'Yüksek' ? 'bg-gray-700 text-white' :
                          vol === 'Orta' ? 'bg-gray-200 text-gray-700' :
                          'bg-gray-100 text-gray-400'
                        }`}>{vol}</span>
                      </td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${
                          conversion === 'Çok Yüksek' ? 'bg-gray-900 text-white' :
                          conversion === 'Yüksek' ? 'bg-gray-700 text-white' :
                          'bg-gray-200 text-gray-700'
                        }`}>{conversion}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Dönemsel Yatırım Dağılımı */}
          <div>
            <div className="mb-3">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Dönemsel Keyword Yatırım Dağılımı</h3>
              <p className="text-xs text-gray-400 mt-0.5">Başlangıçta dönüşümü garanti Brand + Generic ağırlıklı, zamanla Generic ve Conquest'e doğru genişleyen strateji</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-900">
                    <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Keyword Kategorisi</th>
                    <th className="text-center py-3 px-4 text-[10px] font-bold text-gray-900 uppercase tracking-wider bg-gray-50">Q1 — Temel</th>
                    <th className="text-center py-3 px-4 text-[10px] font-bold text-gray-900 uppercase tracking-wider">Q2 — Büyüme</th>
                    <th className="text-center py-3 px-4 text-[10px] font-bold text-gray-900 uppercase tracking-wider bg-gray-50">Q3 — Genişleme</th>
                    <th className="text-center py-3 px-4 text-[10px] font-bold text-gray-900 uppercase tracking-wider">Q4 — Olgunluk</th>
                    <th className="text-center py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Trend</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b border-gray-100">
                    <td className="py-3 px-4">
                      <p className="font-semibold text-gray-900">Brand + Generic</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">hoka bondi, on cloudmonster, salomon xt-6…</p>
                    </td>
                    <td className="py-3 px-4 text-center bg-gray-50">
                      <span className="text-lg font-black text-gray-900">%60</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-lg font-black text-gray-900">%45</span>
                    </td>
                    <td className="py-3 px-4 text-center bg-gray-50">
                      <span className="text-lg font-black text-gray-700">%35</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-lg font-black text-gray-700">%30</span>
                    </td>
                    <td className="py-3 px-4 text-center text-xs text-gray-400">↘ Azalan</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-3 px-4">
                      <p className="font-semibold text-gray-900">Generic</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">koşu ayakkabısı, trail ayakkabı, spor giyim…</p>
                    </td>
                    <td className="py-3 px-4 text-center bg-gray-50">
                      <span className="text-lg font-black text-gray-700">%20</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-lg font-black text-gray-900">%28</span>
                    </td>
                    <td className="py-3 px-4 text-center bg-gray-50">
                      <span className="text-lg font-black text-gray-900">%32</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-lg font-black text-gray-900">%30</span>
                    </td>
                    <td className="py-3 px-4 text-center text-xs text-gray-400">↗ Artan</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-3 px-4">
                      <p className="font-semibold text-gray-900">Competitor / Conquest</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">spx spor, sportive koşu, nike koşu ayakkabısı…</p>
                    </td>
                    <td className="py-3 px-4 text-center bg-gray-50">
                      <span className="text-lg font-black text-gray-500">%8</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-lg font-black text-gray-700">%12</span>
                    </td>
                    <td className="py-3 px-4 text-center bg-gray-50">
                      <span className="text-lg font-black text-gray-900">%15</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-lg font-black text-gray-900">%18</span>
                    </td>
                    <td className="py-3 px-4 text-center text-xs text-gray-400">↗ Artan</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-3 px-4">
                      <p className="font-semibold text-gray-900">Karşılaştırma & Araştırma</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">hoka vs on, en iyi trail ayakkabısı 2026…</p>
                    </td>
                    <td className="py-3 px-4 text-center bg-gray-50">
                      <span className="text-lg font-black text-gray-500">%7</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-lg font-black text-gray-700">%9</span>
                    </td>
                    <td className="py-3 px-4 text-center bg-gray-50">
                      <span className="text-lg font-black text-gray-700">%10</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-lg font-black text-gray-900">%12</span>
                    </td>
                    <td className="py-3 px-4 text-center text-xs text-gray-400">↗ Artan</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4">
                      <p className="font-semibold text-gray-900">Long-Tail & Niş</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">plantar fasit ayakkabı, on cloudmonster nereden alınır…</p>
                    </td>
                    <td className="py-3 px-4 text-center bg-gray-50">
                      <span className="text-lg font-black text-gray-500">%5</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-lg font-black text-gray-500">%6</span>
                    </td>
                    <td className="py-3 px-4 text-center bg-gray-50">
                      <span className="text-lg font-black text-gray-700">%8</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="text-lg font-black text-gray-700">%10</span>
                    </td>
                    <td className="py-3 px-4 text-center text-xs text-gray-400">↗ Artan</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-900">
                    <td className="py-3 px-4 font-bold text-gray-900">Toplam</td>
                    <td className="py-3 px-4 text-center bg-gray-50 font-bold text-gray-900">%100</td>
                    <td className="py-3 px-4 text-center font-bold text-gray-900">%100</td>
                    <td className="py-3 px-4 text-center bg-gray-50 font-bold text-gray-900">%100</td>
                    <td className="py-3 px-4 text-center font-bold text-gray-900">%100</td>
                    <td className="py-3 px-4"></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Dönem Açıklamaları */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
              <div className="rounded-lg border p-4">
                <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">Q1 — Temel Oluşturma</p>
                <p className="text-xs text-gray-500 leading-relaxed">Hızlı dönüşüm ve veri toplama. Marka arayanlar zaten satın almaya yakın — onları yakala, data biriktir, ROAS'ı güvenceye al.</p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">Q2 — Büyüme</p>
                <p className="text-xs text-gray-500 leading-relaxed">Q1 verileriyle generic'te hangi keyword'lerin dönüştüğünü gör. Generic payını artır, conquest'e giriş yap. İçerik (blog/video) üretime başla.</p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">Q3 — Genişleme</p>
                <p className="text-xs text-gray-500 leading-relaxed">Generic ve Brand neredeyse eşit. SEO içerikler indeksleniyor, organik trafik destekliyor. Conquest agresifleşiyor, rakip müşteri çalma stratejisi olgunlaşıyor.</p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">Q4 — Olgunluk</p>
                <p className="text-xs text-gray-500 leading-relaxed">Dengeli portföy. Brand hâlâ güçlü ama generic ve conquest büyümüş. Long-tail ve niş keyword'ler yüksek ROAS'la katkı sağlıyor. Sezon sonu agresif push.</p>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ───────── Targeting Mindmap ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Targeting Modelleri — Mindmap</h2>
          <p className="text-xs text-gray-400 mt-1">High5 hedef kitlesine ulaşmak için kullanılacak 8 farklı hedefleme yaklaşımı</p>
        </div>
        {/* === CARD GRID MINDMAP === */}
        <div className="p-8">

          {/* Center Hub */}
          <div className="flex flex-col items-center mb-10">
            <div className="bg-[#111] text-white px-8 py-4 rounded-2xl shadow-lg">
              <p className="text-lg font-black tracking-wide text-center">HIGH5 TARGETING</p>
              <p className="text-[10px] text-neutral-400 text-center mt-0.5 tracking-widest uppercase">8 Hedefleme Modeli</p>
            </div>
            <div className="w-px h-8 bg-gray-300"></div>
          </div>

          {/* Branches Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">

            {/* 1. Keyword Targeting */}
            <div className="relative">
              <div className="bg-gray-900 text-white px-4 py-3 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wider">① Keyword Targeting</p>
                  <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded">Google Ads</span>
                </div>
              </div>
              <div className="border border-t-0 rounded-b-xl p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-900 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Brand + Generic</p><p className="text-[10px] text-gray-400">hoka bondi, on cloudmonster…</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-700 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Generic</p><p className="text-[10px] text-gray-400">koşu ayakkabısı, trail ayakkabı…</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Conquest</p><p className="text-[10px] text-gray-400">spx spor, sportive koşu…</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Karşılaştırma</p><p className="text-[10px] text-gray-400">hoka vs on, en iyi trail…</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Long-Tail</p><p className="text-[10px] text-gray-400">plantar fasit ayakkabı…</p></div>
                </div>
              </div>
            </div>

            {/* 2. Interest-Based */}
            <div className="relative">
              <div className="bg-gray-900 text-white px-4 py-3 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wider">② Interest Targeting</p>
                  <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded">Meta + Google</span>
                </div>
              </div>
              <div className="border border-t-0 rounded-b-xl p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-900 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Spor & Aktivite</p><p className="text-[10px] text-gray-400">koşu, trail, yoga, CrossFit, hiking</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-700 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Uygulama & Teknoloji</p><p className="text-[10px] text-gray-400">Strava, Garmin, Apple Watch…</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Beslenme & Wellness</p><p className="text-[10px] text-gray-400">sağlıklı beslenme, protein, organik</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Medya & İçerik</p><p className="text-[10px] text-gray-400">running vlog, sneaker review…</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Etkinlik & Topluluk</p><p className="text-[10px] text-gray-400">maraton, parkrun, koşu kulüpleri</p></div>
                </div>
              </div>
            </div>

            {/* 3. Cross-Shopping Intent */}
            <div className="relative">
              <div className="bg-gray-900 text-white px-4 py-3 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wider">③ Alışveriş Intent</p>
                  <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded">Meta + Google</span>
                </div>
              </div>
              <div className="border border-t-0 rounded-b-xl p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-900 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Premium Moda</p><p className="text-[10px] text-gray-400">Beymen, Vakko, Network…</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-700 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Athleisure & Spor Moda</p><p className="text-[10px] text-gray-400">Lululemon, Arc'teryx, TNF…</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Sağlık & Beslenme</p><p className="text-[10px] text-gray-400">Eataly, iHerb, Supplementler…</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Kozmetik & Bakım</p><p className="text-[10px] text-gray-400">Sephora, Aesop, Kiehl's…</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Rakip Perakende</p><p className="text-[10px] text-gray-400">SPX, Sportive, Intersport…</p></div>
                </div>
              </div>
            </div>

            {/* 4. Location-Based */}
            <div className="relative">
              <div className="bg-gray-900 text-white px-4 py-3 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wider">④ Lokasyon Bazlı</p>
                  <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded">Meta + Google</span>
                </div>
              </div>
              <div className="border border-t-0 rounded-b-xl p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-900 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Spor Merkezleri</p><p className="text-[10px] text-gray-400">Boutique gym, fitness merkezi çevresi</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-700 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Koşu Parkurları</p><p className="text-[10px] text-gray-400">Caddebostan, Bebek, Konyaaltı…</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Premium Bölgeler</p><p className="text-[10px] text-gray-400">Maslak, Levent, Bağdat Cd., Alsancak</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Doğa Parkurları</p><p className="text-[10px] text-gray-400">Milli parklar, trail rotaları</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">AVM Proximity</p><p className="text-[10px] text-gray-400">Premium AVM katları çevresindekiler</p></div>
                </div>
              </div>
            </div>

            {/* 5. Demographic */}
            <div className="relative">
              <div className="bg-gray-900 text-white px-4 py-3 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wider">⑤ Demografik</p>
                  <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded">Meta + Google</span>
                </div>
              </div>
              <div className="border border-t-0 rounded-b-xl p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-900 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Yaş: 25–45</p><p className="text-[10px] text-gray-400">Çekirdek: 28–38 arası</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-700 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Beyaz Yaka</p><p className="text-[10px] text-gray-400">Yazılım, finans, hukuk, sağlık, kreatif</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Gelir: A / B+</p><p className="text-[10px] text-gray-400">Üst-orta ve üst gelir segmenti</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Eğitim: Üniversite+</p><p className="text-[10px] text-gray-400">Lisans ve üstü eğitim seviyesi</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Cinsiyet: K55 / E45</p><p className="text-[10px] text-gray-400">Kadın ağırlıklı ama dengeli</p></div>
                </div>
              </div>
            </div>

            {/* 6. Retargeting */}
            <div className="relative">
              <div className="bg-gray-900 text-white px-4 py-3 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wider">⑥ Retargeting</p>
                  <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded">Meta + Google</span>
                </div>
              </div>
              <div className="border border-t-0 rounded-b-xl p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-900 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Site Ziyaretçileri</p><p className="text-[10px] text-gray-400">Son 7/14/30/60/90 gün segmentleri</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-700 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Sepet Terk Edenler</p><p className="text-[10px] text-gray-400">Add-to-cart yapıp satın almayan</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Ürün Görüntüleyenler</p><p className="text-[10px] text-gray-400">Kategori/ürün sayfası ziyareti</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Mevcut Müşteriler</p><p className="text-[10px] text-gray-400">Cross-sell, upsell, yeni sezon push</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Video İzleyiciler</p><p className="text-[10px] text-gray-400">%25/%50/%75/%95 izleyenlere göre segmentleme</p></div>
                </div>
              </div>
            </div>

            {/* 7. Lookalike / Similar */}
            <div className="relative">
              <div className="bg-gray-900 text-white px-4 py-3 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wider">⑦ Lookalike</p>
                  <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded">Meta + Google</span>
                </div>
              </div>
              <div className="border border-t-0 rounded-b-xl p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-900 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Purchaser LAL %1–3</p><p className="text-[10px] text-gray-400">Satın alan müşteri benzerleri</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-700 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">High-Value LAL</p><p className="text-[10px] text-gray-400">Yüksek AOV müşteri benzerleri</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">ATC LAL</p><p className="text-[10px] text-gray-400">Add-to-cart yapan benzerleri</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Website Visitor LAL</p><p className="text-[10px] text-gray-400">Site ziyaretçisi benzerleri</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Email List LAL</p><p className="text-[10px] text-gray-400">CRM listesi benzerleri</p></div>
                </div>
              </div>
            </div>

            {/* 8. Contextual & Placement */}
            <div className="relative">
              <div className="bg-gray-900 text-white px-4 py-3 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wider">⑧ Contextual</p>
                  <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded">Google + YT</span>
                </div>
              </div>
              <div className="border border-t-0 rounded-b-xl p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-900 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">YouTube Kanalları</p><p className="text-[10px] text-gray-400">Koşu, fitness, outdoor vlogger'ları</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-700 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Content Kategorileri</p><p className="text-[10px] text-gray-400">Spor, sağlık, outdoor, wellness</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Placement (GDN)</p><p className="text-[10px] text-gray-400">Runner's World, Trail Mag, Strava blog…</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">App Targeting</p><p className="text-[10px] text-gray-400">Strava, Garmin, MyFitnessPal içi reklam</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0"></span>
                  <div><p className="text-xs font-semibold text-gray-900">Topic Targeting</p><p className="text-[10px] text-gray-400">Maraton hazırlık, ayakkabı inceleme…</p></div>
                </div>
              </div>
            </div>

          </div>

          {/* Summary Bar */}
          <div className="mt-8 rounded-xl bg-gray-50 border p-5">
            <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Targeting Katmanları Özet</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="text-center p-3 bg-white rounded-lg border">
                <p className="text-2xl font-black text-gray-900">8</p>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mt-1">Targeting Modeli</p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg border">
                <p className="text-2xl font-black text-gray-900">40+</p>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mt-1">Alt Segment</p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg border">
                <p className="text-2xl font-black text-gray-900">2</p>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mt-1">Ana Platform</p>
                <p className="text-[10px] text-gray-400">Meta + Google</p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg border">
                <p className="text-2xl font-black text-gray-900">3</p>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mt-1">Funnel Katmanı</p>
                <p className="text-[10px] text-gray-400">TOFU / MOFU / BOFU</p>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ───────── Kullanıcı Yolculuğu & Funnel Modeli ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Kullanıcı Yolculuğu & Harcama Modeli</h2>
          <p className="text-xs text-gray-400 mt-1">New User → Returning User yolculuğunda her adımda hangi kanalda, nasıl, ne kadar harcıyoruz</p>
        </div>
        <div className="p-6">

          {/* Funnel Flow Visual */}
          <div className="flex items-center justify-between mb-8 gap-1 overflow-x-auto">
            {[
              { step: '1', label: 'FARKINDALIK', sub: 'Awareness', width: 'w-full' },
              { step: '2', label: 'MARUZ BIRAKMA', sub: 'Exposure', width: 'w-[95%]' },
              { step: '3', label: 'DEĞERLENDIRME', sub: 'Consideration', width: 'w-[88%]' },
              { step: '4', label: 'KARAR ANI', sub: 'Decision', width: 'w-[78%]' },
              { step: '5', label: 'SATIN ALMA', sub: 'Purchase', width: 'w-[65%]' },
              { step: '6', label: 'TEKRAR HEDEFLEME', sub: 'Re-engagement', width: 'w-[55%]' },
              { step: '7', label: 'SADAKATx REFERANS', sub: 'Loyalty', width: 'w-[45%]' },
            ].map((s, i) => (
              <div key={i} className="flex-1 min-w-[120px]">
                <div className={`bg-gray-900 text-white rounded-lg py-2 px-3 text-center mx-auto ${s.width}`} style={{ opacity: 1 - i * 0.08 }}>
                  <p className="text-[10px] font-bold tracking-widest">{s.step}</p>
                  <p className="text-[9px] font-bold tracking-wider mt-0.5">{s.label}</p>
                  <p className="text-[8px] text-neutral-400">{s.sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Main Journey Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-900">
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[13%]">Adım</th>
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[10%]">Kullanıcı Tipi</th>
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[18%]">Kullanıcının Durumu</th>
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[17%]">Kanal & Kampanya Tipi</th>
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[17%]">Targeting Yöntemi</th>
                  <th className="text-center py-3 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[8%]">Bütçe Payı</th>
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[10%]">KPI</th>
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[7%]">Hedef</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">

                {/* 1. Farkındalık */}
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <td className="py-3 px-3" rowSpan="2">
                    <p className="font-bold text-gray-900">1. Farkındalık</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">İlk temas — "High5 ne?"</p>
                  </td>
                  <td className="py-3 px-3" rowSpan="2">
                    <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded bg-gray-900 text-white">NEW USER</span>
                  </td>
                  <td className="py-3 px-3" rowSpan="2">
                    <p className="text-xs text-gray-600">Markayı hiç duymamış. Koşu/spor ilgisi var ama High5'ı bilmiyor.</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">Meta — Video Views</p>
                    <p className="text-[10px] text-gray-400">Marka tanıtım videosu, lifestyle içerik</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Interest + Demografik + Lookalike (Purchaser LAL %3-5)</p>
                  </td>
                  <td className="py-3 px-3 text-center" rowSpan="2">
                    <span className="text-lg font-black text-gray-900">%20</span>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">CPM, Reach, Video View %50+</p>
                  </td>
                  <td className="py-3 px-3" rowSpan="2">
                    <p className="text-xs text-gray-600">Maksimum erişim</p>
                  </td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50/50">
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">YouTube — Video Ads</p>
                    <p className="text-[10px] text-gray-400">In-stream, bumper, discovery</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Contextual (spor kanalları) + Custom Intent</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">CPV, View Rate, Brand Lift</p>
                  </td>
                </tr>

                {/* 2. Maruz Bırakma */}
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-3" rowSpan="2">
                    <p className="font-bold text-gray-900">2. Maruz Bırakma</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Tekrar tekrar göster</p>
                  </td>
                  <td className="py-3 px-3" rowSpan="2">
                    <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded bg-gray-900 text-white">NEW USER</span>
                  </td>
                  <td className="py-3 px-3" rowSpan="2">
                    <p className="text-xs text-gray-600">Videoyu gördü ama siteye gelmedi. Markayı duydu, henüz ilgi oluşmadı. Frekans artırılmalı.</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">Meta — Reach & Frequency</p>
                    <p className="text-[10px] text-gray-400">Carousel, collection, kısa video</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Video Viewer Custom Audience (%25+) + Interest layering</p>
                  </td>
                  <td className="py-3 px-3 text-center" rowSpan="2">
                    <span className="text-lg font-black text-gray-900">%15</span>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Frequency (3-5x), CPM, CTR</p>
                  </td>
                  <td className="py-3 px-3" rowSpan="2">
                    <p className="text-xs text-gray-600">Hatırlama & tanınma</p>
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">GDN — Display Banners</p>
                    <p className="text-[10px] text-gray-400">Responsive, spor site placement</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Placement (spor siteleri) + Custom Audience</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Impression Share, Viewability</p>
                  </td>
                </tr>

                {/* 3. Değerlendirme */}
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <td className="py-3 px-3" rowSpan="3">
                    <p className="font-bold text-gray-900">3. Değerlendirme</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Araştırıyor, karşılaştırıyor</p>
                  </td>
                  <td className="py-3 px-3" rowSpan="3">
                    <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded bg-gray-700 text-white">WARM USER</span>
                  </td>
                  <td className="py-3 px-3" rowSpan="3">
                    <p className="text-xs text-gray-600">Siteye geldi, ürün baktı, "HOKA vs On" araştırması yapıyor. Fiyat karşılaştırması, review okuyor.</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">Google — Search Ads</p>
                    <p className="text-[10px] text-gray-400">Brand+Generic, Karşılaştırma KW</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Keyword: "hoka bondi fiyat", "en iyi trail ayakkabısı 2026"</p>
                  </td>
                  <td className="py-3 px-3 text-center" rowSpan="3">
                    <span className="text-lg font-black text-gray-900">%25</span>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">CPC, CTR, Site Visit</p>
                  </td>
                  <td className="py-3 px-3" rowSpan="3">
                    <p className="text-xs text-gray-600">Siteye trafik çek, listeler oluştur</p>
                  </td>
                </tr>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">Google — PMAX</p>
                    <p className="text-[10px] text-gray-400">Ürün feed, shopping, discovery</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">In-market audience + Feed signals</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">CPC, ROAS, Conv. Rate</p>
                  </td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50/50">
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">Meta — Traffic & Engagement</p>
                    <p className="text-[10px] text-gray-400">Blog / inceleme içerik, UGC</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Alışveriş intent sinyalleri + Lookalike (ATC LAL)</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">CPC, Landing Page View</p>
                  </td>
                </tr>

                {/* 4. Karar Anı */}
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-3" rowSpan="2">
                    <p className="font-bold text-gray-900">4. Karar Anı</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Sepette, son dürtü lazım</p>
                  </td>
                  <td className="py-3 px-3" rowSpan="2">
                    <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded bg-gray-500 text-white">HOT USER</span>
                  </td>
                  <td className="py-3 px-3" rowSpan="2">
                    <p className="text-xs text-gray-600">Ürünü gördü, belki sepete attı ama çıktı. Son push lazım — kargo bedava, stokta azalıyor, yorumlar…</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">Meta — Retargeting (DPA)</p>
                    <p className="text-[10px] text-gray-400">Dinamik ürün reklamları, carousel</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Sepet terk + Ürün görüntüleyen (son 7 gün)</p>
                  </td>
                  <td className="py-3 px-3 text-center" rowSpan="2">
                    <span className="text-lg font-black text-gray-900">%20</span>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">ROAS, CPA, Conv. Rate</p>
                  </td>
                  <td className="py-3 px-3" rowSpan="2">
                    <p className="text-xs text-gray-600">Satın almayı tamamlat</p>
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">Google — Remarketing + Brand Search</p>
                    <p className="text-[10px] text-gray-400">"high5" brand araması, RLSA</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">RLSA (site ziyaretçisine search'te tekrar çık) + Brand KW</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">CPA, ROAS, Conversion</p>
                  </td>
                </tr>

                {/* 5. Satın Alma */}
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <td className="py-3 px-3">
                    <p className="font-bold text-gray-900">5. Satın Alma</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Tamamlandı, artık müşteri</p>
                  </td>
                  <td className="py-3 px-3">
                    <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded bg-gray-900 text-white">CUSTOMER</span>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">İlk satın alma gerçekleşti. Onay maili, kargo takibi, unboxing deneyimi. Post-purchase journey başlıyor.</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">CRM — E-mail / SMS</p>
                    <p className="text-[10px] text-gray-400">Teşekkür, bakım rehberi, review isteği</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Transactional + Post-purchase flow (otomatik)</p>
                  </td>
                  <td className="py-3 px-3 text-center">
                    <span className="text-lg font-black text-gray-500">—</span>
                    <p className="text-[9px] text-gray-400 mt-0.5">Otomasyon</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">NPS, Review Rate, Delivery Speed</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Memnuniyet & deneyim</p>
                  </td>
                </tr>

                {/* 6. Tekrar Hedefleme */}
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-3" rowSpan="2">
                    <p className="font-bold text-gray-900">6. Tekrar Hedefleme</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Geri gel, yeni sezon var</p>
                  </td>
                  <td className="py-3 px-3" rowSpan="2">
                    <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded bg-gray-200 text-gray-800">RETURNING</span>
                  </td>
                  <td className="py-3 px-3" rowSpan="2">
                    <p className="text-xs text-gray-600">30-90 gün önce aldı, yeni sezon ürünleri çıktı. Ayakkabısı 500 km'ye yaklaştı. Cross-sell fırsatı.</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">Meta — Retargeting (Cross-sell)</p>
                    <p className="text-[10px] text-gray-400">Yeni sezon, tamamlayıcı ürün</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Mevcut müşteri listesi (CRM) + Exclude recent purchaser (&lt;14 gün)</p>
                  </td>
                  <td className="py-3 px-3 text-center" rowSpan="2">
                    <span className="text-lg font-black text-gray-900">%12</span>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">ROAS, Repeat Rate, AOV</p>
                  </td>
                  <td className="py-3 px-3" rowSpan="2">
                    <p className="text-xs text-gray-600">2. satın almayı tetikle</p>
                  </td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">CRM — E-mail / WhatsApp</p>
                    <p className="text-[10px] text-gray-400">Yenileme hatırlatma, özel erişim</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">RFM segmentasyonu — 60-90 gün inaktif tetikleyici</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Open Rate, CLV, Repeat Purchase</p>
                  </td>
                </tr>

                {/* 7. Sadakat & Referans */}
                <tr className="bg-gray-50/50">
                  <td className="py-3 px-3">
                    <p className="font-bold text-gray-900">7. Sadakat & Referans</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Elçimiz olsun</p>
                  </td>
                  <td className="py-3 px-3">
                    <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded bg-gray-200 text-gray-800">LOYAL</span>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">3+ kez almış, markayı seviyor. Koşu topluluğuna öneriyor, UGC üretiyor. VIP deneyim sunulmalı.</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs font-medium text-gray-900">Community & Loyalty</p>
                    <p className="text-[10px] text-gray-400">Referral program, VIP early access, ambassador</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">CRM VIP segment + High-value purchaser listesi</p>
                  </td>
                  <td className="py-3 px-3 text-center">
                    <span className="text-lg font-black text-gray-900">%8</span>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">CLV, Referral Rate, UGC Count</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-xs text-gray-600">Marka elçisi yarat</p>
                  </td>
                </tr>

              </tbody>
            </table>
          </div>

          {/* Budget Distribution Summary */}
          <div className="mt-8 grid grid-cols-7 gap-2">
            {[
              { label: 'Farkındalık', pct: '%20', type: 'NEW' },
              { label: 'Maruz Bırakma', pct: '%15', type: 'NEW' },
              { label: 'Değerlendirme', pct: '%25', type: 'WARM' },
              { label: 'Karar Anı', pct: '%20', type: 'HOT' },
              { label: 'Satın Alma', pct: '—', type: 'CRM' },
              { label: 'Tekrar', pct: '%12', type: 'RETURN' },
              { label: 'Sadakat', pct: '%8', type: 'LOYAL' },
            ].map((s, i) => (
              <div key={i} className="text-center p-3 rounded-lg border bg-gray-50">
                <p className="text-xl font-black text-gray-900">{s.pct}</p>
                <p className="text-[9px] font-bold text-gray-500 uppercase tracking-wider mt-1">{s.label}</p>
                <p className="text-[8px] text-gray-400 mt-0.5">{s.type}</p>
              </div>
            ))}
          </div>

          {/* Key Insight Cards */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-lg border p-4">
              <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">TOFU — Top of Funnel</p>
              <p className="text-xs text-gray-500 leading-relaxed">Farkındalık + Maruz Bırakma = <strong>%35</strong> bütçe. Yeni kullanıcıyı markayı tanıması, hatırlaması ve güven duyması için hazırlıyoruz. Video, reach, frequency.</p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">MOFU — Mid Funnel</p>
              <p className="text-xs text-gray-500 leading-relaxed">Değerlendirme = <strong>%25</strong> bütçe. En yüksek pay. Search, PMAX, traffic — kullanıcı aktif araştırıyor. Bu adımda siteye çekmek ve pixel'e düşürmek kritik.</p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">BOFU — Bottom of Funnel</p>
              <p className="text-xs text-gray-500 leading-relaxed">Karar + Tekrar + Sadakat = <strong>%40</strong> bütçe. Retargeting, DPA, CRM — zaten ilgilenen veya satın almış kullanıcıyı dönüştür ve geri getir. En yüksek ROAS burada.</p>
            </div>
          </div>

        </div>
      </div>

      {/* ───────── Hedef Placement Listesi ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Hedef Placement Listesi — Web Siteleri & YouTube Kanalları</h2>
          <p className="text-xs text-gray-400 mt-1">GDN placement, YouTube kanal hedefleme ve contextual reklam için kullanılacak siteler ve kanallar</p>
        </div>
        <div className="p-6 space-y-8">

          {/* ── YouTube Kanalları ── */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-1">YouTube Kanal Hedefleme</h3>
            <p className="text-xs text-gray-400 mb-4">In-stream, bumper ve discovery reklamlarını göstereceğimiz YouTube kanalları ve içerik tipleri</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-900">
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Kanal / Kanal Tipi</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Kategori</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">İçerik Örneği</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Neden Hedef?</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Öncelik</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 divide-y divide-gray-100">
                  {[
                    ['Koşu & Ayakkabı Review Kanalları', 'Koşu', '"HOKA Bondi 9 İnceleme", "En İyi 5 Koşu Ayakkabısı"', 'Satın alma öncesi araştıran kitleye direkt erişim', 'Çok Yüksek'],
                    ['Doctors of Running, Believe in the Run (TR altyazılı)', 'Koşu (Global)', 'Profesyonel ayakkabı analizi, performans testleri', 'Türk izleyicisi büyüyor, premium sporcu profili', 'Yüksek'],
                    ['Kişisel Koşu Vlogger\'ları', 'Koşu / Vlog', '"İlk Maratonum", "Günlük 10K Challenge"', 'Micro-influencer etkisi, samimi & güvenilir', 'Çok Yüksek'],
                    ['Trail Running & Outdoor Kanalları', 'Trail / Outdoor', '"Kaçkar Trail Koşusu", "Likya Yolu Deneyimi"', 'Salomon, HOKA Speedgoat hedef kitlesi birebir', 'Yüksek'],
                    ['Fitness & Antrenman Kanalları', 'Fitness', 'HIIT, fonksiyonel antrenman, form videoları', 'Cross-training ayakkabı ve giyim satış potansiyeli', 'Yüksek'],
                    ['Yoga & Pilates Kanalları', 'Wellness', 'Ev yogası, pilates rutini, mindfulness', 'Kadın ağırlıklı, wellness yaşam tarzı segmenti', 'Orta'],
                    ['Sağlıklı Beslenme & Meal Prep', 'Beslenme', '"Günlük Makrolarım", "Sporcu Beslenme Planı"', 'Sağlık bilincli + spor yapan profil örtüşmesi', 'Orta'],
                    ['Tech & Wearable Review', 'Teknoloji', 'Garmin inceleme, Apple Watch Ultra, akıllı saat', 'Performans takip eden, yatırım yapan sporcu', 'Yüksek'],
                    ['Sneaker & Streetwear Kültürü', 'Moda / Sneaker', 'Sneaker unboxing, OOTD, "On vs Nike" style karşılaştırma', 'Athleisure trendi — lifestyle ayakkabı satış fırsatı', 'Orta'],
                    ['Türk Maraton & Etkinlik Kanalları', 'Etkinlik', 'İstanbul Maratonu, Runatolia, parkrun videoları', 'Etkinlik öncesi ayakkabı satış piki — zamanlama önemli', 'Çok Yüksek'],
                    ['Outdoor & Seyahat Vlogger\'ları', 'Seyahat', '"Kapadokya Trekking", "Kamp Ekipmanları"', 'Outdoor ekipman ihtiyacı, Salomon/HOKA trail ürünleri', 'Orta'],
                    ['Motivasyon & Self-Improvement', 'Yaşam Tarzı', '"Sabah Rutini", "5\'te Kalk Koş", "Atomic Habits"', 'Kendine yatırım yapan, disiplinli profil — premium müşteri potansiyeli', 'Orta'],
                  ].map(([name, cat, content, reason, priority], i) => (
                    <tr key={i}>
                      <td className="py-2.5 px-4 font-medium text-gray-900">{name}</td>
                      <td className="py-2.5 px-4 text-gray-500">{cat}</td>
                      <td className="py-2.5 px-4 text-gray-500 text-xs">{content}</td>
                      <td className="py-2.5 px-4 text-gray-600 text-xs">{reason}</td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${
                          priority === 'Çok Yüksek' ? 'bg-gray-900 text-white' :
                          priority === 'Yüksek' ? 'bg-gray-700 text-white' :
                          'bg-gray-200 text-gray-700'
                        }`}>{priority}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Web Siteleri / GDN Placement ── */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-1">Web Sitesi & GDN Placement Hedefleri</h3>
            <p className="text-xs text-gray-400 mb-4">Google Display Network banner reklamları, native reklam ve sponsorlu içerik için hedef siteler</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-900">
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Site / Site Tipi</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Kategori</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Neden Hedef?</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Reklam Formatı</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Öncelik</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 divide-y divide-gray-100">
                  {[
                    ['Strava Blog / Metro', 'Koşu Platform', 'Aktif sporcu kitlesi, yüksek kalite trafik, koşu topluluğunun kalbi', 'Display + Native', 'Çok Yüksek'],
                    ['Garmin Connect / Blog', 'Wearable', 'Garmin kullanıcısı = performans odaklı sporcu, yüksek harcama gücü', 'Display Banner', 'Çok Yüksek'],
                    ['Runner\'s World Türkiye', 'Koşu Dergisi', 'Türkiye\'nin en büyük koşu medyası, review & haber odaklı', 'Display + Sponsorlu İçerik', 'Çok Yüksek'],
                    ['Trail.run Türkiye / Koşu Siteleri', 'Trail / Koşu', 'Trail koşu topluluğu, Salomon/HOKA ürün ilgisi çok yüksek', 'Display + Native', 'Yüksek'],
                    ['Sporx / Fanatik / NTV Spor', 'Spor Haber', 'Geniş spor kitlesi, maraton/koşu haberleri bölümü hedeflenebilir', 'Display Banner (Topic: Running)', 'Orta'],
                    ['Outdoor Rehber / Gezimanya', 'Outdoor / Seyahat', 'Trekking, hiking, doğa yürüyüşü yapan kitle — outdoor ekipman ihtiyacı', 'Display + Native', 'Yüksek'],
                    ['MyFitnessPal / Yazio (Web)', 'Beslenme', 'Kalori/makro takip eden sağlık bilincli kullanıcı, wellness profili', 'Display Banner', 'Orta'],
                    ['AllTrails / Komoot (Web)', 'Rota Planlama', 'Trail ve hiking rotası arayan aktif outdoor kullanıcısı', 'Display Banner', 'Yüksek'],
                    ['Reddit (r/RunningShoeGeeks, r/trailrunning)', 'Forum / Topluluk', 'Derinlemesine araştırma yapan, review okuyan, karar aşamasındaki kullanıcı', 'Display (Managed Placement)', 'Orta'],
                    ['Ekşi Sözlük — Koşu/Spor Başlıkları', 'Forum / Türkiye', 'Türkiye\'nin en büyük topluluk platformu, "en iyi koşu ayakkabısı" başlıkları', 'Display (Managed Placement)', 'Orta'],
                    ['Technopat / DonanimHaber', 'Teknoloji', 'Garmin, akıllı saat, wearable review — tech-savvy sporcu profili', 'Display Banner', 'Orta'],
                    ['GQ / Esquire / Elle Türkiye', 'Yaşam Tarzı', 'Premium yaşam tarzı içeriği, athleisure/fitness bölümleri', 'Display + Sponsorlu İçerik', 'Orta'],
                    ['WebMD / Sağlık Siteleri', 'Sağlık', '"Plantar fasit", "diz ağrısı koşu" arayanlar — long-tail dönüşüm fırsatı', 'Display (Contextual KW)', 'Yüksek'],
                    ['Hepsiburada / Trendyol Blog', 'E-ticaret', 'Spor kategorisinde aktif alışveriş yapan kullanıcı, fiyat karşılaştırması', 'Display (Conquest)', 'Yüksek'],
                    ['Marathon Siteleri (maratonizmir, istanbulmarathon)', 'Etkinlik', 'Etkinlik kayıt sayfası ziyaretçisi — ayakkabı satış piki öncesi', 'Display Banner', 'Çok Yüksek'],
                  ].map(([name, cat, reason, format, priority], i) => (
                    <tr key={i}>
                      <td className="py-2.5 px-4 font-medium text-gray-900">{name}</td>
                      <td className="py-2.5 px-4 text-gray-500">{cat}</td>
                      <td className="py-2.5 px-4 text-gray-600 text-xs">{reason}</td>
                      <td className="py-2.5 px-4 text-gray-500 text-xs">{format}</td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${
                          priority === 'Çok Yüksek' ? 'bg-gray-900 text-white' :
                          priority === 'Yüksek' ? 'bg-gray-700 text-white' :
                          'bg-gray-200 text-gray-700'
                        }`}>{priority}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Uygulama İçi Reklam Hedefleri ── */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-1">Uygulama İçi Reklam Hedefleri</h3>
            <p className="text-xs text-gray-400 mb-4">Mobil uygulamalar içinde gösterilecek reklamlar — kullanıcı en aktif olduğu anda yakalama</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-900">
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Uygulama</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Kategori</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Kullanıcı Profili</th>
                    <th className="text-left py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Reklam Anı</th>
                    <th className="text-center py-2.5 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Öncelik</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 divide-y divide-gray-100">
                  {[
                    ['Strava', 'Koşu / Bisiklet', 'Haftalık koşu yapan, performans takip eden aktif sporcu', 'Koşu sonrası özet ekranı — "ayakkabınız eskidi mi?" anı', 'Çok Yüksek'],
                    ['Garmin Connect', 'Wearable', 'Garmin saat sahibi, veri odaklı, premium profil', 'Antrenman planı görüntüleme sonrası', 'Çok Yüksek'],
                    ['Nike Run Club', 'Koşu', 'Nike ekosistemindeki koşucu — premium\'a upgrade potansiyeli', 'Koşu hedefi tamamlama anı', 'Yüksek'],
                    ['AllTrails / Komoot', 'Outdoor', 'Trail/hiking rotası planlayan doğa sever', 'Rota kaydedildiğinde — ekipman ihtiyacı hatırlatma', 'Yüksek'],
                    ['MyFitnessPal / Yazio', 'Beslenme', 'Kalori/makro takip eden sağlık bilincli kullanıcı', 'Günlük hedef tamamlama sonrası', 'Orta'],
                    ['Calm / Headspace', 'Wellness', 'Mindfulness, stres yönetimi — wellness yaşam tarzı', 'Meditasyon sonrası — "vücut için de yatırım yap"', 'Orta'],
                    ['Spotify (Spor Playlistleri)', 'Müzik', 'Koşu/gym playlist dinleyicisi, antrenman anında', 'Workout playlist arası reklam', 'Yüksek'],
                    ['Hava Durumu Uygulamaları', 'Utility', 'Koşu öncesi hava durumu kontrol eden sporcu', 'Hava güzel → "bugün koşuya çık" + ürün önerisi', 'Orta'],
                  ].map(([name, cat, profile, moment, priority], i) => (
                    <tr key={i}>
                      <td className="py-2.5 px-4 font-medium text-gray-900">{name}</td>
                      <td className="py-2.5 px-4 text-gray-500">{cat}</td>
                      <td className="py-2.5 px-4 text-gray-600 text-xs">{profile}</td>
                      <td className="py-2.5 px-4 text-gray-600 text-xs">{moment}</td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded ${
                          priority === 'Çok Yüksek' ? 'bg-gray-900 text-white' :
                          priority === 'Yüksek' ? 'bg-gray-700 text-white' :
                          'bg-gray-200 text-gray-700'
                        }`}>{priority}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>

      {/* ───────── Medya Planı — Branding ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Medya Planı</h2>
          <p className="text-xs text-gray-400 mt-1">Branding ve Performance ana başlıklarında platform bazlı yüzdesel yatırım dağılımı</p>
        </div>
        <div className="p-6">

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="rounded-lg border p-4">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Branding</p>
              <p className="text-2xl font-black text-gray-900 mt-1">%35</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Awareness · Reach · Video</p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Performance</p>
              <p className="text-2xl font-black text-gray-900 mt-1">%65</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Conversion · ROAS · Retargeting</p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Platform Sayısı</p>
              <p className="text-2xl font-black text-gray-900 mt-1">8</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Google, Meta, TikTok, YouTube…</p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Kampanya Tipi</p>
              <p className="text-2xl font-black text-gray-900 mt-1">16</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Search, PMAX, DPA, Spark Ads…</p>
            </div>
          </div>

          {/* ── SINGLE COMBINED TABLE ── */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr>
                  <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b-2 border-gray-900" rowSpan="2">Platform</th>
                  <th className="text-center py-2 px-4 text-[10px] font-bold text-blue-600 uppercase tracking-wider border-b border-blue-200 border-r-2 border-r-gray-300" colSpan="2">Branding</th>
                  <th className="text-center py-2 px-4 text-[10px] font-bold text-emerald-600 uppercase tracking-wider border-b border-emerald-200" colSpan="2">Performance</th>
                  <th className="text-center py-3 px-4 text-[10px] font-bold text-gray-900 uppercase tracking-wider border-b-2 border-gray-900 border-l-2 border-l-gray-300 bg-gray-50" rowSpan="2">Toplam</th>
                </tr>
                <tr>
                  <th className="text-left py-2 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b-2 border-gray-900">Campaign Types</th>
                  <th className="text-center py-2 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b-2 border-gray-900 border-r-2 border-r-gray-300 w-[7%]">Pay</th>
                  <th className="text-left py-2 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b-2 border-gray-900">Campaign Types</th>
                  <th className="text-center py-2 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b-2 border-gray-900 w-[7%]">Pay</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">

                {/* Google Ads */}
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2.5">
                      <img src="/google_ads-512.webp" alt="Google Ads" className="w-7 h-7 object-contain" />
                      <span className="font-bold text-gray-900">Google Ads</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-gray-300 text-xs">—</td>
                  <td className="py-4 px-4 text-center text-gray-300 border-r-2 border-r-gray-300">—</td>
                  <td className="py-4 px-4 text-xs text-gray-600">Search (Brand+Generic, Conquest) · Performance Max (PMAX) · Shopping · RLSA</td>
                  <td className="py-4 px-4 text-center"><span className="font-bold text-gray-900">%25</span></td>
                  <td className="py-4 px-4 text-center border-l-2 border-l-gray-300 bg-gray-50"><span className="text-base font-black text-gray-900">%25</span></td>
                </tr>

                {/* Meta Ads */}
                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2.5">
                      <img src="/6033716.png" alt="Meta" className="w-7 h-7 object-contain" />
                      <span className="font-bold text-gray-900">Meta Ads</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">Reach &amp; Frequency · Video Views (Reels, Stories) · Brand Awareness</td>
                  <td className="py-4 px-4 text-center border-r-2 border-r-gray-300"><span className="font-bold text-gray-900">%8</span></td>
                  <td className="py-4 px-4 text-xs text-gray-600">Sales (Advantage+ Shopping) · DPA · Conversion Optimize · Catalog Retargeting</td>
                  <td className="py-4 px-4 text-center"><span className="font-bold text-gray-900">%18</span></td>
                  <td className="py-4 px-4 text-center border-l-2 border-l-gray-300 bg-gray-50"><span className="text-base font-black text-gray-900">%26</span></td>
                </tr>

                {/* YouTube */}
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2.5">
                      <img src="/youtube.svg" alt="YouTube" className="w-7 h-7 object-contain" />
                      <span className="font-bold text-gray-900">YouTube</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">In-Stream (Skippable + Non-skip) · Bumper 6s · Video Discovery</td>
                  <td className="py-4 px-4 text-center border-r-2 border-r-gray-300"><span className="font-bold text-gray-900">%10</span></td>
                  <td className="py-4 px-4 text-gray-300 text-xs">—</td>
                  <td className="py-4 px-4 text-center text-gray-300">—</td>
                  <td className="py-4 px-4 text-center border-l-2 border-l-gray-300 bg-gray-50"><span className="text-base font-black text-gray-900">%10</span></td>
                </tr>

                {/* TikTok */}
                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2.5">
                      <img src="/tiktokv4.svg" alt="TikTok" className="w-7 h-7 object-contain" />
                      <span className="font-bold text-gray-900">TikTok</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">TopView / Top Feed · Spark Ads (UGC Boost) · Reach &amp; Video Views</td>
                  <td className="py-4 px-4 text-center border-r-2 border-r-gray-300"><span className="font-bold text-gray-900">%5</span></td>
                  <td className="py-4 px-4 text-xs text-gray-600">Conversion (Website) · Catalog Ads · Video Shopping Ads</td>
                  <td className="py-4 px-4 text-center"><span className="font-bold text-gray-900">%5</span></td>
                  <td className="py-4 px-4 text-center border-l-2 border-l-gray-300 bg-gray-50"><span className="text-base font-black text-gray-900">%10</span></td>
                </tr>

                {/* RTB House */}
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2.5">
                      <img src="/29374191.png" alt="RTB House" className="w-7 h-7 object-contain" />
                      <span className="font-bold text-gray-900">RTB House</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-gray-300 text-xs">—</td>
                  <td className="py-4 px-4 text-center text-gray-300 border-r-2 border-r-gray-300">—</td>
                  <td className="py-4 px-4 text-xs text-gray-600">Deep Learning Retargeting · Dynamic Product Banner · Cross-Device</td>
                  <td className="py-4 px-4 text-center"><span className="font-bold text-gray-900">%7</span></td>
                  <td className="py-4 px-4 text-center border-l-2 border-l-gray-300 bg-gray-50"><span className="text-base font-black text-gray-900">%7</span></td>
                </tr>

                {/* Programmatic */}
                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2.5">
                      <img src="/programmatic-logo.svg" alt="Programmatic" className="w-7 h-7 object-contain" />
                      <span className="font-bold text-gray-900">Programmatic</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">Rich Media Display (DV360) · Native (Taboola, Outbrain) · OLV Pre-roll</td>
                  <td className="py-4 px-4 text-center border-r-2 border-r-gray-300"><span className="font-bold text-gray-900">%5</span></td>
                  <td className="py-4 px-4 text-xs text-gray-600">DCO (Dynamic Creative) · Site Retargeting (30/60/90 gün)</td>
                  <td className="py-4 px-4 text-center"><span className="font-bold text-gray-900">%3</span></td>
                  <td className="py-4 px-4 text-center border-l-2 border-l-gray-300 bg-gray-50"><span className="text-base font-black text-gray-900">%8</span></td>
                </tr>

                {/* Pinterest */}
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2.5">
                      <img src="/pinterest-logo.svg" alt="Pinterest" className="w-7 h-7 object-contain" />
                      <span className="font-bold text-gray-900">Pinterest</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">Idea Pins (Lifestyle) · Awareness Kampanyaları</td>
                  <td className="py-4 px-4 text-center border-r-2 border-r-gray-300"><span className="font-bold text-gray-900">%3</span></td>
                  <td className="py-4 px-4 text-xs text-gray-600">Shopping Ads (Catalog) · Collection Ads</td>
                  <td className="py-4 px-4 text-center"><span className="font-bold text-gray-900">%3</span></td>
                  <td className="py-4 px-4 text-center border-l-2 border-l-gray-300 bg-gray-50"><span className="text-base font-black text-gray-900">%6</span></td>
                </tr>

                {/* Yandex */}
                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2.5">
                      <img src="/Yandex_Start_logo_(en).png" alt="Yandex" className="w-7 h-7 object-contain" />
                      <span className="font-bold text-gray-900">Yandex</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">Navigasyon Pin Reklam · Rota Üzeri Banner</td>
                  <td className="py-4 px-4 text-center border-r-2 border-r-gray-300"><span className="font-bold text-gray-900">%4</span></td>
                  <td className="py-4 px-4 text-xs text-gray-600">Smart Banner (Dynamic) · Display Remarketing</td>
                  <td className="py-4 px-4 text-center"><span className="font-bold text-gray-900">%4</span></td>
                  <td className="py-4 px-4 text-center border-l-2 border-l-gray-300 bg-gray-50"><span className="text-base font-black text-gray-900">%8</span></td>
                </tr>

              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-900 bg-gray-100">
                  <td className="py-3 px-4 font-bold text-gray-900">TOPLAM</td>
                  <td className="py-3 px-4"></td>
                  <td className="py-3 px-4 text-center border-r-2 border-r-gray-300"><span className="text-base font-black text-blue-700">%35</span></td>
                  <td className="py-3 px-4"></td>
                  <td className="py-3 px-4 text-center"><span className="text-base font-black text-emerald-700">%65</span></td>
                  <td className="py-3 px-4 text-center border-l-2 border-l-gray-300 bg-gray-200"><span className="text-lg font-black text-gray-900">%100</span></td>
                </tr>
                <tr className="bg-gray-900 text-white">
                  <td className="py-3 px-4 font-bold rounded-bl-lg">Grand Total</td>
                  <td className="py-3 px-4" colSpan="4">
                    <span className="text-xs text-neutral-400">8 Platform · 16 Campaign Type · Branding %35 + Performance %65</span>
                  </td>
                  <td className="py-3 px-4 text-center font-black text-xl rounded-br-lg">%100</td>
                </tr>
              </tfoot>
            </table>
          </div>

        </div>
      </div>

      {/* ───────── Müşteri Segmentasyonu ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Müşteri Segmentasyonu</h2>
          <p className="text-xs text-gray-400 mt-1">Mevcut müşteri tabanının davranışsal ve değer bazlı segmentlere ayrılması — remarketing, CRM ve otomasyon senaryoları</p>
        </div>
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-900">
                  <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[5%]">#</th>
                  <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[18%]">Segment Adı</th>
                  <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[24%]">Tanım / Kural</th>
                  <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[20%]">Aksiyon</th>
                  <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[18%]">Kanal</th>
                  <th className="text-center py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[8%]">Öncelik</th>
                  <th className="text-center py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider w-[7%]">Tahmini Oran</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">

                {/* 1 — Uyuyan Müşteriler */}
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-4 text-gray-400 font-mono text-xs">01</td>
                  <td className="py-4 px-4">
                    <span className="font-bold text-gray-900">Uyuyan Müşteriler</span>
                    <span className="ml-2 inline-block px-2 py-0.5 bg-red-50 text-red-600 text-[10px] font-bold rounded-full">Win-back</span>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">Son 90+ gündür sipariş vermeyen, daha önce en az 1 satın alma yapmış kullanıcılar</td>
                  <td className="py-4 px-4 text-xs text-gray-600">Kişiselleştirilmiş indirim kodu + &quot;Seni özledik&quot; kampanyası · Önceki satın alma kategorisine göre ürün önerisi</td>
                  <td className="py-4 px-4 text-xs text-gray-600">E-posta · Push · Meta DPA · RTB House Retargeting</td>
                  <td className="py-4 px-4 text-center"><span className="inline-block px-2.5 py-1 bg-red-100 text-red-700 text-[10px] font-bold rounded-full">Yüksek</span></td>
                  <td className="py-4 px-4 text-center text-xs font-bold text-gray-900">~%25</td>
                </tr>

                {/* 2 — Tekrar Eden Alıcılar */}
                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-4 px-4 text-gray-400 font-mono text-xs">02</td>
                  <td className="py-4 px-4">
                    <span className="font-bold text-gray-900">Tekrar Eden Alıcılar</span>
                    <span className="ml-2 inline-block px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-bold rounded-full">Loyal</span>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">2+ sipariş veren kullanıcılar — marka sadakati potansiyeli yüksek grup</td>
                  <td className="py-4 px-4 text-xs text-gray-600">Early access kampanyaları · VIP indirim · Cross-sell (ayakkabı aldıysa çorap/aksesuar öner)</td>
                  <td className="py-4 px-4 text-xs text-gray-600">E-posta · SMS · WhatsApp · Meta Lookalike Seed</td>
                  <td className="py-4 px-4 text-center"><span className="inline-block px-2.5 py-1 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded-full">Yüksek</span></td>
                  <td className="py-4 px-4 text-center text-xs font-bold text-gray-900">~%12</td>
                </tr>

                {/* 3 — Kategori Spesifik */}
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-4 text-gray-400 font-mono text-xs">03</td>
                  <td className="py-4 px-4">
                    <span className="font-bold text-gray-900">Go Run Alıcıları</span>
                    <span className="ml-2 inline-block px-2 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-bold rounded-full">Kategori</span>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">skechers.com.tr üzerinden Go Run serisi satın almış kullanıcılar</td>
                  <td className="py-4 px-4 text-xs text-gray-600">Yeni sezon Go Run modelleri tanıtım · Koşu aksesuarları cross-sell · Sezon değişiminde yenileme hatırlatması</td>
                  <td className="py-4 px-4 text-xs text-gray-600">E-posta · Google RLSA · Meta Custom Audience</td>
                  <td className="py-4 px-4 text-center"><span className="inline-block px-2.5 py-1 bg-blue-100 text-blue-700 text-[10px] font-bold rounded-full">Orta</span></td>
                  <td className="py-4 px-4 text-center text-xs font-bold text-gray-900">~%8</td>
                </tr>

                {/* 4 — Yüksek AOV */}
                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-4 px-4 text-gray-400 font-mono text-xs">04</td>
                  <td className="py-4 px-4">
                    <span className="font-bold text-gray-900">Yüksek Sepet Ortalaması</span>
                    <span className="ml-2 inline-block px-2 py-0.5 bg-amber-50 text-amber-600 text-[10px] font-bold rounded-full">High AOV</span>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">Ortalama sepet tutarı site genelinin 1.5x üzerinde olan kullanıcılar (premium alıcı profili)</td>
                  <td className="py-4 px-4 text-xs text-gray-600">Premium koleksiyon lansmanları · Ücretsiz kargo · Bundle teklifleri · Kişiye özel kupon</td>
                  <td className="py-4 px-4 text-xs text-gray-600">E-posta · SMS · RTB House · Programmatic DCO</td>
                  <td className="py-4 px-4 text-center"><span className="inline-block px-2.5 py-1 bg-red-100 text-red-700 text-[10px] font-bold rounded-full">Yüksek</span></td>
                  <td className="py-4 px-4 text-center text-xs font-bold text-gray-900">~%10</td>
                </tr>

                {/* 5 — Tek Seferlik */}
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-4 text-gray-400 font-mono text-xs">05</td>
                  <td className="py-4 px-4">
                    <span className="font-bold text-gray-900">Tek Seferlik Alıcılar</span>
                    <span className="ml-2 inline-block px-2 py-0.5 bg-orange-50 text-orange-600 text-[10px] font-bold rounded-full">One-timer</span>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">Yalnızca 1 kez sipariş vermiş ve son 30-90 gün içinde tekrar gelmemiş kullanıcılar</td>
                  <td className="py-4 px-4 text-xs text-gray-600">İkinci alışverişe özel %15 indirim · &quot;İlk siparişini beğendin mi?&quot; anket + ürün önerisi</td>
                  <td className="py-4 px-4 text-xs text-gray-600">E-posta · Push · Meta Retargeting · Google Display</td>
                  <td className="py-4 px-4 text-center"><span className="inline-block px-2.5 py-1 bg-orange-100 text-orange-700 text-[10px] font-bold rounded-full">Orta</span></td>
                  <td className="py-4 px-4 text-center text-xs font-bold text-gray-900">~%35</td>
                </tr>

                {/* 6 — Sepet Terk */}
                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-4 px-4 text-gray-400 font-mono text-xs">06</td>
                  <td className="py-4 px-4">
                    <span className="font-bold text-gray-900">Sepet Terk Edenler</span>
                    <span className="ml-2 inline-block px-2 py-0.5 bg-purple-50 text-purple-600 text-[10px] font-bold rounded-full">Cart Abandon</span>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">Sepete ürün ekleyip satın almadan çıkan kullanıcılar (son 7 gün aktif)</td>
                  <td className="py-4 px-4 text-xs text-gray-600">Sepetteki ürünle dinamik reklam · 24 saat sonra hatırlatma e-posta · 48 saat sonra sınırlı indirim</td>
                  <td className="py-4 px-4 text-xs text-gray-600">E-posta · Meta DPA · RTB House · Google RLSA</td>
                  <td className="py-4 px-4 text-center"><span className="inline-block px-2.5 py-1 bg-red-100 text-red-700 text-[10px] font-bold rounded-full">Yüksek</span></td>
                  <td className="py-4 px-4 text-center text-xs font-bold text-gray-900">~%18</td>
                </tr>

                {/* 7 — İndirim Avcısı */}
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-4 text-gray-400 font-mono text-xs">07</td>
                  <td className="py-4 px-4">
                    <span className="font-bold text-gray-900">İndirim Avcıları</span>
                    <span className="ml-2 inline-block px-2 py-0.5 bg-pink-50 text-pink-600 text-[10px] font-bold rounded-full">Promo Hunter</span>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">Yalnızca kampanya/indirim dönemlerinde alışveriş yapan, kupon kullanan kullanıcılar</td>
                  <td className="py-4 px-4 text-xs text-gray-600">Flash sale öncesi erken bildirim · Sınırlı stok urgency mesajları · Full price ürünlere kademeli indirim denemeleri</td>
                  <td className="py-4 px-4 text-xs text-gray-600">E-posta · Push · SMS</td>
                  <td className="py-4 px-4 text-center"><span className="inline-block px-2.5 py-1 bg-orange-100 text-orange-700 text-[10px] font-bold rounded-full">Orta</span></td>
                  <td className="py-4 px-4 text-center text-xs font-bold text-gray-900">~%15</td>
                </tr>

                {/* 8 — Sezonsal Alıcı */}
                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-4 px-4 text-gray-400 font-mono text-xs">08</td>
                  <td className="py-4 px-4">
                    <span className="font-bold text-gray-900">Sezonsal Alıcılar</span>
                    <span className="ml-2 inline-block px-2 py-0.5 bg-cyan-50 text-cyan-600 text-[10px] font-bold rounded-full">Seasonal</span>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">Belirli dönemlerde (okul açılışı, yaz sezonu, Black Friday) alışveriş yapan döngüsel alıcılar</td>
                  <td className="py-4 px-4 text-xs text-gray-600">Sezon başlamadan 2 hafta önce hatırlatma · Geçen yıl aldığı kategoriden yeni model önerisi</td>
                  <td className="py-4 px-4 text-xs text-gray-600">E-posta · Meta · Google Ads Remarketing</td>
                  <td className="py-4 px-4 text-center"><span className="inline-block px-2.5 py-1 bg-blue-100 text-blue-700 text-[10px] font-bold rounded-full">Orta</span></td>
                  <td className="py-4 px-4 text-center text-xs font-bold text-gray-900">~%20</td>
                </tr>

                {/* 9 — Çoklu Kategori */}
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-4 text-gray-400 font-mono text-xs">09</td>
                  <td className="py-4 px-4">
                    <span className="font-bold text-gray-900">Çoklu Kategori Alıcıları</span>
                    <span className="ml-2 inline-block px-2 py-0.5 bg-violet-50 text-violet-600 text-[10px] font-bold rounded-full">Multi-cat</span>
                  </td>
                  <td className="py-4 px-4 text-xs text-gray-600">2+ farklı kategoriden (ayakkabı + giyim, koşu + günlük) ürün satın almış kullanıcılar</td>
                  <td className="py-4 px-4 text-xs text-gray-600">Henüz keşfetmediği 3. kategori önerisi · Kombin/set teklifleri · Lifestyle içerik kampanyaları</td>
                  <td className="py-4 px-4 text-xs text-gray-600">E-posta · Meta Lookalike · Programmatic</td>
                  <td className="py-4 px-4 text-center"><span className="inline-block px-2.5 py-1 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded-full">Yüksek</span></td>
                  <td className="py-4 px-4 text-center text-xs font-bold text-gray-900">~%6</td>
                </tr>

              </tbody>
            </table>
          </div>

          {/* Segment Özet Kartları */}
          <div className="mt-6 grid grid-cols-3 gap-4">
            <div className="rounded-xl bg-red-50 border border-red-100 p-4">
              <p className="text-[10px] font-bold text-red-400 uppercase tracking-wider">Yüksek Öncelik</p>
              <p className="text-2xl font-black text-red-700 mt-1">4 Segment</p>
              <p className="text-xs text-red-500 mt-1">Uyuyan · Tekrar Eden · Yüksek AOV · Sepet Terk · Çoklu Kategori</p>
            </div>
            <div className="rounded-xl bg-amber-50 border border-amber-100 p-4">
              <p className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">Orta Öncelik</p>
              <p className="text-2xl font-black text-amber-700 mt-1">4 Segment</p>
              <p className="text-xs text-amber-500 mt-1">Go Run · Tek Seferlik · İndirim Avcısı · Sezonsal</p>
            </div>
            <div className="rounded-xl bg-gray-900 p-4">
              <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider">Toplam Segment</p>
              <p className="text-2xl font-black text-white mt-1">9 Segment</p>
              <p className="text-xs text-neutral-400 mt-1">RFM + Kategori + Davranışsal kırılım</p>
            </div>
          </div>
        </div>
      </div>

      {/* ───────── 12 Aylık Bütçe Dağılımı ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">12 Aylık Bütçe Dağılımı</h2>
          <p className="text-xs text-gray-400 mt-1">Aylık yüzdesel yatırım dağılımı — sezonalite, kampanya dönemleri ve tüketici davranış döngüsüne göre ağırlıklandırılmıştır</p>
        </div>
        <div className="p-6">

          {/* Bar Chart Visual */}
          <div className="flex items-end gap-2 h-32 mb-2 px-2">
            {[
              { ay: 'Oca', pct: 9, color: 'bg-blue-500' },
              { ay: 'Şub', pct: 8, color: 'bg-blue-500' },
              { ay: 'Mar', pct: 9, color: 'bg-blue-500' },
              { ay: 'Nis', pct: 7, color: 'bg-gray-400' },
              { ay: 'May', pct: 7, color: 'bg-gray-400' },
              { ay: 'Haz', pct: 6, color: 'bg-gray-300' },
              { ay: 'Tem', pct: 5, color: 'bg-gray-300' },
              { ay: 'Ağu', pct: 6, color: 'bg-gray-300' },
              { ay: 'Eyl', pct: 10, color: 'bg-emerald-500' },
              { ay: 'Eki', pct: 9, color: 'bg-emerald-500' },
              { ay: 'Kas', pct: 14, color: 'bg-orange-500' },
              { ay: 'Ara', pct: 10, color: 'bg-red-500' },
            ].map((m, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-[11px] font-black text-gray-900">%{m.pct}</span>
                <div className={`w-full rounded-t-md ${m.color}`} style={{ height: `${(m.pct / 14) * 100}%` }}></div>
                <span className="text-[10px] font-bold text-gray-500 mt-1">{m.ay}</span>
              </div>
            ))}
          </div>

          {/* Detail Table */}
          <div className="overflow-x-auto mt-6">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-900">
                  <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Ay</th>
                  <th className="text-center py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Pay (%)</th>
                  <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Dönem</th>
                  <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Sezon Notları / Önemli Günler</th>
                  <th className="text-center py-3 px-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Yoğunluk</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">

                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4 font-bold text-gray-900">Ocak</td>
                  <td className="py-3 px-4 text-center"><span className="text-base font-black text-gray-900">%9</span></td>
                  <td className="py-3 px-4"><span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-bold rounded-full">Q1</span></td>
                  <td className="py-3 px-4 text-xs text-gray-600">Yeni yıl indirimleri · Kış sezonu devam · Yılbaşı hediye kartı kullanımı · Ocak kampanyaları</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-3 px-4 font-bold text-gray-900">Şubat</td>
                  <td className="py-3 px-4 text-center"><span className="text-base font-black text-gray-900">%8</span></td>
                  <td className="py-3 px-4"><span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-bold rounded-full">Q1</span></td>
                  <td className="py-3 px-4 text-xs text-gray-600">Sevgililer Günü · Kış sezonu sonu · İlkbahar koleksiyonu teaser · Yarıyıl tatili</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4 font-bold text-gray-900">Mart</td>
                  <td className="py-3 px-4 text-center"><span className="text-base font-black text-gray-900">%9</span></td>
                  <td className="py-3 px-4"><span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-bold rounded-full">Q1</span></td>
                  <td className="py-3 px-4 text-xs text-gray-600">8 Mart Dünya Kadınlar Günü · İlkbahar lansmanı · Koşu sezonu açılışı · Outdoor aktivite artışı</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-3 px-4 font-bold text-gray-900">Nisan</td>
                  <td className="py-3 px-4 text-center"><span className="text-base font-black text-gray-900">%7</span></td>
                  <td className="py-3 px-4"><span className="inline-block px-2 py-0.5 bg-violet-50 text-violet-600 text-[10px] font-bold rounded-full">Q2</span></td>
                  <td className="py-3 px-4 text-xs text-gray-600">23 Nisan · Ramazan Bayramı (değişken) · Bahar dönemi · Ara sezon geçişi</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4 font-bold text-gray-900">Mayıs</td>
                  <td className="py-3 px-4 text-center"><span className="text-base font-black text-gray-900">%7</span></td>
                  <td className="py-3 px-4"><span className="inline-block px-2 py-0.5 bg-violet-50 text-violet-600 text-[10px] font-bold rounded-full">Q2</span></td>
                  <td className="py-3 px-4 text-xs text-gray-600">Anneler Günü · 19 Mayıs · Kurban Bayramı (değişken) · Yaz öncesi hazırlık</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-3 px-4 font-bold text-gray-900">Haziran</td>
                  <td className="py-3 px-4 text-center"><span className="text-base font-black text-gray-900">%6</span></td>
                  <td className="py-3 px-4"><span className="inline-block px-2 py-0.5 bg-violet-50 text-violet-600 text-[10px] font-bold rounded-full">Q2</span></td>
                  <td className="py-3 px-4 text-xs text-gray-600">Babalar Günü · Yaz sezonu başlangıcı · Tatil dönemi · Düşük trafik başlangıcı</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4 font-bold text-gray-900">Temmuz</td>
                  <td className="py-3 px-4 text-center"><span className="text-base font-black text-gray-900">%5</span></td>
                  <td className="py-3 px-4"><span className="inline-block px-2 py-0.5 bg-amber-50 text-amber-600 text-[10px] font-bold rounded-full">Q3</span></td>
                  <td className="py-3 px-4 text-xs text-gray-600">Yaz tatili yoğun dönem · E-ticaret düşük sezon · Maintenance bütçe · A/B test dönemi</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-3 px-4 font-bold text-gray-900">Ağustos</td>
                  <td className="py-3 px-4 text-center"><span className="text-base font-black text-gray-900">%6</span></td>
                  <td className="py-3 px-4"><span className="inline-block px-2 py-0.5 bg-amber-50 text-amber-600 text-[10px] font-bold rounded-full">Q3</span></td>
                  <td className="py-3 px-4 text-xs text-gray-600">Okul alışverişi erken başlangıcı · Back to School teaser · Yaz sonu indirimleri</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-gray-200">
                  <td className="py-3 px-4 font-bold text-gray-900">Eylül</td>
                  <td className="py-3 px-4 text-center"><span className="text-base font-black text-emerald-700">%10</span></td>
                  <td className="py-3 px-4"><span className="inline-block px-2 py-0.5 bg-amber-50 text-amber-600 text-[10px] font-bold rounded-full">Q3</span></td>
                  <td className="py-3 px-4 text-xs text-gray-600">Okul açılışı · Back to School zirve · Sonbahar koleksiyonu lansmanı · Spor sezonu başlangıcı</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-3 px-4 font-bold text-gray-900">Ekim</td>
                  <td className="py-3 px-4 text-center"><span className="text-base font-black text-gray-900">%9</span></td>
                  <td className="py-3 px-4"><span className="inline-block px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-bold rounded-full">Q4</span></td>
                  <td className="py-3 px-4 text-xs text-gray-600">29 Ekim · Sonbahar kampanyaları · Black Friday pre-heat · 11.11 Singles Day</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-gray-200 bg-orange-50/50">
                  <td className="py-3 px-4 font-bold text-orange-700">Kasım</td>
                  <td className="py-3 px-4 text-center"><span className="text-xl font-black text-orange-600">%14</span></td>
                  <td className="py-3 px-4"><span className="inline-block px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-bold rounded-full">Q4</span></td>
                  <td className="py-3 px-4 text-xs text-orange-700 font-medium">Black Friday · Cyber Monday · Efsane Cuma · Yılın en yüksek conversion dönemi · Agresif remarketing</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-orange-500"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-orange-500"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-orange-500"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-orange-500"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-orange-500"></div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-gray-200 bg-gray-50/30">
                  <td className="py-3 px-4 font-bold text-gray-900">Aralık</td>
                  <td className="py-3 px-4 text-center"><span className="text-base font-black text-red-600">%10</span></td>
                  <td className="py-3 px-4"><span className="inline-block px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-bold rounded-full">Q4</span></td>
                  <td className="py-3 px-4 text-xs text-gray-600">Yılbaşı hediye sezonu · Kış koleksiyonu · Son dakika alışverişi · Yıl sonu kapanış kampanyaları</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                    </div>
                  </td>
                </tr>

              </tbody>
              <tfoot>
                <tr className="bg-gray-900 text-white">
                  <td className="py-3 px-4 font-bold rounded-bl-lg">TOPLAM</td>
                  <td className="py-3 px-4 text-center font-black text-xl">%100</td>
                  <td className="py-3 px-4" colSpan="2"><span className="text-xs text-neutral-400">Q1 %26 · Q2 %20 · Q3 %21 · Q4 %33</span></td>
                  <td className="py-3 px-4 rounded-br-lg"></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Quarterly Summary */}
          <div className="mt-6 grid grid-cols-4 gap-3">
            <div className="rounded-xl bg-blue-50 border border-blue-100 p-4 text-center">
              <p className="text-[10px] font-bold text-blue-400 uppercase tracking-wider">Q1 — Oca / Şub / Mar</p>
              <p className="text-2xl font-black text-blue-700 mt-1">%26</p>
              <p className="text-[10px] text-blue-500 mt-1">Kış sezonu + Yeni yıl</p>
            </div>
            <div className="rounded-xl bg-violet-50 border border-violet-100 p-4 text-center">
              <p className="text-[10px] font-bold text-violet-400 uppercase tracking-wider">Q2 — Nis / May / Haz</p>
              <p className="text-2xl font-black text-violet-700 mt-1">%20</p>
              <p className="text-[10px] text-violet-500 mt-1">Ara dönem + Geçiş</p>
            </div>
            <div className="rounded-xl bg-amber-50 border border-amber-100 p-4 text-center">
              <p className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">Q3 — Tem / Ağu / Eyl</p>
              <p className="text-2xl font-black text-amber-700 mt-1">%21</p>
              <p className="text-[10px] text-amber-500 mt-1">Düşük sezon → Back to School</p>
            </div>
            <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4 text-center">
              <p className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider">Q4 — Eki / Kas / Ara</p>
              <p className="text-2xl font-black text-emerald-700 mt-1">%33</p>
              <p className="text-[10px] text-emerald-500 mt-1">Peak Season + Black Friday</p>
            </div>
          </div>

        </div>
      </div>

      {/* ───────── Ajans Yaklaşımı & Stratejik Sorular ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Ajans Yaklaşımı &mdash; Stratejik İş Ortaklığı</h2>
          <p className="text-xs text-gray-400 mt-1">Bütçe yönetiminde yalnızca operasyon değil; sorgulayan, öneri getiren ve uzun vadede markayla birlikte büyümeyi hedefleyen bir iş ortağı olarak konumlanıyoruz</p>
        </div>
        <div className="p-6">

          {/* Manifesto */}
          <div className="rounded-xl bg-gray-900 text-white p-6 mb-8">
            <p className="text-sm leading-relaxed text-neutral-300">
              Şeffaf iletişim, düzenli ve yorum içeren raporlama ve <strong className="text-white">&quot;ne oldu?&quot;</strong> sorusunun ötesinde <strong className="text-white">&quot;neden oldu ve nasıl ilerlemeliyiz?&quot;</strong> perspektifi bizim için temel yaklaşımdır. Aşağıdaki sorular, her raporlama döngüsünde ve stratejik toplantıda ajans olarak aktif şekilde sorguladığımız konulardır.
            </p>
          </div>

          {/* Soru Kategorileri */}
          <div className="space-y-6">

            {/* 1 — Performans & Verimlilik */}
            <div className="rounded-xl border border-blue-100 overflow-hidden">
              <div className="bg-blue-50 px-5 py-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center text-xs font-black">01</span>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Performans &amp; Verimlilik Sorguları</h3>
                  <p className="text-[10px] text-gray-500 mt-0.5">Her hafta / aylık rapor döngüsünde</p>
                </div>
              </div>
              <div className="p-5">
                <ul className="space-y-3">
                  {[
                    ['CPC neden bu ay yükseldi?', 'Rakip aktivitesi mi, sezonsal talep artışı mı, yoksa bid strategy kaynaklı mı? Kuru hesaba katınca dolar bazında da yükseldi mi?'],
                    ['Hangi kampanya tipi en düşük CPA ile dönüşüm getiriyor?', 'Search vs PMAX vs Shopping performans karşılaştırması — bütçe kaydırma önerisi var mı?'],
                    ['ROAS hedefinden sapma var mı, varsa nedeni ne?', 'Kreatif yorgunluğu mu, hedef kitle doygunluğu mu, yoksa ürün karması mı etkiliyor?'],
                    ['Meta ve Google arasındaki bütçe dağılımı optimal mi?', 'Marginal ROAS analizi yapıldığında hangi platforma ek bütçe kaydırılmalı?'],
                    ['Dolar bazında maliyetlerimiz geçen aya göre ne durumda?', 'TL\'deki artış gerçek bir maliyet artışı mı yoksa kur etkisi mi? Net performans değişimi nedir?'],
                  ].map(([q, detail], i) => (
                    <li key={i} className="flex gap-3">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">?</span>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{q}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{detail}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 2 — Hedef Kitle & Segmentasyon */}
            <div className="rounded-xl border border-emerald-100 overflow-hidden">
              <div className="bg-emerald-50 px-5 py-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center text-xs font-black">02</span>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Hedef Kitle &amp; Segmentasyon Sorguları</h3>
                  <p className="text-[10px] text-gray-500 mt-0.5">Aylık / çeyreklik strateji toplantılarında</p>
                </div>
              </div>
              <div className="p-5">
                <ul className="space-y-3">
                  {[
                    ['Lookalike audience\'lar hâlâ performans gösteriyor mu?', 'Seed listemiz ne zaman güncellendi? Purchase-based seed vs Add-to-cart seed karşılaştırması yapıldı mı?'],
                    ['Sepet terk segmentinde recovery oranımız kaç?', 'E-posta + DPA + RTB House birlikte çalışıyor mu, yoksa çakışma (overlap) mı var?'],
                    ['Yeni müşteri kazanım maliyeti (nCAC) ile tekrar müşteri maliyeti arasındaki fark nedir?', 'Bütçenin ne kadarı yeni müşteriye, ne kadarı mevcut müşteriye gidiyor — bu oran doğru mu?'],
                    ['Hangi persona en yüksek CLV üretiyor?', 'Go Run alıcıları mı, athleisure segmenti mi? Buna göre bütçe ağırlığı değişmeli mi?'],
                    ['Coğrafi segmentasyonda kaçırdığımız fırsat var mı?', 'İstanbul dışı şehirlerde (Ankara, İzmir, Antalya) potansiyel büyüme alanı test edildi mi?'],
                  ].map(([q, detail], i) => (
                    <li key={i} className="flex gap-3">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">?</span>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{q}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{detail}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 3 — Kreatif & İçerik */}
            <div className="rounded-xl border border-violet-100 overflow-hidden">
              <div className="bg-violet-50 px-5 py-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-violet-600 text-white flex items-center justify-center text-xs font-black">03</span>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Kreatif &amp; İçerik Sorguları</h3>
                  <p className="text-[10px] text-gray-500 mt-0.5">Her kampanya döngüsü öncesi ve sonrası</p>
                </div>
              </div>
              <div className="p-5">
                <ul className="space-y-3">
                  {[
                    ['Hangi kreatif format en iyi CTR ve CVR üretiyor?', 'Statik vs video vs carousel — platform bazında farklılık var mı? TikTok\'ta UGC, Meta\'da katalog mu kazanıyor?'],
                    ['Kreatif yorgunluğu (ad fatigue) başladı mı?', 'Frequency 3+ olan kampanyalarda CTR düşüşü gözlemleniyor mu? Yeni kreatif rotasyonu planı ne?'],
                    ['Ürün görselleri mi yoksa lifestyle görselleri mi daha çok dönüştürüyor?', 'A/B test sonuçları ne diyor? Funnel aşamasına göre farklılık var mı?'],
                    ['Landing page deneyimi reklam vaadiyle tutarlı mı?', 'Reklam mesajı ile açılış sayfası arasında kopukluk var mı? Bounce rate ve session duration buna işaret ediyor mu?'],
                  ].map(([q, detail], i) => (
                    <li key={i} className="flex gap-3">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">?</span>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{q}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{detail}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 4 — Bütçe & Planlama */}
            <div className="rounded-xl border border-amber-100 overflow-hidden">
              <div className="bg-amber-50 px-5 py-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-amber-600 text-white flex items-center justify-center text-xs font-black">04</span>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Bütçe &amp; Kaynak Planlama Sorguları</h3>
                  <p className="text-[10px] text-gray-500 mt-0.5">Aylık bütçe review toplantılarında</p>
                </div>
              </div>
              <div className="p-5">
                <ul className="space-y-3">
                  {[
                    ['Bu ayın bütçesi planlandığı gibi mi harcandı, sapma varsa neden?', 'Pacing doğru mu? Ay ortasında agresif harcama mı yapıldı yoksa ay sonuna mı yığıldı?'],
                    ['Black Friday / kampanya dönemlerine kaç hafta kaldı, pre-heat bütçesi ayrıldı mı?', 'Geçen yılın kampanya verileri benchmark olarak kullanılıyor mu?'],
                    ['Branding ve Performance arasındaki %35-%65 dağılım hâlâ doğru mu?', 'Marka bilinirliği hedeflerine ulaşıldıysa performansa mı kaydırmalı, yoksa branding awareness hâlâ yetersiz mi?'],
                    ['Yeni bir platform testi (Pinterest, Programmatic) için pilot bütçe ayırdık mı?', 'Mevcut kanallardaki diminishing returns sinyalleri var mı? Diversifikasyon zamanı geldi mi?'],
                    ['Sezonsal bütçe artırımı için hangi sinyaller bekleniyor?', 'Google Trends, site trafiği, sepet verileri — erken sezon sinyali yakaladık mı?'],
                  ].map(([q, detail], i) => (
                    <li key={i} className="flex gap-3">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">?</span>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{q}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{detail}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 5 — Rekabet & Pazar */}
            <div className="rounded-xl border border-red-100 overflow-hidden">
              <div className="bg-red-50 px-5 py-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-red-600 text-white flex items-center justify-center text-xs font-black">05</span>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Rekabet &amp; Pazar Sorguları</h3>
                  <p className="text-[10px] text-gray-500 mt-0.5">Çeyreklik strateji değerlendirmelerinde</p>
                </div>
              </div>
              <div className="p-5">
                <ul className="space-y-3">
                  {[
                    ['Rakipler hangi kanallarda agresifleşti?', 'Auction Insights / Meta Ad Library verilerine göre Sportive, SPX, Intersport son dönemde ne yapıyor?'],
                    ['Rakip CPC artışları bizim maliyetlerimizi etkiliyor mu?', 'Impression share düşüşü var mı? Outranking share değişimi ne yönde?'],
                    ['Markaların (On, HOKA) kendi DTC kampanyaları bizimle çakışıyor mu?', 'Aynı keyword\'lerde marka ile rekabet mi ediyoruz? Kanibalizasyon riski var mı?'],
                    ['Sektörde yeni bir oyuncu veya trend belirdi mi?', 'Yeni açılan niş e-ticaret siteleri, değişen tüketici davranışları, yeni platform fırsatları?'],
                  ].map(([q, detail], i) => (
                    <li key={i} className="flex gap-3">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">?</span>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{q}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{detail}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 6 — Uzun Vadeli Büyüme */}
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <div className="bg-gray-100 px-5 py-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-gray-900 text-white flex items-center justify-center text-xs font-black">06</span>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Uzun Vadeli Büyüme &amp; Vizyon Sorguları</h3>
                  <p className="text-[10px] text-gray-500 mt-0.5">Yıllık / yarı yıllık stratejik planlama</p>
                </div>
              </div>
              <div className="p-5">
                <ul className="space-y-3">
                  {[
                    ['1st-party data stratejimiz ne aşamada?', 'E-posta listesi büyüme hızı, SMS opt-in oranları, cookie-less dünyaya hazırlık seviyesi nedir?'],
                    ['Marka bilinirliği ölçümü (brand lift study) yaptık mı?', 'Aided / unaided awareness metrikleri nerede? Branding yatırımlarının geri dönüşü nasıl ölçülüyor?'],
                    ['Organik trafik payımız artıyor mu, yoksa paid\'e bağımlılık mı büyüyor?', 'SEO ve içerik yatırımları paid bütçe baskısını hafifletiyor mu?'],
                    ['Customer Lifetime Value (CLV) trendi nasıl?', '6 ay önce kazanılan müşterilerin tekrar satın alma oranı nedir? CLV/CAC oranı sağlıklı mı?'],
                    ['Yılsonu hedeflerine ulaşmak için kalan dönemde ne yapılmalı?', 'Gap analizi — hedefe ne kadar uzağız, hangi kaldıraçları kullanabiliriz?'],
                  ].map(([q, detail], i) => (
                    <li key={i} className="flex gap-3">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">?</span>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{q}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{detail}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ───────── Ajans Çalışma Yapısı ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Ajans Çalışma Yapısı</h2>
          <p className="text-xs text-gray-400 mt-1">Şeffaf, ölçülebilir ve sistematik bir operasyon modeli — her gün, her hafta, her ay</p>
        </div>
        <div className="p-6">

          {/* Üst Özet Kartları */}
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="rounded-xl bg-blue-50 border border-blue-100 p-4 text-center">
              <p className="text-2xl font-black text-blue-700">Kanban</p>
              <p className="text-[10px] text-blue-500 mt-1 font-medium">Task Board üzerinden tüm işler takip edilir</p>
            </div>
            <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4 text-center">
              <p className="text-2xl font-black text-emerald-700">Checklist</p>
              <p className="text-[10px] text-emerald-500 mt-1 font-medium">Her güne kontrol listesi ile başlanır</p>
            </div>
            <div className="rounded-xl bg-amber-50 border border-amber-100 p-4 text-center">
              <p className="text-2xl font-black text-amber-700">Timesheet</p>
              <p className="text-[10px] text-amber-500 mt-1 font-medium">Kimin hangi markaya ne kadar vakit ayırdığı yazılı</p>
            </div>
            <div className="rounded-xl bg-violet-50 border border-violet-100 p-4 text-center">
              <p className="text-2xl font-black text-violet-700">Review</p>
              <p className="text-[10px] text-violet-500 mt-1 font-medium">Haftalık geliştirme önerileri içeride sorgulanır</p>
            </div>
          </div>

          {/* Günlük / Haftalık / Aylık Döngü */}
          <div className="space-y-6">

            {/* Günlük */}
            <div className="rounded-xl border border-blue-200 overflow-hidden">
              <div className="bg-blue-600 text-white px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-xs font-black">D</span>
                  <h3 className="text-sm font-bold">Günlük Döngü — Daily Ops</h3>
                </div>
                <span className="text-[10px] bg-white/20 px-2.5 py-1 rounded-full font-medium">Her iş günü</span>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3">Sabah Checklist (09:00)</p>
                    <ul className="space-y-2">
                      {[
                        'Kampanya durumları kontrol (active / paused / error)',
                        'Bütçe pacing kontrol — günlük harcama hedefte mi?',
                        'Anomali taraması — CPC, CTR, CVR\'da ani sapma var mı?',
                        'Disapproved reklam kontrolü',
                        'Rakip aktivite hızlı tarama (Auction Insights)',
                        'Stok / ürün feed hatası kontrolü (Shopping / DPA)',
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="mt-1 w-4 h-4 rounded bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <span className="w-1.5 h-1.5 rounded-sm bg-blue-600"></span>
                          </span>
                          <span className="text-xs text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3">Akşam Kapanış (17:30)</p>
                    <ul className="space-y-2">
                      {[
                        'Günlük harcama ve dönüşüm özeti log\'a işlenir',
                        'Kritik anomali varsa marka yöneticisine bildirim',
                        'Ertesi gün için task board güncelleme',
                        'A/B test sonuçları kontrol (yeterli data birikti mi?)',
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="mt-1 w-4 h-4 rounded bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <span className="w-1.5 h-1.5 rounded-sm bg-blue-600"></span>
                          </span>
                          <span className="text-xs text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Haftalık */}
            <div className="rounded-xl border border-emerald-200 overflow-hidden">
              <div className="bg-emerald-600 text-white px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-xs font-black">W</span>
                  <h3 className="text-sm font-bold">Haftalık Döngü — Weekly Sprint</h3>
                </div>
                <span className="text-[10px] bg-white/20 px-2.5 py-1 rounded-full font-medium">Her Pazartesi + Cuma</span>
              </div>
              <div className="p-5">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Gün</th>
                        <th className="text-left py-2 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Aktivite</th>
                        <th className="text-left py-2 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Çıktı</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700">
                      {[
                        ['Pazartesi', 'Sprint planlama — haftalık hedefler, task atama, önceliklendirme', 'Kanban board güncellenir, sorumlular netleşir'],
                        ['Salı-Perşembe', 'Geliştirme çalışmaları — yeni kampanya kurulumu, A/B test, bid optimizasyon, kreatif yenileme', 'Her task board\'da ilerleme güncellenir'],
                        ['Çarşamba', 'İç sorgulama toplantısı — her marka için geliştirme önerileri masaya yatırılır', 'Onaylanan öneriler sprint\'e eklenir'],
                        ['Cuma', 'Haftalık performans review — veri analizi, sapma tespiti, aksiyon listesi', 'Haftalık mini rapor + sonraki hafta aksiyonları'],
                      ].map(([gun, aktivite, cikti], i) => (
                        <tr key={i} className={`border-b border-gray-100 ${i % 2 === 1 ? 'bg-gray-50/30' : ''}`}>
                          <td className="py-3 px-3 font-bold text-gray-900 whitespace-nowrap">{gun}</td>
                          <td className="py-3 px-3 text-xs text-gray-600">{aktivite}</td>
                          <td className="py-3 px-3 text-xs text-gray-500">{cikti}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Aylık */}
            <div className="rounded-xl border border-amber-200 overflow-hidden">
              <div className="bg-amber-600 text-white px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-xs font-black">M</span>
                  <h3 className="text-sm font-bold">Aylık Döngü — Monthly Strategy</h3>
                </div>
                <span className="text-[10px] bg-white/20 px-2.5 py-1 rounded-full font-medium">Her ayın ilk haftası</span>
              </div>
              <div className="p-5">
                <ul className="space-y-3">
                  {[
                    ['Aylık Performans Raporu', 'Detaylı veri analizi, TL ve USD bazında karşılaştırma, önceki ay ve geçen yıl aynı ay benchmark\'ı'],
                    ['Bütçe Review', 'Planlanan vs gerçekleşen harcama, platform bazında sapma analizi, sonraki ay bütçe önerisi'],
                    ['Strateji Toplantısı (Marka ile)', 'Performans sunumu, öneriler, marka tarafı feedback, sonraki ay hedefleri ve aksiyon planı'],
                    ['Geliştirme Backlog Güncelleme', 'Test sonuçlarından çıkarımlar, yeni fırsatlar, teknik iyileştirmeler — öncelik sıralaması'],
                    ['Timesheet Özeti', 'Ekibin markaya ayırdığı toplam saat, görev dağılımı, verimlilik değerlendirmesi'],
                  ].map(([title, desc], i) => (
                    <li key={i} className="flex gap-3">
                      <span className="mt-0.5 w-6 h-6 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i + 1}</span>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Kanban Board Örneği */}
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <div className="bg-gray-900 text-white px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-xs font-black">KB</span>
                  <h3 className="text-sm font-bold">Kanban Board — Task Akışı</h3>
                </div>
                <span className="text-[10px] bg-white/20 px-2.5 py-1 rounded-full font-medium">Canlı takip</span>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-4 gap-4">
                  {[
                    { title: 'Backlog', color: 'bg-gray-100 border-gray-200', titleColor: 'text-gray-600', items: ['Pinterest pilot kampanya kurulumu', 'Yeni landing page A/B testi', 'Q2 kreatif brief hazırlığı'] },
                    { title: 'In Progress', color: 'bg-blue-50 border-blue-200', titleColor: 'text-blue-700', items: ['Google PMAX asset güncelleme', 'Meta DPA feed optimizasyonu', 'RTB House segment kurulumu'] },
                    { title: 'Review', color: 'bg-amber-50 border-amber-200', titleColor: 'text-amber-700', items: ['Haftalık performans raporu', 'Yeni keyword seti onayı', 'Branding video kreatif review'] },
                    { title: 'Done', color: 'bg-emerald-50 border-emerald-200', titleColor: 'text-emerald-700', items: ['Black Friday kampanya kurulumu', 'Sepet terk e-posta akışı', 'Search conquest kampanyaları'] },
                  ].map((col, i) => (
                    <div key={i} className={`rounded-lg border ${col.color} p-3`}>
                      <p className={`text-[10px] font-bold uppercase tracking-wider mb-3 ${col.titleColor}`}>{col.title}</p>
                      <div className="space-y-2">
                        {col.items.map((item, j) => (
                          <div key={j} className="bg-white rounded-md border border-gray-200 px-3 py-2 shadow-sm">
                            <p className="text-[11px] text-gray-700">{item}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Ekip Zaman Dağılımı */}
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <div className="bg-gray-100 px-5 py-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-gray-900 text-white flex items-center justify-center text-xs font-black">T</span>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Haftalık Timesheet — Zaman Dağılımı</h3>
                  <p className="text-[10px] text-gray-500 mt-0.5">Kimin hangi markaya ne kadar vakit ayırdığı kayıt altında</p>
                </div>
              </div>
              <div className="p-5">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-900">
                        <th className="text-left py-2 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Görev Alanı</th>
                        <th className="text-center py-2 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Haftalık Saat</th>
                        <th className="text-center py-2 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Pay</th>
                        <th className="text-left py-2 px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Açıklama</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700">
                      {[
                        ['Günlük Kontrol & Monitoring', '5s', '%25', 'Sabah checklist, anomali tarama, bütçe pacing'],
                        ['Kampanya Optimizasyon', '6s', '%30', 'Bid ayarları, hedef kitle güncelleme, A/B test yönetimi'],
                        ['Analiz & Raporlama', '3s', '%15', 'Veri analizi, haftalık/aylık rapor hazırlığı'],
                        ['Strateji & Planlama', '2s', '%10', 'Yeni kampanya planı, test önerisi, bütçe review'],
                        ['Kreatif Koordinasyon', '2s', '%10', 'Brief hazırlama, kreatif review, asset yönetimi'],
                        ['İletişim & Toplantı', '2s', '%10', 'Marka toplantısı, iç sorgulama, slack/mail iletişimi'],
                      ].map(([alan, saat, pay, aciklama], i) => (
                        <tr key={i} className={`border-b border-gray-100 ${i % 2 === 1 ? 'bg-gray-50/30' : ''}`}>
                          <td className="py-3 px-3 font-bold text-gray-900">{alan}</td>
                          <td className="py-3 px-3 text-center font-bold">{saat}</td>
                          <td className="py-3 px-3 text-center">
                            <span className="inline-block px-2 py-0.5 bg-gray-900 text-white text-[10px] font-bold rounded-full">{pay}</span>
                          </td>
                          <td className="py-3 px-3 text-xs text-gray-500">{aciklama}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-900 text-white">
                        <td className="py-3 px-3 font-bold rounded-bl-lg">TOPLAM</td>
                        <td className="py-3 px-3 text-center font-black text-lg">20s</td>
                        <td className="py-3 px-3 text-center font-black">%100</td>
                        <td className="py-3 px-3 text-xs text-neutral-400 rounded-br-lg">Haftalık marka bazlı ayrılmış zaman</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ───────── 2026 Yıllık Projeksiyon ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">2026 Yıllık Projeksiyon — Estimation</h2>
          <p className="text-xs text-gray-400 mt-1">Session, Conversion Rate ve AOV bazlı 12 aylık gelir projeksiyonu</p>
        </div>
        <div className="p-6">

          {/* Üst Özet Kartları */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Session Aralığı', value: '50K → 400K', color: 'blue', sub: 'Eylül-Kasım pik, Aralık hafif düşüş' },
              { label: 'CR Aralığı', value: '%0.80 → %0.34', color: 'emerald', sub: 'Trafik arttıkça doğal düşüş' },
              { label: 'AOV Aralığı', value: '₺7,000 → ₺9,500', color: 'amber', sub: 'Sepet değeri kademeli artış' },
              { label: 'Tahmini Yıllık Gelir', value: (() => {
                const d = [
                  { s: 50000, cr: 0.0080, aov: 7000 },
                  { s: 60000, cr: 0.0075, aov: 7200 },
                  { s: 75000, cr: 0.0072, aov: 7400 },
                  { s: 90000, cr: 0.0068, aov: 7500 },
                  { s: 110000, cr: 0.0065, aov: 7700 },
                  { s: 130000, cr: 0.0062, aov: 7900 },
                  { s: 150000, cr: 0.0058, aov: 8100 },
                  { s: 180000, cr: 0.0054, aov: 8300 },
                  { s: 280000, cr: 0.0046, aov: 8500 },
                  { s: 320000, cr: 0.0042, aov: 8800 },
                  { s: 400000, cr: 0.0036, aov: 9200 },
                  { s: 350000, cr: 0.0034, aov: 9500 },
                ];
                const total = d.reduce((sum, m) => sum + Math.round(m.s * m.cr) * m.aov, 0);
                return `₺${(total / 1000000).toFixed(0)}M`;
              })(), color: 'violet', sub: 'Kümülatif ciro hedefi' },
            ].map((card, i) => (
              <div key={i} className={`rounded-xl bg-${card.color}-50 border border-${card.color}-100 p-4 text-center`}>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{card.label}</p>
                <p className={`text-xl font-black text-${card.color}-700 mt-1`}>{card.value}</p>
                <p className={`text-[10px] text-${card.color}-500 mt-1`}>{card.sub}</p>
              </div>
            ))}
          </div>

          {/* Projeksiyon Tablosu */}
          {(() => {
            const months = [
              { name: 'Ocak', sessions: 50000, cr: 0.0080, aov: 7000, quarter: 'Q1' },
              { name: 'Şubat', sessions: 60000, cr: 0.0075, aov: 7200, quarter: 'Q1' },
              { name: 'Mart', sessions: 75000, cr: 0.0072, aov: 7400, quarter: 'Q1' },
              { name: 'Nisan', sessions: 90000, cr: 0.0068, aov: 7500, quarter: 'Q2' },
              { name: 'Mayıs', sessions: 110000, cr: 0.0065, aov: 7700, quarter: 'Q2' },
              { name: 'Haziran', sessions: 130000, cr: 0.0062, aov: 7900, quarter: 'Q2' },
              { name: 'Temmuz', sessions: 150000, cr: 0.0058, aov: 8100, quarter: 'Q3' },
              { name: 'Ağustos', sessions: 180000, cr: 0.0054, aov: 8300, quarter: 'Q3' },
              { name: 'Eylül', sessions: 280000, cr: 0.0046, aov: 8500, quarter: 'Q3' },
              { name: 'Ekim', sessions: 320000, cr: 0.0042, aov: 8800, quarter: 'Q4' },
              { name: 'Kasım', sessions: 400000, cr: 0.0036, aov: 9200, quarter: 'Q4' },
              { name: 'Aralık', sessions: 350000, cr: 0.0034, aov: 9500, quarter: 'Q4' },
            ].map(m => ({
              ...m,
              transactions: Math.round(m.sessions * m.cr),
              revenue: Math.round(m.sessions * m.cr) * m.aov,
            }));

            const totalSessions = months.reduce((s, m) => s + m.sessions, 0);
            const totalTransactions = months.reduce((s, m) => s + m.transactions, 0);
            const totalRevenue = months.reduce((s, m) => s + m.revenue, 0);
            const avgCr = totalTransactions / totalSessions;
            const avgAov = totalRevenue / totalTransactions;
            const maxRevenue = Math.max(...months.map(m => m.revenue));

            const quarterColors = { Q1: 'bg-blue-50 text-blue-700', Q2: 'bg-emerald-50 text-emerald-700', Q3: 'bg-amber-50 text-amber-700', Q4: 'bg-violet-50 text-violet-700' };

            return (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="py-3 px-3 text-left text-[10px] font-bold uppercase tracking-wider rounded-tl-lg">Ay</th>
                      <th className="py-3 px-3 text-center text-[10px] font-bold uppercase tracking-wider">Q</th>
                      <th className="py-3 px-3 text-right text-[10px] font-bold uppercase tracking-wider">Session</th>
                      <th className="py-3 px-3 text-right text-[10px] font-bold uppercase tracking-wider">CR</th>
                      <th className="py-3 px-3 text-right text-[10px] font-bold uppercase tracking-wider">AOV (₺)</th>
                      <th className="py-3 px-3 text-right text-[10px] font-bold uppercase tracking-wider">İşlem</th>
                      <th className="py-3 px-3 text-right text-[10px] font-bold uppercase tracking-wider">Gelir (₺)</th>
                      <th className="py-3 px-3 text-left text-[10px] font-bold uppercase tracking-wider rounded-tr-lg" style={{ minWidth: '200px' }}>Gelir Dağılımı</th>
                    </tr>
                  </thead>
                  <tbody>
                    {months.map((m, i) => {
                      const isQuarterEnd = i === 2 || i === 5 || i === 8 || i === 11;
                      const isQuarterStart = i === 0 || i === 3 || i === 6 || i === 9;
                      const quarterMonths = months.filter(x => x.quarter === m.quarter);
                      const quarterRevenue = quarterMonths.reduce((s, x) => s + x.revenue, 0);

                      return (
                        <React.Fragment key={i}>
                          <tr className={`border-b ${isQuarterEnd ? 'border-gray-300' : 'border-gray-100'} ${i % 2 === 1 ? 'bg-gray-50/30' : ''}`}>
                            <td className="py-3 px-3 font-bold text-gray-900">{m.name}</td>
                            <td className="py-3 px-3 text-center">
                              <span className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded-full ${quarterColors[m.quarter]}`}>{m.quarter}</span>
                            </td>
                            <td className="py-3 px-3 text-right font-mono text-xs">{m.sessions.toLocaleString('tr-TR')}</td>
                            <td className="py-3 px-3 text-right font-mono text-xs font-semibold text-emerald-700">%{(m.cr * 100).toFixed(2)}</td>
                            <td className="py-3 px-3 text-right font-mono text-xs">₺{m.aov.toLocaleString('tr-TR')}</td>
                            <td className="py-3 px-3 text-right font-mono text-xs font-bold">{m.transactions.toLocaleString('tr-TR')}</td>
                            <td className="py-3 px-3 text-right font-mono text-xs font-bold text-violet-700">₺{(m.revenue / 1000000).toFixed(1)}M</td>
                            <td className="py-3 px-3">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-gradient-to-r from-violet-500 to-violet-600"
                                    style={{ width: `${(m.revenue / maxRevenue * 100).toFixed(1)}%` }}
                                  ></div>
                                </div>
                                <span className="text-[10px] text-gray-400 font-mono w-10 text-right">{(m.revenue / totalRevenue * 100).toFixed(1)}%</span>
                              </div>
                            </td>
                          </tr>
                          {isQuarterEnd && (() => {
                            const qSessions = quarterMonths.reduce((s, x) => s + x.sessions, 0);
                            const qTransactions = quarterMonths.reduce((s, x) => s + x.transactions, 0);
                            const qAvgCr = qTransactions / qSessions;
                            return (
                              <tr className="bg-gray-100 border-b-2 border-gray-300">
                                <td className="py-2 px-3 text-[10px] font-bold text-gray-500 uppercase">{m.quarter} Toplam</td>
                                <td></td>
                                <td className="py-2 px-3 text-right text-[10px] font-bold text-gray-600">{qSessions.toLocaleString('tr-TR')}</td>
                                <td className="py-2 px-3 text-right text-[10px] font-bold text-emerald-600">%{(qAvgCr * 100).toFixed(2)}</td>
                                <td></td>
                                <td className="py-2 px-3 text-right text-[10px] font-bold text-gray-600">{qTransactions.toLocaleString('tr-TR')}</td>
                                <td className="py-2 px-3 text-right text-[10px] font-black text-gray-700">₺{(quarterRevenue / 1000000).toFixed(1)}M</td>
                                <td className="py-2 px-3 text-[10px] text-gray-400">{(quarterRevenue / totalRevenue * 100).toFixed(1)}% yıllık payı</td>
                              </tr>
                            );
                          })()}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-900 text-white">
                      <td className="py-3 px-3 font-bold rounded-bl-lg">2026 TOPLAM</td>
                      <td></td>
                      <td className="py-3 px-3 text-right font-mono font-bold">{totalSessions.toLocaleString('tr-TR')}</td>
                      <td className="py-3 px-3 text-right font-mono font-bold text-emerald-300">%{(avgCr * 100).toFixed(2)}</td>
                      <td className="py-3 px-3 text-right font-mono font-bold">₺{Math.round(avgAov).toLocaleString('tr-TR')}</td>
                      <td className="py-3 px-3 text-right font-mono font-black text-lg">{totalTransactions.toLocaleString('tr-TR')}</td>
                      <td className="py-3 px-3 text-right font-mono font-black text-lg text-violet-300">₺{(totalRevenue / 1000000).toFixed(0)}M</td>
                      <td className="py-3 px-3 rounded-br-lg"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            );
          })()}

          {/* Alt Not */}
          <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-100">
            <p className="text-xs text-amber-800">
              <span className="font-bold">⚠ Disclaimer:</span> Bu projeksiyon, tanımlanan Session / CR / AOV varsayımlarına dayanmaktadır. 
              Gerçekleşme; reklam bütçesi, kampanya performansı, pazar koşulları, mevsimsellik ve stok durumuna göre değişkenlik gösterebilir. 
              Q4 hedefleri agresif olup Black Friday ve yılbaşı sezonu etkisini içermektedir.
            </p>
          </div>

        </div>
      </div>

      {/* ───────── Mart 2026 Medya Planı — Bütçe & Tahmini Performans ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Mart 2026 Medya Planı</h2>
            <p className="text-xs text-gray-400 mt-1">Google Ads (Search + PMAX + Shopping + YouTube + DG) + Meta Ads (Sales + Reach + Traffic + Video)</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Toplam Bütçe</p>
              <p className="text-2xl font-black text-gray-900">₺400.000</p>
            </div>
            <div className="h-10 w-px bg-gray-200"></div>
            <div className="text-right">
              <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Performance</p>
              <p className="text-xl font-black text-emerald-700">₺350K</p>
              <p className="text-[10px] text-gray-400">%87,5</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">Branding</p>
              <p className="text-xl font-black text-blue-700">₺50K</p>
              <p className="text-[10px] text-gray-400">%12,5</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-8">

          {/* Campaign Plan Table */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wider">Campaign Plan — High5 Mart 2026</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b-2 border-gray-900 bg-gray-50">
                    <th className="text-left py-3 px-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Platform</th>
                    <th className="text-left py-3 px-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Campaign Type</th>
                    <th className="text-right py-3 px-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Impressions</th>
                    <th className="text-right py-3 px-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Reach</th>
                    <th className="text-right py-3 px-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Clicks</th>
                    <th className="text-right py-3 px-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">CPC (₺)</th>
                    <th className="text-right py-3 px-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">CPM (₺)</th>
                    <th className="text-right py-3 px-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Budget</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  {/* Google Ads */}
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-3 font-medium text-gray-900" rowSpan="6">
                      <div className="flex items-center gap-2">
                        <img src="/google_ads-512.webp" alt="Google" className="w-5 h-5 object-contain" />
                        Google Ads
                      </div>
                    </td>
                    <td className="py-3 px-3">Search</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-mono">24.800</td>
                    <td className="py-3 px-3 text-right font-mono">₺5,0</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-bold">₺124.000</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-3">Performance Max</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-mono">22.756</td>
                    <td className="py-3 px-3 text-right font-mono">₺4,5</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-bold">₺102.400</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-3">Shopping</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-mono">16.800</td>
                    <td className="py-3 px-3 text-right font-mono">₺3,0</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-bold">₺50.400</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-3">YouTube Video</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-mono">133K views</td>
                    <td className="py-3 px-3 text-right font-mono">₺0,12 CPV</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-bold">₺16.000</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-3">Demand Gen</td>
                    <td className="py-3 px-3 text-right font-mono">360.000</td>
                    <td className="py-3 px-3 text-right font-mono">252.000</td>
                    <td className="py-3 px-3 text-right font-mono">1.029</td>
                    <td className="py-3 px-3 text-right font-mono">₺7,0</td>
                    <td className="py-3 px-3 text-right font-mono">₺20,0</td>
                    <td className="py-3 px-3 text-right font-bold">₺7.200</td>
                  </tr>
                  <tr className="border-b-2 border-gray-300 bg-gray-50">
                    <td className="py-3 px-3 font-bold text-gray-900">Google Toplam</td>
                    <td className="py-3 px-3 text-right font-mono font-medium">360.000</td>
                    <td className="py-3 px-3 text-right font-mono font-medium">252.000</td>
                    <td className="py-3 px-3 text-right font-mono font-medium">65.385</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-black text-gray-900">₺300.000</td>
                  </tr>
                  {/* Meta Ads */}
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-3 font-medium text-gray-900" rowSpan="5">
                      <div className="flex items-center gap-2">
                        <img src="/6033716.png" alt="Meta" className="w-5 h-5 object-contain" />
                        Meta Ads
                      </div>
                    </td>
                    <td className="py-3 px-3">Sales (Advantage+ & Catalog)</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-mono">16.905</td>
                    <td className="py-3 px-3 text-right font-mono">₺4,2</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-bold">₺71.200</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-3">Reach & Frequency</td>
                    <td className="py-3 px-3 text-right font-mono">846.154</td>
                    <td className="py-3 px-3 text-right font-mono">592.308</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-mono">₺13,0</td>
                    <td className="py-3 px-3 text-right font-bold">₺11.200</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-3">Traffic</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-mono">4.480</td>
                    <td className="py-3 px-3 text-right font-mono">₺2,5</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-bold">₺11.200</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-3">Video Views</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-mono">87.500 views</td>
                    <td className="py-3 px-3 text-right font-mono">₺0,08 CPV</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-bold">₺6.400</td>
                  </tr>
                  <tr className="border-b-2 border-gray-300 bg-gray-50">
                    <td className="py-3 px-3 font-bold text-gray-900">Meta Toplam</td>
                    <td className="py-3 px-3 text-right font-mono font-medium">846.154</td>
                    <td className="py-3 px-3 text-right font-mono font-medium">592.308</td>
                    <td className="py-3 px-3 text-right font-mono font-medium">21.385</td>
                    <td className="py-3 px-3 text-right font-mono font-medium">₺3,8</td>
                    <td className="py-3 px-3 text-right text-gray-500">—</td>
                    <td className="py-3 px-3 text-right font-black text-gray-900">₺100.000</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr className="bg-gray-900 text-white">
                    <td className="py-3 px-3 font-bold rounded-bl-lg" colSpan="2">TOPLAM</td>
                    <td className="py-3 px-3 text-right font-mono font-bold">1.206.154</td>
                    <td className="py-3 px-3 text-right font-mono font-bold">844.308</td>
                    <td className="py-3 px-3 text-right font-mono font-bold">86.770</td>
                    <td className="py-3 px-3 text-right font-mono font-bold">—</td>
                    <td className="py-3 px-3 text-right font-mono font-bold">—</td>
                    <td className="py-3 px-3 text-right font-black text-xl rounded-br-lg">₺400.000</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Spend by Brand */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wider">Spend by Brand</h3>
            <p className="text-xs text-gray-400 mb-4">Google Ads & Meta Ads · Brand matched from campaign names · ON %55 · ASICS %25 · Diğer markalar eşit dağılım</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b-2 border-gray-900 bg-gray-50">
                    <th className="text-left py-3 px-4 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Brand</th>
                    <th className="text-right py-3 px-4 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Google Ads</th>
                    <th className="text-right py-3 px-4 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Meta Ads</th>
                    <th className="text-right py-3 px-4 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="text-right py-3 px-4 text-[10px] font-bold text-gray-500 uppercase tracking-wider">%</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-4 font-bold text-gray-900">ON</td>
                    <td className="py-3 px-4 text-right font-mono">₺165.000</td>
                    <td className="py-3 px-4 text-right font-mono">₺55.000</td>
                    <td className="py-3 px-4 text-right font-bold">₺220.000</td>
                    <td className="py-3 px-4 text-right font-black text-gray-900">55,0%</td>
                  </tr>
                  <tr className="border-b border-gray-200 bg-gray-50/50">
                    <td className="py-3 px-4 font-bold text-gray-900">ASICS</td>
                    <td className="py-3 px-4 text-right font-mono">₺75.000</td>
                    <td className="py-3 px-4 text-right font-mono">₺25.000</td>
                    <td className="py-3 px-4 text-right font-bold">₺100.000</td>
                    <td className="py-3 px-4 text-right font-black text-gray-900">25,0%</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-4 font-medium text-gray-900">HOKA</td>
                    <td className="py-3 px-4 text-right font-mono">₺12.000</td>
                    <td className="py-3 px-4 text-right font-mono">₺4.000</td>
                    <td className="py-3 px-4 text-right font-bold">₺16.000</td>
                    <td className="py-3 px-4 text-right font-black text-gray-900">4,0%</td>
                  </tr>
                  <tr className="border-b border-gray-200 bg-gray-50/50">
                    <td className="py-3 px-4 font-medium text-gray-900">BROOKS</td>
                    <td className="py-3 px-4 text-right font-mono">₺12.000</td>
                    <td className="py-3 px-4 text-right font-mono">₺4.000</td>
                    <td className="py-3 px-4 text-right font-bold">₺16.000</td>
                    <td className="py-3 px-4 text-right font-black text-gray-900">4,0%</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-4 font-medium text-gray-900">SALOMON</td>
                    <td className="py-3 px-4 text-right font-mono">₺12.000</td>
                    <td className="py-3 px-4 text-right font-mono">₺4.000</td>
                    <td className="py-3 px-4 text-right font-bold">₺16.000</td>
                    <td className="py-3 px-4 text-right font-black text-gray-900">4,0%</td>
                  </tr>
                  <tr className="border-b border-gray-200 bg-gray-50/50">
                    <td className="py-3 px-4 font-medium text-gray-900">SKECHERS</td>
                    <td className="py-3 px-4 text-right font-mono">₺12.000</td>
                    <td className="py-3 px-4 text-right font-mono">₺4.000</td>
                    <td className="py-3 px-4 text-right font-bold">₺16.000</td>
                    <td className="py-3 px-4 text-right font-black text-gray-900">4,0%</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-4 font-medium text-gray-900">SAUCONY</td>
                    <td className="py-3 px-4 text-right font-mono">₺12.000</td>
                    <td className="py-3 px-4 text-right font-mono">₺4.000</td>
                    <td className="py-3 px-4 text-right font-bold">₺16.000</td>
                    <td className="py-3 px-4 text-right font-black text-gray-900">4,0%</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr className="bg-gray-900 text-white">
                    <td className="py-3 px-4 font-bold rounded-bl-lg">TOPLAM</td>
                    <td className="py-3 px-4 text-right font-mono font-bold">₺300.000</td>
                    <td className="py-3 px-4 text-right font-mono font-bold">₺100.000</td>
                    <td className="py-3 px-4 text-right font-black text-xl rounded-br-lg">₺400.000</td>
                    <td className="py-3 px-4 text-right font-black">100%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Hedeflenen Ciro ve Trafik — Ay sonu tahmini */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wider">Hedeflenen Ciro ve Trafik</h3>
            <p className="text-xs text-gray-400 mb-4">GA verilerine dayalı ay sonu projeksiyonu · AOV ve CR trendi dikkate alınır</p>
            {!brandId ? (
              <p className="text-sm text-amber-600 py-4">Sol menüden High5 markasını seçerek verileri görüntüleyebilirsiniz.</p>
            ) : monthly.isLoading ? (
              <div className="flex justify-center py-8"><div className="animate-pulse text-gray-400 text-sm">Yükleniyor…</div></div>
            ) : (
              <HedeflenenCirTrafikTable
                gaMonthlySummary={monthly.gaMonthlySummary}
                allCostsByMonth={monthly.allCostsByMonth}
                targetCost={400000}
                targetCr={0.16}
                sessionsBoost={1.15}
                maxSessions={67500}
                maxRevenue={1500000}
              />
            )}
          </div>

          {/* Disclaimer */}
          <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
            <p className="text-xs text-amber-800">
              <span className="font-bold">Tahmini Performans Projeksiyonları:</span> Bu plan, Türkiye dijital reklam pazarındaki güncel benchmark'lara dayalı tahmini projeksiyonlarla hazırlanmıştır. 
              Gerçek sonuçlar; açık artırma dinamikleri, hedef kitle davranışı, mevsimsellik, kreatif performansı ve pazar koşullarına bağlı olarak değişiklik gösterebilir. 
              Store odaklı kampanyalar e-ticaret dönüşümü ve ürün satışı hedeflidir.
            </p>
          </div>

        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          CRM & LIFECYCLE MARKETING STRATEJİSİ
          ══════════════════════════════════════════════════════════════════════ */}

      {/* ───────── CRM Section Header ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-700 via-emerald-600 to-teal-600 p-6 text-white">
          <div className="flex items-center gap-4">
            <div className="bg-white/15 rounded-xl p-3">
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold tracking-tight">CRM & Lifecycle Marketing Stratejisi</h2>
              <p className="text-sm text-emerald-100 mt-1">Müşteri verisi yönetimi · Segmentasyon · İletişim akışları · Kişiselleştirme</p>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Segment Sayısı', value: '14+' },
              { label: 'Otomasyon Akışı', value: '23' },
              { label: 'Temas Noktası', value: '47' },
              { label: 'Hedef Retention', value: '%35+' },
            ].map(s => (
              <div key={s.label} className="bg-white/10 rounded-lg px-4 py-3 border border-white/20">
                <p className="text-xl font-black">{s.value}</p>
                <p className="text-[11px] text-emerald-200 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ───────── 1. CRM Veri Stratejisi & Unified Customer Profile ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">1. CRM Veri Stratejisi & Unified Customer Profile</h2>
          <p className="text-xs text-gray-500 mt-1">Müşteri verisini tek bir profilde birleştirme ve zenginleştirme yol haritası</p>
        </div>
        <div className="p-6 space-y-6">

          <p className="text-sm text-gray-700 leading-relaxed">
            CRM'in temel amacı <strong>her müşteri için 360° bir profil</strong> oluşturmak ve bu profili gerçek zamanlı olarak güncellemektir.
            E-ticaret, mağaza, sosyal medya ve reklam verilerinin birleştirilmesi ile müşteriye <strong>doğru zamanda, doğru kanaldan, doğru mesajla</strong> ulaşılır.
          </p>

          {/* Veri Katmanları */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider">Veri Katmanları & Toplama Noktaları</h3>
            <div className="overflow-x-auto rounded-xl border">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                    <th className="py-3 px-4 text-left font-semibold">Veri Katmanı</th>
                    <th className="py-3 px-4 text-left font-semibold">Veri Noktaları</th>
                    <th className="py-3 px-4 text-left font-semibold">Kaynak</th>
                    <th className="py-3 px-4 text-left font-semibold">Güncelleme</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { layer: 'Kimlik & Demografik', data: 'Ad, soyad, e-posta, telefon, doğum tarihi, cinsiyet, şehir, ilçe', source: 'Kayıt formu, Checkout, Mağaza POS', freq: 'İlk kayıt + güncelleme' },
                    { layer: 'Satın Alma Geçmişi', data: 'Sipariş tarihi, ürünler, tutar, kategori, marka, indirim kullanımı, ödeme yöntemi', source: 'E-ticaret DB, POS entegrasyonu', freq: 'Her işlem sonrası' },
                    { layer: 'Davranışsal', data: 'Sayfa görüntüleme, ürün tıklama, arama sorgusu, sepet ekleme, wishlist, session süresi', source: 'GA4, Site pixel, CDP', freq: 'Gerçek zamanlı' },
                    { layer: 'Etkileşim', data: 'E-posta açılma, tıklama, SMS yanıt, push bildirim, bildirim tercihleri', source: 'ESP (Klaviyo/Mailchimp), SMS provider', freq: 'Her etkileşim sonrası' },
                    { layer: 'Reklam & Kazanım', data: 'İlk temas kanalı, UTM parametreleri, kampanya adı, ROAS, CAC', source: 'Google Ads, Meta Ads, GA4', freq: 'Attribution window içinde' },
                    { layer: 'Sosyal & UGC', data: 'Instagram takip, yorum, etiketleme, review, NPS skoru', source: 'Social API, Review platformu', freq: 'Haftalık batch' },
                    { layer: 'Mağaza (Offline)', data: 'Mağaza ziyaret, deneme kabini, satış danışmanı notu, VIP etkinlik katılımı', source: 'POS, Staff app, RFID/Beacon', freq: 'Günlük sync' },
                  ].map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 font-bold text-gray-900 whitespace-nowrap">{r.layer}</td>
                      <td className="py-3 px-4 text-gray-600">{r.data}</td>
                      <td className="py-3 px-4 text-gray-500 whitespace-nowrap">{r.source}</td>
                      <td className="py-3 px-4 text-gray-500 whitespace-nowrap">{r.freq}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Unified Profile Card */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider">Unified Customer Profile — Örnek</h3>
            <div className="rounded-xl border bg-gradient-to-br from-gray-50 to-white p-5">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-black text-lg shrink-0">AY</div>
                <div>
                  <p className="font-bold text-gray-900">Ayşe Yılmaz</p>
                  <p className="text-xs text-gray-500">ayse.y@email.com · +90 532 *** ** 78 · İstanbul, Kadıköy</p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {['VIP Gold', 'Koşucu', 'On Sadık', 'E-posta Aktif', 'Yüksek AOV'].map(tag => (
                      <span key={tag} className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-emerald-100 text-emerald-700">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                {[
                  { label: 'Toplam Sipariş', value: '12', sub: 'Son 12 ay' },
                  { label: 'Toplam Harcama', value: '₺47.800', sub: 'LTV' },
                  { label: 'AOV', value: '₺3.983', sub: 'Ortalama sepet' },
                  { label: 'Son Satın Alma', value: '18 gün', sub: 'Recency' },
                ].map(m => (
                  <div key={m.label} className="rounded-lg border bg-white p-3">
                    <p className="text-lg font-black text-gray-900">{m.value}</p>
                    <p className="text-[10px] font-semibold text-gray-500 uppercase">{m.label}</p>
                    <p className="text-[10px] text-gray-400">{m.sub}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-lg border p-3">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Favori Markalar</p>
                  <p className="text-sm text-gray-900">On (%55), ASICS (%30), HOKA (%15)</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Favori Kategoriler</p>
                  <p className="text-sm text-gray-900">Koşu Ayakkabı, Tights, Spor Sütyeni</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">İletişim Tercihi</p>
                  <p className="text-sm text-gray-900">E-posta (haftada 2x), SMS (kampanya), Push açık</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ───────── 2. Müşteri Segmentasyonu ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">2. Müşteri Segmentasyonu</h2>
          <p className="text-xs text-gray-500 mt-1">RFM analizi, yaşam döngüsü, davranışsal ve değer bazlı segmentler</p>
        </div>
        <div className="p-6 space-y-8">

          {/* 2a. RFM Matrisi */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-2 uppercase tracking-wider">2a. RFM Segmentasyon Matrisi</h3>
            <p className="text-xs text-gray-500 mb-4">Recency (son satın alma), Frequency (sıklık), Monetary (harcama) skorlarına göre 5 ana segment</p>
            <div className="overflow-x-auto rounded-xl border">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                    <th className="py-3 px-4 text-left font-semibold">RFM Segment</th>
                    <th className="py-3 px-4 text-left font-semibold">R Skoru</th>
                    <th className="py-3 px-4 text-left font-semibold">F Skoru</th>
                    <th className="py-3 px-4 text-left font-semibold">M Skoru</th>
                    <th className="py-3 px-4 text-left font-semibold">Tanım</th>
                    <th className="py-3 px-4 text-left font-semibold">Tahmini Oran</th>
                    <th className="py-3 px-4 text-left font-semibold">Aksiyon</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { seg: 'Şampiyonlar', r: '5', f: '5', m: '5', color: 'bg-emerald-100 text-emerald-800', desc: 'Son 30 günde alışveriş, 6+ sipariş, yüksek AOV', pct: '%8', action: 'VIP program, erken erişim, özel etkinlik, kişisel danışman' },
                    { seg: 'Sadık Müşteriler', r: '4-5', f: '3-4', m: '4-5', color: 'bg-blue-100 text-blue-800', desc: 'Düzenli alışveriş, ortalamanın üstü harcama', pct: '%12', action: 'Sadakat puanı çarpanı, cross-sell, referral programı' },
                    { seg: 'Potansiyel Sadık', r: '3-4', f: '2-3', m: '3-4', color: 'bg-cyan-100 text-cyan-800', desc: 'Son 60 günde aktif, 2-3 sipariş, artan trend', pct: '%15', action: 'Üyelik upgrade teklifi, seri sipariş indirimi, özel koleksiyon tanıtımı' },
                    { seg: 'Yeni Müşteriler', r: '5', f: '1', m: '1-3', color: 'bg-violet-100 text-violet-800', desc: 'İlk veya ikinci siparişini vermiş', pct: '%20', action: 'Welcome serisi, ikinci alışveriş kuponu, onboarding rehberi' },
                    { seg: 'Uyuyan Devler', r: '2-3', f: '3-5', m: '4-5', color: 'bg-amber-100 text-amber-800', desc: 'Eskiden çok alışveriş yapan ama 60-120 gündür pasif', pct: '%10', action: 'Win-back kampanya, "seni özledik" serisi, özel indirim' },
                    { seg: 'Risk Altında', r: '2', f: '2-3', m: '2-3', color: 'bg-orange-100 text-orange-800', desc: '90-120 gündür alışveriş yok, eskiden orta düzey', pct: '%15', action: 'Agresif win-back, anket, kişiselleştirilmiş teklif' },
                    { seg: 'Kaybedilen', r: '1', f: '1-2', m: '1-3', color: 'bg-red-100 text-red-800', desc: '120+ gündür hiç etkileşim yok', pct: '%12', action: 'Son şans e-postası, büyük indirim, re-permission kampanya' },
                    { seg: 'Düşük Değer', r: '1-2', f: '1', m: '1', color: 'bg-gray-100 text-gray-600', desc: 'Tek seferlik düşük tutarlı alışveriş', pct: '%8', action: 'Otomatik nurture, düşük maliyetli kanallar (push/SMS)' },
                  ].map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4"><span className={`px-2.5 py-1 rounded-full text-xs font-bold ${r.color}`}>{r.seg}</span></td>
                      <td className="py-3 px-4 font-mono font-bold text-center">{r.r}</td>
                      <td className="py-3 px-4 font-mono font-bold text-center">{r.f}</td>
                      <td className="py-3 px-4 font-mono font-bold text-center">{r.m}</td>
                      <td className="py-3 px-4 text-gray-600">{r.desc}</td>
                      <td className="py-3 px-4 font-bold text-center">{r.pct}</td>
                      <td className="py-3 px-4 text-gray-600">{r.action}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 2b. Lifecycle Segmentleri */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-2 uppercase tracking-wider">2b. Yaşam Döngüsü (Lifecycle) Segmentleri</h3>
            <p className="text-xs text-gray-500 mb-4">Her müşterinin markadaki yolculuğundaki mevcut aşaması</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { name: 'Aday (Prospect)', icon: '', color: 'border-l-violet-500 bg-violet-50', desc: 'Henüz satın alma yapmamış ama siteye kayıt olmuş, newsletter abone, sepete ürün eklemiş', kpi: 'İlk satın alma dönüşümü', target: '%12-18 dönüşüm hedefi', actions: ['Welcome serisi (3 e-posta)', 'İlk alışveriş %10 kuponu', 'Popüler ürün showcase', 'Sosyal kanıt (review\'lar)'] },
                { name: 'İlk Alıcı (First-Time)', icon: '🌱', color: 'border-l-green-500 bg-green-50', desc: 'İlk siparişini vermiş, marka deneyimini değerlendiriyor', kpi: '2. sipariş dönüşümü', target: '60 gün içinde %25 repeat', actions: ['Post-purchase welcome', 'Ürün kullanım rehberi', '2. alışveriş %15 kuponu (30 gün)', 'Review isteği (14. gün)'] },
                { name: 'Gelişen (Developing)', icon: '📈', color: 'border-l-cyan-500 bg-cyan-50', desc: '2-3 sipariş vermiş, marka bağı gelişiyor', kpi: 'AOV artışı & sıklık', target: 'AOV +%20, 4+ sipariş/yıl', actions: ['Cross-sell önerileri', 'Kategori keşfi kampanyaları', 'Sadakat programına davet', 'Marka hikayesi & topluluk içeriği'] },
                { name: 'Sadık (Loyal)', icon: '💎', color: 'border-l-blue-500 bg-blue-50', desc: '4+ sipariş, düzenli alışveriş ritmi, yüksek NPS', kpi: 'Retention & LTV', target: '%80+ yıllık retention', actions: ['VIP erken erişim', 'Kişisel danışman atama', 'Özel etkinlik davetleri', 'Referral bonus programı'] },
                { name: 'VIP / Şampiyon', icon: '👑', color: 'border-l-amber-500 bg-amber-50', desc: 'En yüksek LTV, marka elçisi, topluluk lideri', kpi: 'Advocacy & CLV max', target: 'NPS 9+, CLV ₺50K+', actions: ['1:1 iletişim', 'Ürün lansmanı öncesi erişim', 'Co-creation fırsatları', 'Yıllık VIP hediyesi'] },
                { name: 'Riskli / Uyuyan', icon: '⚠️', color: 'border-l-red-500 bg-red-50', desc: '60+ gün inaktif, alışveriş ritmi bozulmuş', kpi: 'Reactivation rate', target: '%15-25 geri kazanım', actions: ['Win-back e-posta serisi', '"Seni özledik" kampanyası', 'Anket & geri bildirim', 'Agresif teklif (son şans)'] },
              ].map((seg, i) => (
                <div key={i} className={`rounded-xl border-l-4 ${seg.color} p-5`}>
                  <div className="mb-2">
                    <h4 className="font-bold text-gray-900">{seg.name}</h4>
                  </div>
                  <p className="text-xs text-gray-600 mb-3">{seg.desc}</p>
                  <div className="mb-3 rounded-lg bg-white/80 p-2.5 border border-gray-200/50">
                    <p className="text-[10px] font-bold text-gray-400 uppercase">Ana KPI: {seg.kpi}</p>
                    <p className="text-xs font-bold text-gray-900 mt-0.5">{seg.target}</p>
                  </div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Aksiyonlar</p>
                  <ul className="space-y-1">
                    {seg.actions.map((a, j) => (
                      <li key={j} className="flex items-start gap-1.5 text-xs text-gray-700">
                        <span className="text-emerald-500 mt-0.5 shrink-0">✓</span>{a}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* 2c. Davranışsal Segmentler */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-2 uppercase tracking-wider">2c. Davranışsal & İlgi Alanı Segmentleri</h3>
            <p className="text-xs text-gray-500 mb-4">Kullanıcı davranışlarına göre dinamik olarak güncellenen mikro segmentler</p>
            <div className="overflow-x-auto rounded-xl border">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                    <th className="py-3 px-4 text-left font-semibold">Segment Grubu</th>
                    <th className="py-3 px-4 text-left font-semibold">Segment Adı</th>
                    <th className="py-3 px-4 text-left font-semibold">Kriter</th>
                    <th className="py-3 px-4 text-left font-semibold">Kişiselleştirme Kullanımı</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { group: 'Spor Dalı', seg: 'Koşucu', kriter: 'Koşu ayakkabı/ekipman sayfaları 3+ görüntüleme veya koşu kategorisi satın alma', usage: 'Koşu ağırlıklı e-posta içerik, antrenman tipleri, yarış takvimleri' },
                    { group: 'Spor Dalı', seg: 'Yogi / Pilates', kriter: 'Yoga/pilates ürün sayfaları, mat/kıyafet satın alma', usage: 'Yoga akışları, mindfulness içerik, esneklik ekipmanları' },
                    { group: 'Spor Dalı', seg: 'Outdoor / Trail', kriter: 'Trail ayakkabı, hiking ekipman sayfaları, Salomon/HOKA trail ürünleri', usage: 'Parkur önerileri, doğa etkinlikleri, su geçirmez ürünler' },
                    { group: 'Spor Dalı', seg: 'Fitness / Gym', kriter: 'Cross-training, ağırlık, gym kıyafet kategorileri', usage: 'Gym rutinleri, protein/supplement partnerlik, performans kıyafet' },
                    { group: 'Marka Tercihi', seg: 'On Lover', kriter: '%50+ satın alma On markası', usage: 'On yeni sezon, limited edition, teknoloji hikayesi' },
                    { group: 'Marka Tercihi', seg: 'ASICS Fan', kriter: '%50+ satın alma ASICS', usage: 'GEL teknolojisi, retro koleksiyon, maratona hazırlık' },
                    { group: 'Marka Tercihi', seg: 'Multi-Brand', kriter: '3+ farklı markadan alışveriş', usage: 'Marka karşılaştırmaları, "size özel seçim" kürasyon' },
                    { group: 'Fiyat Hassasiyeti', seg: 'Premium Alıcı', kriter: 'AOV > ₺5.000, indirim kuponu kullanmayan', usage: 'Yeni sezon erken erişim, limited edition, premium ambalaj' },
                    { group: 'Fiyat Hassasiyeti', seg: 'Fırsat Avcısı', kriter: '%70+ siparişte kupon kullanmış, indirim sayfası ziyaretçisi', usage: 'Flash sale bildirimi, outlet, sezon sonu, kupon ağırlıklı' },
                    { group: 'Fiyat Hassasiyeti', seg: 'Sezonsal Alıcı', kriter: 'Yılda 1-2 alışveriş, genellikle aynı dönem (Ocak/Ağustos)', usage: 'Sezon öncesi hatırlatma, early-bird kampanya' },
                    { group: 'Kanal Tercihi', seg: 'Mobil-First', kriter: '%80+ mobil session', usage: 'Mobil-optimized içerik, app push, SMS' },
                    { group: 'Kanal Tercihi', seg: 'Mağaza + Online', kriter: 'Hem online hem mağazadan alışveriş', usage: 'Omnichannel teklif, mağazada dene online al, in-store pickup' },
                  ].map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 font-bold text-gray-400 text-xs uppercase whitespace-nowrap">{r.group}</td>
                      <td className="py-3 px-4 font-bold text-gray-900 whitespace-nowrap">{r.seg}</td>
                      <td className="py-3 px-4 text-gray-600">{r.kriter}</td>
                      <td className="py-3 px-4 text-gray-600">{r.usage}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ───────── 3. Satış Öncesi İletişim Akışları (Pre-Purchase) ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">3. Satış Öncesi İletişim Akışları</h2>
          <p className="text-xs text-gray-500 mt-1">Müşteri henüz satın almadan önceki tüm temas noktaları ve otomasyon senaryoları</p>
        </div>
        <div className="p-6 space-y-8">

          {/* 3a. Welcome Serisi */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-2 uppercase tracking-wider">3a. Welcome (Hoş Geldin) Serisi — 7 Gün</h3>
            <p className="text-xs text-gray-500 mb-4">Yeni kayıt olan her kullanıcıya uygulanan onboarding akışı</p>
            <div className="overflow-x-auto rounded-xl border">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-emerald-50 text-emerald-700 text-xs uppercase tracking-wider">
                    <th className="py-3 px-4 text-left font-semibold">Gün</th>
                    <th className="py-3 px-4 text-left font-semibold">Kanal</th>
                    <th className="py-3 px-4 text-left font-semibold">Konu / Mesaj</th>
                    <th className="py-3 px-4 text-left font-semibold">İçerik Detayı</th>
                    <th className="py-3 px-4 text-left font-semibold">CTA</th>
                    <th className="py-3 px-4 text-left font-semibold">Hedef</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { day: '0 (Anında)', ch: 'E-posta', subj: 'High5\'e Hoş Geldin! ', detail: 'Marka hikayesi, değer önerisi, popüler markalar showcase, %10 hoş geldin kuponu (7 gün geçerli)', cta: 'Alışverişe Başla', goal: 'Marka tanıtımı + ilk tıklama' },
                    { day: '0 (Anında)', ch: 'SMS', subj: 'Hoş geldin! %10 kuponun hazır ', detail: 'Kısa metin: Kupon kodu + site linki (max 160 karakter)', cta: 'Kuponu Kullan', goal: 'Anlık hatırlatma' },
                    { day: '1', ch: 'E-posta', subj: 'Sana En Uygun Spor Markasını Bul', detail: 'Quiz/anket: Hangi spor dalı, tercih edilen marka, bütçe aralığı → sonuçlara göre profil zenginleştirme', cta: 'Testini Çöz', goal: 'Zero-party data toplama' },
                    { day: '3', ch: 'E-posta', subj: 'En Çok Sevilen 10 Ürünümüz', detail: 'Best-seller ürünler, gerçek müşteri yorumları, puan ortalamaları, sosyal kanıt', cta: 'Ürünleri İncele', goal: 'Social proof + engagement' },
                    { day: '5', ch: 'E-posta', subj: 'Kuponun Son 2 Gün! ', detail: 'Scarcity hatırlatma, %10 kupon countdown, kişiselleştirilmiş ürün önerisi (görüntüleme bazlı)', cta: 'Kuponu Kullan', goal: 'Urgency + dönüşüm' },
                    { day: '5', ch: 'SMS', subj: 'Son 2 gün! %10 kuponun sona eriyor', detail: 'Kısa SMS: Kupon kodu hatırlatma + deep link', cta: 'Alışverişe Git', goal: 'Multi-channel urgency' },
                    { day: '7', ch: 'E-posta', subj: 'High5 Topluluğuna Katıl ', detail: 'Instagram, YouTube, Strava kulübü, koşu etkinlikleri, topluluk içeriği', cta: 'Takip Et', goal: 'Community building' },
                  ].map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 font-bold text-emerald-700 whitespace-nowrap">{r.day}</td>
                      <td className="py-3 px-4 whitespace-nowrap">{r.ch}</td>
                      <td className="py-3 px-4 font-medium text-gray-900">{r.subj}</td>
                      <td className="py-3 px-4 text-gray-600 text-xs">{r.detail}</td>
                      <td className="py-3 px-4 whitespace-nowrap"><span className="px-2 py-1 rounded bg-emerald-100 text-emerald-700 text-xs font-bold">{r.cta}</span></td>
                      <td className="py-3 px-4 text-gray-500 text-xs">{r.goal}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 3b. Browse Abandonment */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-2 uppercase tracking-wider">3b. Browse Abandonment (Göz Atıp Ayrılan)</h3>
            <p className="text-xs text-gray-500 mb-4">Ürün sayfası görüntüleyip sepete eklemeden ayrılan kullanıcılar — Tetik: 2+ ürün görüntüleme, 0 sepet ekleme, 1 saat inaktif</p>
            <div className="overflow-x-auto rounded-xl border">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-violet-50 text-violet-700 text-xs uppercase tracking-wider">
                    <th className="py-3 px-4 text-left font-semibold">Zamanlama</th>
                    <th className="py-3 px-4 text-left font-semibold">Kanal</th>
                    <th className="py-3 px-4 text-left font-semibold">İçerik</th>
                    <th className="py-3 px-4 text-left font-semibold">Kişiselleştirme</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { time: '+1 saat', ch: 'Web Push', content: '"Baktığın ürünler hâlâ seni bekliyor" — Görüntülenen ürün görseli + fiyat', pers: 'Son görüntülenen ürün adı & görseli' },
                    { time: '+4 saat', ch: 'E-posta', content: 'Görüntülenen ürünlerin listesi, benzer ürün önerileri, stok uyarısı', pers: 'Ürün geçmişi, marka tercihi, fiyat segmenti' },
                    { time: '+24 saat', ch: 'SMS', content: 'Kısa hatırlatma: "X ürünü tükenmeden yakala!" + deep link', pers: 'En çok vakit geçirilen ürün' },
                    { time: '+48 saat', ch: 'E-posta', content: 'Sosyal kanıt: Ürün yorumları, kaç kişi aldı, trend badge', pers: 'Segment bazlı social proof seçimi' },
                  ].map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 font-bold text-violet-700 whitespace-nowrap">{r.time}</td>
                      <td className="py-3 px-4 whitespace-nowrap">{r.ch}</td>
                      <td className="py-3 px-4 text-gray-700">{r.content}</td>
                      <td className="py-3 px-4 text-gray-500 text-xs">{r.pers}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 3c. Cart Abandonment */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-2 uppercase tracking-wider">3c. Cart Abandonment (Sepet Terk)</h3>
            <p className="text-xs text-gray-500 mb-4">Sepete ürün ekleyip satın almadan ayrılan kullanıcılar — En yüksek dönüşüm potansiyeli</p>
            <div className="overflow-x-auto rounded-xl border">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-orange-50 text-orange-700 text-xs uppercase tracking-wider">
                    <th className="py-3 px-4 text-left font-semibold">Zamanlama</th>
                    <th className="py-3 px-4 text-left font-semibold">Kanal</th>
                    <th className="py-3 px-4 text-left font-semibold">Konu / Mesaj</th>
                    <th className="py-3 px-4 text-left font-semibold">İçerik</th>
                    <th className="py-3 px-4 text-left font-semibold">Teşvik</th>
                    <th className="py-3 px-4 text-left font-semibold">Beklenen OR/CR</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { time: '+30 dk', ch: 'Push', subj: 'Sepetinde bir şeyler unuttuğun gibi!', content: 'Ürün görseli + fiyat, tek tıkla sepete dön butonu', incentive: 'Yok (ilk hatırlatma)', rate: 'OR %15-20' },
                    { time: '+1 saat', ch: 'E-posta', subj: 'Sepetindeki ürünler seni bekliyor ', content: 'Sepet ürünlerinin tam listesi, ürün görselleri, toplam tutar, stok uyarısı, müşteri yorumları', incentive: 'Yok (henüz)', rate: 'OR %45, CR %5-8' },
                    { time: '+4 saat', ch: 'SMS', subj: 'Sepetini tamamla! Stok azalıyor ', content: 'Kısa metin + deep link direkt sepete', incentive: 'Yok', rate: 'CR %3-5' },
                    { time: '+24 saat', ch: 'E-posta', subj: 'Son şans: Sepetindeki ürünler tükenmek üzere', content: 'Scarcity vurgusu, "sadece X adet kaldı", benzer ürün alternatifleri', incentive: '%5 indirim kodu (ilk kez terk edenler)', rate: 'OR %35, CR %8-12' },
                    { time: '+48 saat', ch: 'E-posta', subj: 'Sana özel %10 indirim — Son 24 saat', content: 'Agresif teklif, countdown timer, güven rozetleri (ücretsiz kargo, kolay iade)', incentive: '%10 indirim + ücretsiz kargo (tekrar terk edenler)', rate: 'OR %30, CR %10-15' },
                    { time: '+72 saat', ch: 'E-posta', subj: 'Bu ürünleri beğenebilirsin', content: 'Pivot: Sepet ürünü yerine benzer/alternatif öneriler, farklı fiyat noktaları', incentive: 'Kuponsuz — keşif odaklı', rate: 'OR %25, CR %2-4' },
                  ].map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 font-bold text-orange-700 whitespace-nowrap">{r.time}</td>
                      <td className="py-3 px-4 whitespace-nowrap">{r.ch}</td>
                      <td className="py-3 px-4 font-medium text-gray-900">{r.subj}</td>
                      <td className="py-3 px-4 text-gray-600 text-xs">{r.content}</td>
                      <td className="py-3 px-4 text-xs"><span className="px-2 py-1 rounded bg-orange-100 text-orange-700 font-bold">{r.incentive}</span></td>
                      <td className="py-3 px-4 font-mono text-xs font-bold">{r.rate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 3d. Other Pre-Purchase Triggers */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-2 uppercase tracking-wider">3d. Diğer Satış Öncesi Otomasyon Tetikleri</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { name: 'Fiyat Düşüşü Bildirimi', trigger: 'Wishlist\'teki veya görüntülenen ürünün fiyatı düşünce', channels: 'E-posta + Push', flow: '1) Anında bildirim "Baktığın X ürünü şimdi %20 ucuz!" 2) 24s sonra hatırlatma "İndirim son gün"', kpi: 'CR %12-18' },
                { name: 'Stok Uyarısı (Back in Stock)', trigger: 'Tükenen ürüne "haber ver" kaydı yapılınca + ürün stoğa girince', channels: 'E-posta + SMS + Push', flow: '1) Anında "Aradığın ürün stoklarda!" 2) 4s push hatırlatma 3) 24s "Az kaldı, kaçırma"', kpi: 'CR %15-25' },
                { name: 'Wishlist Hatırlatma', trigger: 'Wishlist\'e eklenen ürün 7+ gündür satın alınmadıysa', channels: 'E-posta', flow: '1) 7. gün "Wishlist\'indeki ürünlere göz at" 2) 14. gün stok/fiyat güncellemesi 3) 30. gün "Son kontrol"', kpi: 'CR %5-8' },
                { name: 'Doğum Günü / Özel Gün', trigger: 'Profildeki doğum tarihi yaklaşınca (3 gün önce)', channels: 'E-posta + SMS', flow: '1) 3 gün önce "Doğum gününe özel sürprizimiz var!" 2) Gün "Mutlu yıllar! %15 kuponun hazır" 3) +7 gün "Kuponun son 3 gün"', kpi: 'CR %8-12, AOV +%25' },
              ].map((s, i) => (
                <div key={i} className="rounded-xl border p-5 hover:shadow-md transition-shadow">
                  <h4 className="font-bold text-gray-900 mb-1">{s.name}</h4>
                  <div className="space-y-2 text-xs">
                    <div><span className="font-bold text-gray-500">Tetik:</span> <span className="text-gray-700">{s.trigger}</span></div>
                    <div><span className="font-bold text-gray-500">Kanallar:</span> <span className="text-gray-700">{s.channels}</span></div>
                    <div><span className="font-bold text-gray-500">Akış:</span> <span className="text-gray-700">{s.flow}</span></div>
                    <div><span className="font-bold text-gray-500">Beklenen:</span> <span className="font-mono font-bold text-emerald-700">{s.kpi}</span></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ───────── 4. Satış Sonrası İletişim Akışları (Post-Purchase) ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">4. Satış Sonrası İletişim Akışları</h2>
          <p className="text-xs text-gray-500 mt-1">Sipariş anından 90+ güne kadar uzanan detaylı post-purchase yolculuğu</p>
        </div>
        <div className="p-6 space-y-8">

          {/* 4a. Sipariş Sonrası İlk 7 Gün */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-2 uppercase tracking-wider">4a. Sipariş Sonrası — İlk 7 Gün (Teslimat & Onboarding)</h3>
            <div className="overflow-x-auto rounded-xl border">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-blue-50 text-blue-700 text-xs uppercase tracking-wider">
                    <th className="py-3 px-4 text-left font-semibold">Zamanlama</th>
                    <th className="py-3 px-4 text-left font-semibold">Kanal</th>
                    <th className="py-3 px-4 text-left font-semibold">Konu</th>
                    <th className="py-3 px-4 text-left font-semibold">İçerik Detayı</th>
                    <th className="py-3 px-4 text-left font-semibold">Amacı</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { time: 'Anında', ch: 'E-posta + SMS', subj: 'Sipariş Onayı', detail: 'Sipariş özeti, ürün görselleri, tahmini teslimat, kargo takip linki, destek bilgileri', goal: 'Güven & bilgilendirme' },
                    { time: '+1 gün', ch: 'E-posta', subj: 'Kargon Yola Çıktı!', detail: 'Kargo firma, takip numarası, gerçek zamanlı izleme linki, teslimat günü tahmini', goal: 'Heyecan & takip' },
                    { time: 'Teslimat günü', ch: 'SMS + Push', subj: 'Kargon teslim edildi!', detail: 'Teslim onayı, sorun varsa destek linki, ürün inceleme teşviki', goal: 'Onay + review teşvik' },
                    { time: '+1 gün (teslimat)', ch: 'E-posta', subj: 'İlk İzlenimlerin Nasıl?', detail: 'Ürüne özel kullanım ipuçları (ayakkabı: ilk koşuda dikkat edilecekler, bakım önerileri), fit guide', goal: 'Değer katma & memnuniyet' },
                    { time: '+3 gün (teslimat)', ch: 'E-posta', subj: 'Deneyimini Paylaş — Yorum Yaz', detail: 'Yıldız puanlama formu, fotoğraf yükleme, sonraki alışverişte %5 indirim (review ödülü)', goal: 'UGC & social proof' },
                    { time: '+5 gün (teslimat)', ch: 'E-posta', subj: 'Bu Ürünlerle Tamamla', detail: 'İlk siparişe dayalı cross-sell: Koşu ayakkabı aldıysa → koşu çorabı, tights. Yoga mat → blok, strap.', goal: 'Cross-sell + AOV artışı' },
                    { time: '+7 gün (teslimat)', ch: 'SMS', subj: 'Ürününden memnun musun?', detail: 'NPS sorusu (1-10), memnun değilse destek yönlendirme, memnunsa referral kodu', goal: 'NPS + detractor rescue' },
                  ].map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 font-bold text-blue-700 whitespace-nowrap">{r.time}</td>
                      <td className="py-3 px-4 whitespace-nowrap">{r.ch}</td>
                      <td className="py-3 px-4 font-medium text-gray-900">{r.subj}</td>
                      <td className="py-3 px-4 text-gray-600 text-xs">{r.detail}</td>
                      <td className="py-3 px-4 text-gray-500 text-xs">{r.goal}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 4b. 2. Hafta — 1. Ay */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-2 uppercase tracking-wider">4b. 2. Hafta — 1. Ay (Engagement & İkinci Satın Alma)</h3>
            <div className="overflow-x-auto rounded-xl border">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-indigo-50 text-indigo-700 text-xs uppercase tracking-wider">
                    <th className="py-3 px-4 text-left font-semibold">Zamanlama</th>
                    <th className="py-3 px-4 text-left font-semibold">Kanal</th>
                    <th className="py-3 px-4 text-left font-semibold">Konu</th>
                    <th className="py-3 px-4 text-left font-semibold">İçerik Detayı</th>
                    <th className="py-3 px-4 text-left font-semibold">Kişiselleştirme</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { time: '+10 gün', ch: 'E-posta', subj: 'Koşu / Antrenman Rehberin', detail: 'Satın alınan ürün kategorisine özel içerik: Koşu → "İlk 5K Planı", Yoga → "Evde 30 Dakika Yoga"', pers: 'Ürün kategorisi + spor dalı segmenti' },
                    { time: '+14 gün', ch: 'E-posta', subj: 'Sadakat Programı: High5 Club ', detail: 'Puan sistemi tanıtımı, üyelik avantajları, Gold/Platinum seviyeleri, puan kazanma yolları', pers: 'Mevcut sipariş değerine göre başlangıç seviyesi' },
                    { time: '+18 gün', ch: 'E-posta', subj: 'Topluluktan İlham Al ‍♀️', detail: 'Instagram UGC showcase, müşteri hikayeleri, High5 koşu kulübü etkinlikleri, Strava challenge', pers: 'Spor dalına göre içerik filtreleme' },
                    { time: '+21 gün', ch: 'E-posta + SMS', subj: 'İkinci Alışverişine Özel %15 ', detail: '21 gün geçti ve ikinci sipariş yok ise: %15 kupon, önerilen ürünler, "tamamlayıcı parçalar" kürasyon', pers: 'İlk siparişle uyumlu cross-sell ürünler' },
                    { time: '+28 gün', ch: 'E-posta', subj: 'Ayın En Çok Beğenilen Ürünleri', detail: 'Aylık özet: Trend ürünler, yeni sezon önizleme, editör seçimleri', pers: 'Marka tercihi + spor dalı + fiyat segmenti' },
                  ].map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 font-bold text-indigo-700 whitespace-nowrap">{r.time}</td>
                      <td className="py-3 px-4 whitespace-nowrap">{r.ch}</td>
                      <td className="py-3 px-4 font-medium text-gray-900">{r.subj}</td>
                      <td className="py-3 px-4 text-gray-600 text-xs">{r.detail}</td>
                      <td className="py-3 px-4 text-gray-500 text-xs">{r.pers}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 4c. 1. Ay — 3. Ay */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-2 uppercase tracking-wider">4c. 1. Ay — 3. Ay (Derinleştirme & Sadakat)</h3>
            <div className="overflow-x-auto rounded-xl border">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-teal-50 text-teal-700 text-xs uppercase tracking-wider">
                    <th className="py-3 px-4 text-left font-semibold">Zamanlama</th>
                    <th className="py-3 px-4 text-left font-semibold">Kanal</th>
                    <th className="py-3 px-4 text-left font-semibold">Akış Adı</th>
                    <th className="py-3 px-4 text-left font-semibold">Detay</th>
                    <th className="py-3 px-4 text-left font-semibold">Koşul</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { time: '+35 gün', ch: 'E-posta', name: 'Ürün Bakım Rehberi', detail: 'Ayakkabı yıkama, kıyafet bakım, ürün ömrünü uzatma ipuçları — "Yatırımının korumasını sağla"', cond: 'Ürün kategorisine göre içerik' },
                    { time: '+42 gün', ch: 'E-posta', name: 'Sezon Geçişi Koleksiyonu', detail: 'Yeni sezon ürünleri, hava durumu bazlı öneri ("İstanbul\'da bahar için ideal koşu kıyafetleri")', cond: 'Lokasyon + sezon + spor dalı' },
                    { time: '+50 gün', ch: 'E-posta', name: 'Replenishment Hatırlatma', detail: 'Tükenebilir ürünler: Koşu çorabı (3 ayda değişim), jel tabanlık (500 km), spor sütyeni (6 ay)', cond: 'Ürün ömür döngüsü bazlı' },
                    { time: '+60 gün', ch: 'E-posta', name: 'Yarı Yıl Özeti — Senin İçin', detail: 'Kişisel alışveriş özeti: Kaç sipariş, favori marka, toplam tasarruf, kazanılan puan, sonraki seviyeye kalan', cond: 'Tüm müşteriler (kişiselleştirilmiş)' },
                    { time: '+60 gün (inaktif)', ch: 'E-posta + SMS', name: 'Seni Özledik! ', detail: '60 gündür sipariş yok ise: Özel win-back kuponu (%15), yeni gelen ürünler, "neden bizi tercih etmelisin"', cond: 'İkinci sipariş vermediyse' },
                    { time: '+75 gün', ch: 'E-posta', name: 'VIP Upgrade Fırsatı', detail: 'Bir sonraki sadakat seviyesine ne kadar kaldığı, seviye avantajları, hedef tutara ulaşırsa unlock olacak özellikler', cond: 'Sadakat seviyesi geçişine yakın' },
                    { time: '+80 gün', ch: 'E-posta', name: 'Ayakkabı Km Takibi', detail: '"Ayakkabın ~300 km\'de. Performans düşüşü yaşanmadan yenileme zamanı yaklaşıyor" — yenileme önerisi', cond: 'Koşu ayakkabı alıcıları (tahmini km hesabı)' },
                    { time: '+90 gün', ch: 'E-posta + SMS', name: '90 Gün Değerlendirme', detail: 'Tam kişiselleştirilmiş özet + anket: Memnuniyet, iyileştirme önerileri, NPS, sonraki çeyrek planı', cond: 'Tüm müşteriler' },
                    { time: '+90 gün (inaktif)', ch: 'E-posta', name: 'Son Şans Kampanyası', detail: '90 gündür hiç sipariş yok: %20 agresif kupon, "Hesabını kaybetmemek için giriş yap", re-permission', cond: '90 gün inaktif müşteriler' },
                  ].map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 font-bold text-teal-700 whitespace-nowrap">{r.time}</td>
                      <td className="py-3 px-4 whitespace-nowrap">{r.ch}</td>
                      <td className="py-3 px-4 font-medium text-gray-900 whitespace-nowrap">{r.name}</td>
                      <td className="py-3 px-4 text-gray-600 text-xs">{r.detail}</td>
                      <td className="py-3 px-4 text-gray-500 text-xs">{r.cond}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ───────── 5. E-posta & SMS Frekans Matrisi ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">5. E-posta & SMS İletişim Frekans Matrisi</h2>
          <p className="text-xs text-gray-500 mt-1">Segment × kanal bazında haftalık / aylık iletişim sıklığı ve içerik türleri</p>
        </div>
        <div className="p-6 space-y-6">

          {/* Frekans Tablosu */}
          <div className="overflow-x-auto rounded-xl border">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-900 text-white text-xs uppercase tracking-wider">
                  <th className="py-3 px-4 text-left font-semibold" rowSpan={2}>Segment</th>
                  <th className="py-3 px-4 text-center font-semibold border-l border-gray-700" colSpan={3}>E-posta</th>
                  <th className="py-3 px-4 text-center font-semibold border-l border-gray-700" colSpan={2}>SMS</th>
                  <th className="py-3 px-4 text-center font-semibold border-l border-gray-700" colSpan={2}>Push</th>
                </tr>
                <tr className="bg-gray-800 text-gray-300 text-[10px] uppercase tracking-wider">
                  <th className="py-2 px-4 text-center border-l border-gray-700">Haftalık</th>
                  <th className="py-2 px-4 text-center">İçerik Türü</th>
                  <th className="py-2 px-4 text-center">Saat</th>
                  <th className="py-2 px-4 text-center border-l border-gray-700">Aylık</th>
                  <th className="py-2 px-4 text-center">İçerik</th>
                  <th className="py-2 px-4 text-center border-l border-gray-700">Haftalık</th>
                  <th className="py-2 px-4 text-center">İçerik</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  { seg: 'VIP / Şampiyon', email: '3-4x', emailType: 'Erken erişim, VIP özel, kişisel öneri, içerik', hour: '10:00', sms: '4-6x', smsType: 'Flash sale, lansman, etkinlik', push: '5-7x', pushType: 'Stok, fiyat, sadakat puan' },
                  { seg: 'Sadık Müşteri', email: '2-3x', emailType: 'Yeni ürün, kampanya, cross-sell, içerik', hour: '10:00-12:00', sms: '3-4x', smsType: 'Kampanya, kupon, hatırlatma', push: '3-5x', pushType: 'Öneri, stok, yeni ürün' },
                  { seg: 'Potansiyel Sadık', email: '2x', emailType: 'Kampanya, ürün önerisi, sosyal kanıt', hour: '11:00', sms: '2-3x', smsType: 'Kupon, kampanya', push: '3-4x', pushType: 'Göz atılan ürün, fiyat düşüşü' },
                  { seg: 'Yeni Müşteri', email: '2-3x', emailType: 'Welcome serisi, rehber, ilk kupon', hour: '10:00-14:00', sms: '2x', smsType: 'Hoş geldin, kupon', push: '2-3x', pushType: 'Welcome push, trend ürünler' },
                  { seg: 'Risk Altında', email: '1-2x', emailType: 'Win-back, özel teklif, anket', hour: '18:00-20:00', sms: '2x', smsType: 'Son şans, büyük indirim', push: '2x', pushType: 'Geri dön mesajı, kupon' },
                  { seg: 'Kaybedilen', email: '1x (2 haftada)', emailType: 'Re-engagement, büyük indirim, yeni koleksiyon', hour: '12:00', sms: '1x', smsType: 'Agresif teklif (son şans)', push: '1x', pushType: 'Geri dön + indirim' },
                  { seg: 'Fırsat Avcısı', email: '2x', emailType: 'Sale bildirimi, outlet, fırsat ürünleri', hour: '09:00', sms: '3-4x', smsType: 'Flash sale, %XX indirim', push: '4-5x', pushType: 'İndirim, son parça, kampanya' },
                ].map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 font-bold text-gray-900 whitespace-nowrap">{r.seg}</td>
                    <td className="py-3 px-4 font-mono font-bold text-center border-l">{r.email}</td>
                    <td className="py-3 px-4 text-gray-600 text-xs">{r.emailType}</td>
                    <td className="py-3 px-4 text-gray-500 text-xs text-center">{r.hour}</td>
                    <td className="py-3 px-4 font-mono font-bold text-center border-l">{r.sms}</td>
                    <td className="py-3 px-4 text-gray-600 text-xs">{r.smsType}</td>
                    <td className="py-3 px-4 font-mono font-bold text-center border-l">{r.push}</td>
                    <td className="py-3 px-4 text-gray-600 text-xs">{r.pushType}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* İletişim Kuralları */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider">Kanal Bazlı İletişim Kuralları & Best Practice</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { channel: 'E-posta', icon: '📧', color: 'border-blue-200 bg-blue-50', rules: [
                  'Maksimum haftalık 4 e-posta (VIP hariç)',
                  'Transactional + marketing ayrı domain',
                  'Konu satırı 40-60 karakter, emoji dikkatli kullanım',
                  'Preheader mutlaka yazılacak (90 karakter)',
                  'Mobil-first tasarım (açılmaların %70\'i mobil)',
                  'Unsubscribe 1 tıkla, tercih merkezi sunulacak',
                  'A/B test: Her kampanyada konu satırı testi',
                  'Dark mode uyumlu tasarım şablonu',
                  'Gönderim saati: Hafta içi 10:00-12:00, Perşembe/Cuma en yüksek OR',
                ]},
                { channel: 'SMS', icon: '📱', color: 'border-green-200 bg-green-50', rules: [
                  'Maksimum aylık 6 SMS (yasal sınır dikkat)',
                  '160 karakter limiti, net ve öz mesaj',
                  'Mutlaka opt-in alınacak (KVKK uyumu)',
                  'Deep link ile direkt ürün/sepet sayfası',
                  'Gönderim saati: 10:00-20:00 arası (yasal)',
                  'Cuma 18:00-20:00 en yüksek dönüşüm',
                  'Personalized: İsim + son ürün referansı',
                  'Opt-out: Her mesajda "İPTAL yaz" bilgisi',
                  'Acil & urgency mesajları için SMS öncelikli',
                ]},
                { channel: 'Push Notification', icon: '🔔', color: 'border-purple-200 bg-purple-50', rules: [
                  'Maksimum günlük 2 push (session bazlı)',
                  'Rich push: Görsel + buton (iOS/Android)',
                  'Zamanlama: Kullanıcının aktif saatlerine göre',
                  'Tetik bazlı > toplu gönderim',
                  'Kısa metin: 50-80 karakter ideal',
                  'Deep link: Direkt ürün sayfasına yönlendirme',
                  'Segmente göre farklı push izni stratejisi',
                  'İlk push izni sorma: Değer önerisi ile',
                  'Quiet hours: 22:00-08:00 gönderim yapılmaz',
                ]},
              ].map((ch, i) => (
                <div key={i} className={`rounded-xl border ${ch.color} p-5`}>
                  <div className="mb-3">
                    <h4 className="font-bold text-gray-900">{ch.channel}</h4>
                  </div>
                  <ul className="space-y-1.5">
                    {ch.rules.map((rule, j) => (
                      <li key={j} className="flex items-start gap-1.5 text-xs text-gray-700">
                        <span className="text-gray-400 mt-0.5 shrink-0">•</span>{rule}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ───────── 6. Customer Journey Funnel ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">6. Customer Journey Funnel — Uçtan Uca Müşteri Yolculuğu</h2>
          <p className="text-xs text-gray-500 mt-1">Farkındalıktan savunuculuğa kadar 6 aşamalı funnel ve her aşamadaki temas noktaları</p>
        </div>
        <div className="p-6 space-y-6">

          {/* Visual Funnel */}
          <div className="space-y-2">
            {[
              { stage: 'AWARENESS', sub: 'Farkındalık', barW: '100%', color: 'bg-violet-600', pct: '100%', visitors: '~500K', channels: 'Google Ads, Meta Ads, YouTube, Instagram, SEO, Influencer', crm: 'Pixel takip, UTM etiketleme, cookie consent, newsletter popup (exit-intent)', kpi: 'Impression, Reach, CPM, Brand Search Volume', drop: '' },
              { stage: 'INTEREST', sub: 'İlgi', barW: '72%', color: 'bg-blue-600', pct: '72%', visitors: '~360K', channels: 'Site ziyaret, blog okuma, kategori gezme, ürün karşılaştırma', crm: 'Browse tracking, heatmap analizi, quiz/anket, lead magnet (spor rehberi)', kpi: 'Session, Sayfa/Session, Bounce Rate, Time on Site', drop: '28% kayıp: Bounce, irrelevant traffic' },
              { stage: 'CONSIDERATION', sub: 'Değerlendirme', barW: '28%', color: 'bg-cyan-600', pct: '28%', visitors: '~140K', channels: 'Ürün detay, size guide, review okuma, wishlist, fiyat karşılaştırma', crm: 'Browse abandon e-posta, retargeting, wishlist hatırlatma, price alert, sosyal kanıt push', kpi: 'PDP View, Add to Wishlist, Size Guide Click', drop: '44% kayıp: Fiyat hassasiyeti, beden belirsizliği' },
              { stage: 'PURCHASE', sub: 'Satın Alma', barW: '4.5%', color: 'bg-emerald-600', pct: '4,5%', visitors: '~22K', channels: 'Sepete ekle, checkout, ödeme, sipariş tamamla', crm: 'Cart abandon serisi (30dk-72s), checkout nudge, güven rozetleri, ücretsiz kargo eşiği', kpi: 'Add to Cart, CR, AOV, Revenue, Coupon Usage', drop: '23,5% kayıp: Sepet terk, ödeme sorunları' },
              { stage: 'RETENTION', sub: 'Elde Tutma', barW: '1.8%', color: 'bg-amber-600', pct: '1,8%', visitors: '~9K', channels: 'Post-purchase akış, cross-sell, replenishment, sadakat programı', crm: 'Post-purchase serisi (0-90 gün), NPS, review teşvik, 2. sipariş kuponu, VIP program', kpi: 'Repeat Rate, LTV, NPS, Churn Rate, 2nd Order %', drop: '2,7% kayıp: Tek seferlik alıcılar' },
              { stage: 'ADVOCACY', sub: 'Savunuculuk', barW: '0.6%', color: 'bg-rose-600', pct: '0,6%', visitors: '~3K', channels: 'Referral, UGC, review, sosyal paylaşım, etkinlik katılımı', crm: 'Referral programı (100 TL kazanc/referans), UGC kampanya, VIP etkinlik, co-creation', kpi: 'Referral Count, UGC Volume, NPS 9-10, Social Mention', drop: '1,2% kayıp: Pasifleşme' },
            ].map((s, i) => (
              <div key={i} className="rounded-lg border overflow-hidden">
                <div className="flex">
                  <div className={`${s.color} w-2 shrink-0`} />
                  <div className="flex-1 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-black text-sm text-gray-900">{s.stage} <span className="font-medium text-gray-400">({s.sub})</span></p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{s.channels}</p>
                      </div>
                      <div className="text-right shrink-0 ml-4">
                        <p className="text-xl font-black text-gray-900">{s.pct}</p>
                        <p className="text-[10px] text-gray-500">{s.visitors} kullanıcı</p>
                      </div>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full mb-3">
                      <div className={`h-full ${s.color} rounded-full transition-all`} style={{ width: s.barW }} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                      <div><span className="font-bold text-gray-500">CRM Aksiyonu:</span> <span className="text-gray-600">{s.crm}</span></div>
                      <div><span className="font-bold text-gray-500">KPI:</span> <span className="text-gray-600">{s.kpi}</span></div>
                      {s.drop && <div><span className="font-bold text-red-500">Kayıp:</span> <span className="text-red-400">{s.drop}</span></div>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ───────── 7. Kişiselleştirilmiş 1:1 Kullanıcı Yolculuğu ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-5 py-3 border-b flex items-center gap-3">
          <div>
            <h2 className="text-sm font-bold text-gray-900">7. Kişiselleştirilmiş 1:1 Kullanıcı Yolculuğu — Örnek Senaryo</h2>
            <p className="text-[10px] text-gray-400 mt-0.5">"Ayşe Yılmaz, 32" — Kadıköy, Yazılım Mühendisi, amatör koşucu (3x/hafta) · İlk temas: IG reklamı → high5.com.tr</p>
          </div>
        </div>
        <div className="p-4 space-y-3">

          <div className="overflow-x-auto rounded-lg border" style={{ maxHeight: 520, overflowY: 'auto' }}>
            <table className="min-w-full">
              <thead className="sticky top-0 z-10">
                <tr className="bg-gray-900 text-white text-[9px] uppercase tracking-wider">
                  <th className="py-1.5 px-2 text-left font-semibold w-[52px]">Gün</th>
                  <th className="py-1.5 px-2 text-left font-semibold w-[72px]">Aşama</th>
                  <th className="py-1.5 px-2 text-left font-semibold">Aksiyon & Sistem Tepkisi</th>
                  <th className="py-1.5 px-2 text-left font-semibold w-[70px]">Kanal</th>
                  <th className="py-1.5 px-2 text-left font-semibold">Mesaj</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  { day: '0', stage: 'Farkındalık', desc: 'IG reklam tıklama → site ziyareti → popup ile e-posta toplama → Welcome serisi tetiklendi', ch: 'Web + E-posta', msg: 'Exit-intent popup %10 kupon · Welcome mail: marka hikayesi + HOSGELDIN10', ad: false },
                  { day: '1', stage: 'İlgi', desc: 'Cloudmonster sayfasına geri döndü · Quiz tamamladı (koşu, 3x/hafta, asfalt)', ch: 'E-posta + Web', msg: '"Sana Uygun Markayı Bul" quiz · Kişiselleştirilmiş anasayfa', ad: false },
                  { day: '2-3', stage: 'Retargeting', desc: 'IG feed DPA + Google RLSA "on cloudmonster fiyat" araması', ch: 'Meta + Google', msg: 'Dynamic Product Ad · Search Ad: "High5 — Ücretsiz Kargo + %10"', ad: true },
                  { day: '3', stage: 'İlgi', desc: 'Best-seller e-postası açıldı, siteye gelmedi', ch: 'E-posta', msg: '"En Çok Sevilen 10 Koşu Ayakkabı" — Cloudmonster 1. sıra, 4.8★', ad: false },
                  { day: '4', stage: 'Değerlendirme', desc: 'Sepete ekledi (38 no) → 35dk inaktif → Cart abandon akışı: Push + E-posta + YT bumper', ch: 'Push + E-posta + Ads', msg: '"Sepetindeki Cloudmonster seni bekliyor!" · Stok uyarısı: "Son 4 adet"', ad: false },
                  { day: '5', stage: 'Satın Alma', desc: 'IG Stories retargeting → siteye döndü → SMS kupon hatırlatma → sipariş tamamlandı ₺6.291', ch: 'Meta + SMS + E-posta', msg: 'Cart abandon Stories Ad · "Kuponun son 2 gün!" · Sipariş onayı', ad: false },
                  { day: '5', stage: 'Reklam Opt.', desc: 'Meta CAPI + Google Enhanced Conversions tetiklendi, "Purchaser" audience güncellendi', ch: 'Meta + Google', msg: 'Conversion data — prospecting\'den çıkarıldı', ad: true },
                  { day: '7-8', stage: 'Teslimat', desc: 'Kargo webhook → teslim bildirimi · Ertesi gün kullanım rehberi gönderildi', ch: 'SMS + E-posta', msg: '"Kargon teslim edildi" · "İlk 50km tüyoları + bakım rehberi"', ad: false },
                  { day: '10', stage: 'Review', desc: '5★ yorum yazdı → %5 kupon ödülü otomatik oluşturuldu', ch: 'E-posta', msg: '"Deneyimini Paylaş" → "Teşekkürler! %5 kuponun: REVIEW5"', ad: false },
                  { day: '12-14', stage: 'Cross-sell', desc: 'Complementary ürün önerisi + sadakat programı daveti · Stance çorap browse tracking', ch: 'E-posta', msg: '"Bu Ürünlerle Tamamla" (çorap + tights) · "High5 Club — 629 puanın var"', ad: false },
                  { day: '15-18', stage: 'Retargeting', desc: 'IG\'da Stance çorap DPA + Google Display yeni sezon banner', ch: 'Meta + Google', msg: '"On ayakkabınla uyumlu çorap" · "Yeni Sezon On — High5\'da"', ad: true },
                  { day: '21', stage: 'Re-engage', desc: '21 gün, 2. sipariş yok → ikinci alışveriş tetikleyici', ch: 'E-posta + SMS', msg: '"Sana özel %15" — kupon stacking + Stance çorap öncelikli', ad: false },
                  { day: '24', stage: '2. Satın Alma', desc: 'Stance çorap + On şort aldı (₺3.270) → Segment: First-Time → Developing', ch: 'E-posta', msg: '"2. siparişin için teşekkürler! 500 bonus puan"', ad: false },
                  { day: '30-50', stage: 'Nurture', desc: 'Lookalike audience oluşturuldu · Bakım rehberi · Çorap replenishment hatırlatma', ch: 'Meta + E-posta', msg: 'LAL 1% · "Ayakkabı Bakım Rehberi" · "Çorap yenileme zamanı!"', ad: false },
                  { day: '60', stage: 'Özet', desc: '60 gün değerlendirme raporu gönderildi', ch: 'E-posta', msg: '"Senin 2 Ayın" — 2 sipariş, ₺9.561, 1.129 puan, Gold\'a 371 kaldı', ad: false },
                  { day: '75-82', stage: 'Upsell', desc: 'VIP upgrade teşviki · Strava 280km → yenileme önerisi · IG Cloudsurfer retargeting', ch: 'E-posta + Meta', msg: '"Gold\'a ₺2.439 kaldı!" · "Cloudmonster 300km — yenileme zamanı"', ad: false },
                  { day: '90', stage: 'NPS', desc: 'NPS anketi: 9 puan → referral kodu oluşturuldu', ch: 'E-posta', msg: '"Referral kodun: AYSE100 — Arkadaşın ₺100, sen ₺100"', ad: false },
                  { day: '95', stage: '3. Satın Alma', desc: 'On Cloudsurfer aldı (₺7.490), referral paylaştı → Segment: Loyal, Gold seviye', ch: 'E-posta', msg: '"Gold\'a Hoş Geldin!" — Ücretsiz kargo, %10 sürekli, kişisel danışman', ad: false },
                  { day: '100-120', stage: 'VIP', desc: 'Gold exclusive kampanyalar · Referral\'dan 1 arkadaş alışveriş yaptı → ₺100 kredi', ch: 'Google + E-posta', msg: 'PMAX VIP kampanya · "Elif alışveriş yaptı! ₺100 kredin tanımlandı"', ad: false },
                ].map((r, i) => (
                  <tr key={i} className={`${r.stage.includes('Satın Alma') ? 'bg-emerald-50' : ''} ${r.ad ? 'bg-blue-50/50' : 'hover:bg-gray-50'}`}>
                    <td className="py-1.5 px-2 font-mono font-bold text-[10px] text-gray-400 whitespace-nowrap align-top">G{r.day}</td>
                    <td className="py-1.5 px-2 font-semibold text-[10px] whitespace-nowrap align-top">{r.ad ? <span className="text-blue-700">{r.stage}</span> : r.stage}</td>
                    <td className="py-1.5 px-2 text-[10px] text-gray-600 leading-tight align-top">{r.desc}</td>
                    <td className="py-1.5 px-2 text-[10px] whitespace-nowrap align-top">{r.ad ? <span className="px-1 py-0.5 rounded bg-blue-100 text-blue-700 font-bold text-[9px]">{r.ch}</span> : <span className="text-gray-500">{r.ch}</span>}</td>
                    <td className="py-1.5 px-2 text-[10px] text-gray-700 font-medium leading-tight align-top">{r.msg}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-lg bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 p-3">
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">120 Gün Sonucu — Ayşe Yılmaz</p>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {[
                { label: 'Sipariş', value: '3' },
                { label: 'Harcama', value: '₺17.051' },
                { label: 'AOV', value: '₺5.684' },
                { label: 'Seviye', value: 'Gold' },
                { label: 'NPS', value: '9/10' },
                { label: 'Referral', value: '1 kişi' },
              ].map(m => (
                <div key={m.label} className="rounded border bg-white px-2 py-1.5 text-center">
                  <p className="text-sm font-black text-gray-900">{m.value}</p>
                  <p className="text-[9px] font-semibold text-gray-400 uppercase">{m.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ───────── 7b. Loyalty Program Önerisi ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-5 py-3 border-b">
          <h2 className="text-sm font-bold text-gray-900">High5 Loyalty Program — Statü & Avantajlar</h2>
          <p className="text-[10px] text-gray-400 mt-0.5">Satın alma bazlı puan kazanımı, 3 kademeli statü sistemi ve statüye özel avantajlar</p>
        </div>
        <div className="p-4 space-y-4">

          {/* Puan Kazanım Kuralları */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { label: 'Satın Alma', value: 'Her ₺1 = 1 puan', sub: 'Tüm siparişler' },
              { label: 'Yorum Yazma', value: '+50 puan', sub: 'Ürün başına 1 kez' },
              { label: 'Referral', value: '+200 puan', sub: 'Arkadaşın ilk siparişinde' },
              { label: 'Doğum Günü', value: '2x puan', sub: '7 gün boyunca geçerli' },
            ].map(r => (
              <div key={r.label} className="rounded-lg border p-2.5 text-center">
                <p className="text-[10px] font-bold text-gray-400 uppercase">{r.label}</p>
                <p className="text-sm font-black text-gray-900 mt-0.5">{r.value}</p>
                <p className="text-[9px] text-gray-400 mt-0.5">{r.sub}</p>
              </div>
            ))}
          </div>

          {/* Statü Tablosu */}
          <div className="overflow-x-auto rounded-xl border">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-900 text-white text-[9px] uppercase tracking-wider">
                  <th className="py-2 px-3 text-left font-semibold">Avantaj</th>
                  <th className="py-2 px-3 text-center font-semibold" style={{ minWidth: 120 }}>
                    <span className="inline-block px-2 py-0.5 rounded-full bg-gray-600 text-white">Member</span>
                  </th>
                  <th className="py-2 px-3 text-center font-semibold" style={{ minWidth: 120 }}>
                    <span className="inline-block px-2 py-0.5 rounded-full bg-blue-500 text-white">Pro</span>
                  </th>
                  <th className="py-2 px-3 text-center font-semibold" style={{ minWidth: 120 }}>
                    <span className="inline-block px-2 py-0.5 rounded-full bg-amber-500 text-white">Elite</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-[11px]">
                {[
                  { benefit: 'Giriş Koşulu', member: 'Otomatik (kayıt)', pro: '5.000 puan veya ₺5.000+', elite: '15.000 puan veya ₺15.000+' },
                  { benefit: 'Puan Çarpanı', member: '1x', pro: '1.5x', elite: '2x' },
                  { benefit: 'Hoş Geldin Kuponu', member: '%10', pro: '—', elite: '—' },
                  { benefit: 'Sürekli İndirim', member: '—', pro: '%5', elite: '%10' },
                  { benefit: 'Ücretsiz Kargo', member: '₺500+ siparişlerde', pro: '₺250+ siparişlerde', elite: 'Her siparişte' },
                  { benefit: 'Doğum Günü Hediyesi', member: '%10 kupon', pro: '%15 kupon + sürpriz ürün', elite: '%20 kupon + premium hediye kutusu' },
                  { benefit: 'Erken Erişim (Yeni Sezon)', member: '—', pro: '1 gün önce', elite: '3 gün önce + özel davet' },
                  { benefit: 'Özel Kampanyalar', member: 'Genel kampanyalar', pro: 'Pro\'ya özel fırsatlar', elite: 'VIP kampanyalar + flash sale' },
                  { benefit: 'İade Süresi', member: '14 gün', pro: '21 gün', elite: '30 gün' },
                  { benefit: 'Kişisel Danışman', member: '—', pro: '—', elite: 'Dedicated account manager' },
                  { benefit: 'VIP Etkinlikler', member: '—', pro: '—', elite: 'Lansman, koşu etkinlikleri, marka deneyimleri' },
                  { benefit: 'Puan → Hediye Çeki', member: '1.000 puan = ₺50', pro: '1.000 puan = ₺75', elite: '1.000 puan = ₺100' },
                ].map((r, i) => (
                  <tr key={i} className={`hover:bg-gray-50 ${i === 0 ? 'bg-gray-50 font-semibold' : ''}`}>
                    <td className="py-2 px-3 font-semibold text-gray-800 whitespace-nowrap">{r.benefit}</td>
                    <td className="py-2 px-3 text-center text-gray-600">{r.member}</td>
                    <td className="py-2 px-3 text-center text-blue-700 font-medium">{r.pro}</td>
                    <td className="py-2 px-3 text-center text-amber-700 font-medium">{r.elite}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Statü Yükseltme Akışı */}
          <div className="flex items-center justify-center gap-2 py-2">
            <div className="flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-gray-500"></div>
              <span className="text-[10px] font-bold text-gray-600">Member</span>
            </div>
            <div className="text-gray-300 text-xs">→ 5.000 puan →</div>
            <div className="flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-200 px-3 py-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
              <span className="text-[10px] font-bold text-blue-700">Pro</span>
            </div>
            <div className="text-gray-300 text-xs">→ 15.000 puan →</div>
            <div className="flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-3 py-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-amber-500"></div>
              <span className="text-[10px] font-bold text-amber-700">Elite</span>
            </div>
          </div>

          <div className="p-3 bg-gray-50 rounded-lg border">
            <p className="text-[10px] text-gray-500">
              <span className="font-bold text-gray-700">Statü Koruma:</span> Statü yıllık olarak değerlendirilir. Pro seviyeyi korumak için yıllık min. ₺3.000, Elite için min. ₺10.000 harcama gereklidir. 
              Eşik altında kalan kullanıcılar bir alt seviyeye düşer ancak biriken puanları korunur. Yeni üyeliklerde ilk yıl statü düşüşü uygulanmaz.
            </p>
          </div>

          {/* Mobile App Mockup */}
          <div>
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-3">Mobil Uygulama Görünümü — Örnek Ekran</p>
            <div className="flex justify-center">
              <div className="w-[300px] rounded-[2rem] border-[6px] border-gray-900 bg-gray-900 shadow-2xl overflow-hidden">
                {/* Phone notch */}
                <div className="bg-gray-900 flex justify-center pt-2 pb-1">
                  <div className="w-20 h-5 bg-gray-800 rounded-full"></div>
                </div>

                {/* Screen content */}
                <div className="bg-white" style={{ minHeight: 520 }}>
                  {/* Header */}
                  <div className="bg-gradient-to-br from-gray-900 to-gray-800 px-5 pt-4 pb-6 text-white relative overflow-hidden">
                    <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-white/5"></div>
                    <div className="absolute -right-2 bottom-0 w-16 h-16 rounded-full bg-white/5"></div>
                    <p className="text-[9px] text-gray-400 font-medium">HIGH5 CLUB</p>
                    <p className="text-base font-bold mt-0.5">Merhaba, Ayse</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                      <span className="text-[10px] font-semibold text-gray-300">Member</span>
                    </div>
                    <div className="mt-3 bg-white/10 rounded-xl p-3 backdrop-blur">
                      <div className="flex items-end justify-between">
                        <div>
                          <p className="text-[9px] text-gray-400">Toplam Puanin</p>
                          <p className="text-2xl font-black">1.300</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[9px] text-gray-400">Sonraki Seviye</p>
                          <p className="text-sm font-bold text-blue-400">Pro</p>
                        </div>
                      </div>
                      <div className="mt-2.5 bg-white/10 rounded-full h-2 overflow-hidden">
                        <div className="bg-gradient-to-r from-blue-400 to-blue-500 h-full rounded-full" style={{ width: '26%' }}></div>
                      </div>
                      <p className="text-[9px] text-gray-400 mt-1.5">3.700 puan daha kazanirsan <span className="text-blue-400 font-bold">Pro</span> olacaksin!</p>
                    </div>
                  </div>

                  {/* Pro Benefits Preview */}
                  <div className="px-4 pt-3 pb-2">
                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Pro Seviyede Seni Neler Bekliyor</p>
                    <div className="mt-2 space-y-1.5">
                      {[
                        { icon: '🏷️', text: '%5 surekli indirim', sub: 'Tum siparislerde' },
                        { icon: '🚚', text: '₺250+ ucretsiz kargo', sub: 'Kargo esigi yariya duser' },
                        { icon: '⭐', text: '1.5x puan carpani', sub: 'Daha hizli Elite\'e ulasin' },
                        { icon: '🎁', text: '%15 dogum gunu kuponu', sub: '+ surpriz urun hediyesi' },
                      ].map((b, i) => (
                        <div key={i} className="flex items-center gap-2.5 bg-blue-50/60 rounded-lg px-3 py-2">
                          <span className="text-sm">{b.icon}</span>
                          <div className="min-w-0">
                            <p className="text-[10px] font-bold text-gray-800">{b.text}</p>
                            <p className="text-[8px] text-gray-400">{b.sub}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Recent Activity */}
                  <div className="px-4 pt-2 pb-4">
                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Son Puan Hareketlerin</p>
                    <div className="mt-2 space-y-0">
                      {[
                        { label: 'On Cloudmonster Satin Alma', pts: '+629', date: '5 gun once', color: 'text-emerald-600' },
                        { label: 'Hosgeldin Bonus', pts: '+500', date: '12 gun once', color: 'text-emerald-600' },
                        { label: 'Urun Yorumu Yazdin', pts: '+50', date: '10 gun once', color: 'text-emerald-600' },
                        { label: 'Hediye Ceki Kullanimi', pts: '-50', date: '8 gun once', color: 'text-red-400' },
                      ].map((a, i) => (
                        <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                          <div>
                            <p className="text-[10px] font-semibold text-gray-700">{a.label}</p>
                            <p className="text-[8px] text-gray-400">{a.date}</p>
                          </div>
                          <span className={`text-[11px] font-black ${a.color}`}>{a.pts}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Bottom CTA */}
                  <div className="px-4 pb-3">
                    <div className="bg-gray-900 rounded-xl py-2.5 text-center">
                      <p className="text-[10px] font-bold text-white">Puan Kazan — Arkadassini Davet Et (+200)</p>
                    </div>
                  </div>
                </div>

                {/* Phone bottom bar */}
                <div className="bg-gray-900 flex justify-center py-2">
                  <div className="w-24 h-1 bg-gray-600 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ───────── 7c. CRM Segment Tanımları ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-5 py-3 border-b">
          <h2 className="text-sm font-bold text-gray-900">CRM Segment Tanımları & Aksiyon Planı</h2>
          <p className="text-[10px] text-gray-400 mt-0.5">Davranış bazlı dinamik segmentler, tetiklenen otomasyonlar ve hedef KPI'lar</p>
        </div>
        <div className="p-4">
          <div className="overflow-x-auto rounded-xl border">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-900 text-white text-[9px] uppercase tracking-wider">
                  <th className="py-2 px-3 text-left font-semibold w-8">#</th>
                  <th className="py-2 px-3 text-left font-semibold">Segment</th>
                  <th className="py-2 px-3 text-left font-semibold">Tanım / Kural</th>
                  <th className="py-2 px-3 text-left font-semibold">Tahmini Oran</th>
                  <th className="py-2 px-3 text-left font-semibold">Aksiyon</th>
                  <th className="py-2 px-3 text-left font-semibold">Kanal</th>
                  <th className="py-2 px-3 text-left font-semibold">Hedef</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-[10px]">
                {[
                  {
                    id: 1, name: 'Yeni Kayıt — Henüz Almadı', color: 'bg-sky-100 text-sky-800',
                    rule: 'Kayıt tarihi < 30 gün, sipariş = 0',
                    ratio: '%25-30',
                    action: 'Welcome serisi + %10 kupon + quiz + browse retargeting',
                    ch: 'E-posta, Push, Meta, Google',
                    goal: 'İlk siparişe dönüştürme (%12-18 CR)'
                  },
                  {
                    id: 2, name: 'İlk Siparişini Verdi', color: 'bg-emerald-100 text-emerald-800',
                    rule: 'Sipariş = 1, son sipariş < 30 gün',
                    ratio: '%15-20',
                    action: 'Post-purchase flow + cross-sell + review isteği + loyalty davet',
                    ch: 'E-posta, SMS',
                    goal: '2. siparişe dönüşüm (%20-28 CR, 60 gün içinde)'
                  },
                  {
                    id: 3, name: 'Aktif Sadık — 3 Ayda 3+ Sipariş', color: 'bg-emerald-200 text-emerald-900',
                    rule: 'Son 90 günde sipariş ≥ 3',
                    ratio: '%5-8',
                    action: 'VIP kampanya, erken erişim, referral teşvik, Elite upgrade push',
                    ch: 'E-posta, Push',
                    goal: 'Elite\'e yükseltme, AOV artışı (+%20), referral aktivasyonu'
                  },
                  {
                    id: 4, name: 'Gelişen — 6 Ayda 2+ Sipariş, Aktif', color: 'bg-blue-100 text-blue-800',
                    rule: 'Son 180 günde sipariş ≥ 2, son sipariş < 60 gün',
                    ratio: '%10-15',
                    action: 'Cross-sell, replenishment, Pro upgrade teşviki, km takibi',
                    ch: 'E-posta, Meta DPA',
                    goal: 'Pro\'ya yükseltme, repeat frequency artışı'
                  },
                  {
                    id: 5, name: 'Soğuyan — 6 Ayda 2 Sipariş Ama Son 2 Ay Sessiz', color: 'bg-amber-100 text-amber-800',
                    rule: 'Son 180 günde sipariş ≥ 2, son sipariş 60-180 gün önce',
                    ratio: '%8-12',
                    action: '"Seni özledik" kampanyası + kişisel kupon + yeni ürün önerisi',
                    ch: 'E-posta, SMS, Meta',
                    goal: 'Yeniden aktifleştirme (%15-20 win-back CR)'
                  },
                  {
                    id: 6, name: 'Risk Altında — 3 Aydır Sipariş Yok', color: 'bg-orange-100 text-orange-800',
                    rule: 'Daha önce sipariş verdi, son sipariş 90-180 gün önce',
                    ratio: '%12-18',
                    action: 'Win-back Step 1: Kişiselleştirilmiş öneri + %15 kupon + stok/fiyat uyarısı',
                    ch: 'E-posta, SMS, Push',
                    goal: 'Churn önleme (%12-15 geri kazanım)'
                  },
                  {
                    id: 7, name: 'Kayıp — 6 Aydır Sipariş Yok', color: 'bg-red-100 text-red-800',
                    rule: 'Daha önce sipariş verdi, son sipariş > 180 gün',
                    ratio: '%15-20',
                    action: 'Win-back Step 2: Agresif teklif (%20-25 kupon), anket, son şans mesajı',
                    ch: 'E-posta, SMS',
                    goal: 'Son şans kurtarma (%5-10), geri gelemezse suppress'
                  },
                  {
                    id: 8, name: 'Yüksek Harcama — Tek Seferde ₺5K+', color: 'bg-purple-100 text-purple-800',
                    rule: 'Tek sipariş AOV ≥ ₺5.000 (herhangi bir zamanda)',
                    ratio: '%8-12',
                    action: 'VIP deneyim, kişisel danışman, premium ürün lansmanlarına davet',
                    ch: 'E-posta, Telefon',
                    goal: 'LTV maksimizasyonu, brand ambassador dönüşümü'
                  },
                  {
                    id: 9, name: 'Sepet Terk Eden — Kronik', color: 'bg-pink-100 text-pink-800',
                    rule: 'Son 30 günde 3+ sepet terk, sipariş = 0',
                    ratio: '%5-8',
                    action: 'Özel cart abandon akışı: fiyat garantisi + taksit bilgisi + canlı destek',
                    ch: 'E-posta, Push, WhatsApp',
                    goal: 'Cart recovery oranı %15+ (kronik segmentte)'
                  },
                  {
                    id: 10, name: 'Sadece İndirimde Alan', color: 'bg-yellow-100 text-yellow-800',
                    rule: 'Siparişlerin %80+\'ı kupon/kampanya döneminde',
                    ratio: '%10-15',
                    action: 'Değer odaklı içerik, ürün kalitesi vurgusu, early access (kupon yerine)',
                    ch: 'E-posta',
                    goal: 'Full-price satın alma oranı artışı (%10-15)'
                  },
                  {
                    id: 11, name: 'Tek Kategori Müşterisi', color: 'bg-indigo-100 text-indigo-800',
                    rule: 'Tüm siparişleri tek kategoride (ör. sadece ayakkabı)',
                    ratio: '%20-25',
                    action: 'Çapraz kategori keşfi: "Ayakkabınla uyumlu..." + editorial içerik',
                    ch: 'E-posta, Meta DPA',
                    goal: 'Kategori çeşitliliği artışı, AOV +%25'
                  },
                  {
                    id: 12, name: 'Referral Champion', color: 'bg-teal-100 text-teal-800',
                    rule: 'Referral kodu ile 2+ kişi getirdi',
                    ratio: '%1-3',
                    action: 'Ambassador programı, özel referral komisyonu, marka içerik işbirliği',
                    ch: 'E-posta, Telefon',
                    goal: 'Organik büyüme, CAC düşürme (₺50/yeni müşteri)'
                  },
                ].map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="py-2 px-3 font-mono text-gray-400 align-top">{r.id}</td>
                    <td className="py-2 px-3 align-top whitespace-nowrap">
                      <span className={`inline-block px-1.5 py-0.5 rounded font-bold text-[9px] ${r.color}`}>{r.name}</span>
                    </td>
                    <td className="py-2 px-3 text-gray-600 align-top leading-tight">{r.rule}</td>
                    <td className="py-2 px-3 font-mono font-bold text-gray-700 align-top text-center">{r.ratio}</td>
                    <td className="py-2 px-3 text-gray-600 align-top leading-tight">{r.action}</td>
                    <td className="py-2 px-3 text-gray-500 align-top whitespace-nowrap">{r.ch}</td>
                    <td className="py-2 px-3 text-gray-600 align-top leading-tight font-medium">{r.goal}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Marka Bazlı Segmentler */}
          <div className="mt-4">
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">Marka Bazlı Mikro Segmentler</p>
            <div className="overflow-x-auto rounded-xl border">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-800 text-white text-[9px] uppercase tracking-wider">
                    <th className="py-2 px-3 text-left font-semibold">Segment</th>
                    <th className="py-2 px-3 text-left font-semibold">Kural</th>
                    <th className="py-2 px-3 text-left font-semibold">Aksiyon</th>
                    <th className="py-2 px-3 text-left font-semibold">Mesaj Örneği</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-[10px]">
                  {[
                    {
                      name: 'On Sadığı — 2+ On Sipariş',
                      color: 'bg-gray-900 text-white',
                      rule: 'On markalı ürünlerden 2+ farklı sipariş',
                      action: 'Yeni sezon On lansmanına erken erişim, On running community daveti',
                      msg: '"On Cloudmonster\'ını sevdin — yeni On Cloudsurfer 2 sana özel 1 gün erken!"'
                    },
                    {
                      name: 'Hoka Fan — 2+ Hoka Sipariş',
                      color: 'bg-blue-900 text-white',
                      rule: 'Hoka markalı ürünlerden 2+ farklı sipariş',
                      action: 'Hoka limited edition bildirimi, Hoka trail koşu etkinliği daveti',
                      msg: '"Hoka Clifton 10 stoklara geldi — Hoka ailesine özel %10 erken erişim"'
                    },
                    {
                      name: 'Asics Koşucusu',
                      color: 'bg-red-700 text-white',
                      rule: 'Asics markalı 1+ ayakkabı + aksesuar siparişi',
                      action: 'Asics Gel serisi yenileme hatırlatma, koşu programı içerik serisi',
                      msg: '"Gel-Kayano\'n 400 km\'ye yaklaştı — yeni Gel-Nimbus ile tanış"'
                    },
                    {
                      name: 'Multi-Brand — On + Skechers',
                      color: 'bg-violet-100 text-violet-800',
                      rule: 'Hem On hem Skechers\'dan sipariş vermiş',
                      action: 'Hem performans hem günlük kullanım profili → Brooks veya Hoka cross-sell',
                      msg: '"Koşu ve günlük ayakkabını farklı markalardan seçiyorsun — sana özel Brooks Ghost önerisi"'
                    },
                    {
                      name: 'Premium Hunter — Salomon + On',
                      color: 'bg-amber-100 text-amber-800',
                      rule: 'Salomon + On sipariş geçmişi, AOV > ₺5.000',
                      action: 'Ultra-premium segment, yeni marka lansmanı daveti, VIP private sale',
                      msg: '"Trail\'den asfalta — senin için seçtiğimiz premium koleksiyon hazır"'
                    },
                    {
                      name: 'Skechers Günlük Kullanıcı',
                      color: 'bg-sky-100 text-sky-800',
                      rule: 'Skechers 1+ sipariş, performans ayakkabı alımı yok',
                      action: 'Performans ayakkabı keşif kampanyası — On/Asics/Brooks deneyim teklifi',
                      msg: '"Günlük konforunu seviyorsun — koşu deneyimini bir üst seviyeye taşı"'
                    },
                    {
                      name: 'Brooks Sadığı',
                      color: 'bg-emerald-100 text-emerald-800',
                      rule: 'Brooks markalı 2+ sipariş',
                      action: 'Brooks Glycerin/Ghost yenileme döngüsü, beden rehberi, koşu planı',
                      msg: '"Brooks Ghost 16 çıktı — önceki Ghost\'unu beğendiysen bunu çok seveceksin"'
                    },
                    {
                      name: 'Saucony Niş Koşucu',
                      color: 'bg-orange-100 text-orange-800',
                      rule: 'Saucony markalı sipariş vermiş (herhangi)',
                      action: 'Saucony Endorphin serisi, yarış ayakkabı önerisi, maraton içerik serisi',
                      msg: '"Saucony Endorphin Speed ile yarış günü avantajını hisset"'
                    },
                    {
                      name: 'Marka Değiştiren',
                      color: 'bg-pink-100 text-pink-800',
                      rule: '3+ sipariş, her seferinde farklı marka',
                      action: 'Ayak analizi quiz, "sana en uygun marka" kişisel öneri, comparative içerik',
                      msg: '"Farklı markaları deniyorsun — sana en uygun koşu ayakkabını bulmana yardım edelim"'
                    },
                    {
                      name: 'Ayakkabı + Tekstil Combo',
                      color: 'bg-teal-100 text-teal-800',
                      rule: 'Ayakkabı + giyim siparişi olan (herhangi marka)',
                      action: 'Outfit bazlı cross-sell, sezon geçişi kombin önerileri',
                      msg: '"On ayakkabın + Salomon şortunla bu sezonun koşu kombini hazır"'
                    },
                  ].map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="py-2 px-3 align-top whitespace-nowrap">
                        <span className={`inline-block px-1.5 py-0.5 rounded font-bold text-[9px] ${r.color}`}>{r.name}</span>
                      </td>
                      <td className="py-2 px-3 text-gray-600 align-top leading-tight">{r.rule}</td>
                      <td className="py-2 px-3 text-gray-600 align-top leading-tight">{r.action}</td>
                      <td className="py-2 px-3 text-gray-500 align-top leading-tight italic">"{r.msg}"</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Segment Lifecycle Funnel */}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-1.5">
            {[
              { label: 'Yeni Kayıt', color: 'bg-sky-500', w: 'w-full' },
              { label: 'İlk Sipariş', color: 'bg-emerald-500', w: 'w-[90%]' },
              { label: 'Gelişen', color: 'bg-blue-500', w: 'w-[72%]' },
              { label: 'Aktif Sadık', color: 'bg-emerald-700', w: 'w-[45%]' },
              { label: 'Elite / VIP', color: 'bg-amber-500', w: 'w-[25%]' },
            ].map((s, i) => (
              <div key={i} className={`${s.w} ${s.color} text-white text-center py-1.5 rounded text-[9px] font-bold tracking-wider`}>
                {s.label}
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between mt-1 px-2">
            <span className="text-[8px] text-gray-400 font-semibold">GENIŞ KİTLE</span>
            <span className="text-[8px] text-gray-400">← churn / win-back →</span>
            <span className="text-[8px] text-gray-400 font-semibold">YÜKSEK LTV</span>
          </div>

          <div className="p-3 bg-gray-50 rounded-lg border mt-2">
            <p className="text-[10px] text-gray-500">
              <span className="font-bold text-gray-700">Dinamik Segmentasyon:</span> Segmentler gerçek zamanlı güncellenir — bir müşteri sipariş verdikçe, inaktif kaldıkça veya davranış değiştirdikçe otomatik olarak doğru segmente taşınır.
              Her segment geçişi ilgili otomasyon akışını tetikler. Segmentler arasında çakışma olması durumunda öncelik sırası: Aktif Sadık {'>'} Risk Altında {'>'} Soğuyan {'>'} genel.
            </p>
          </div>
        </div>
      </div>

      {/* ───────── 8. Otomasyon Akış Özeti & KPI Hedefleri ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">8. Otomasyon Akış Özeti & KPI Hedefleri</h2>
          <p className="text-xs text-gray-500 mt-1">Tüm otomasyon senaryolarının özet tablosu ve hedef KPI'lar</p>
        </div>
        <div className="p-6 space-y-6">

          <div className="overflow-x-auto rounded-xl border">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                  <th className="py-3 px-4 text-left font-semibold">#</th>
                  <th className="py-3 px-4 text-left font-semibold">Otomasyon Akışı</th>
                  <th className="py-3 px-4 text-left font-semibold">Tetik</th>
                  <th className="py-3 px-4 text-left font-semibold">Mesaj Sayısı</th>
                  <th className="py-3 px-4 text-left font-semibold">Kanallar</th>
                  <th className="py-3 px-4 text-left font-semibold">Hedef OR</th>
                  <th className="py-3 px-4 text-left font-semibold">Hedef CR</th>
                  <th className="py-3 px-4 text-left font-semibold">Beklenen Gelir Etkisi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  { id: 1, name: 'Welcome Serisi', trigger: 'Yeni kayıt / newsletter', msgs: '7 (7 gün)', ch: 'E-posta, SMS', or: '%50-60', cr: '%12-18', rev: 'İlk satın alma dönüşümü' },
                  { id: 2, name: 'Browse Abandonment', trigger: '2+ ürün görüntüleme, 1s inaktif', msgs: '4 (48 saat)', ch: 'Push, E-posta, SMS', or: '%35-45', cr: '%3-5', rev: 'Toplam gelirin %2-4\'ü' },
                  { id: 3, name: 'Cart Abandonment', trigger: 'Sepete ekleme, 30dk+ inaktif', msgs: '6 (72 saat)', ch: 'Push, E-posta, SMS', or: '%45-55', cr: '%8-15', rev: 'Toplam gelirin %5-10\'u' },
                  { id: 4, name: 'Post-Purchase (0-7 gün)', trigger: 'Sipariş tamamlama', msgs: '7 (7 gün)', ch: 'E-posta, SMS, Push', or: '%65-75', cr: 'NPS >8', rev: 'Cross-sell AOV +%15' },
                  { id: 5, name: 'İkinci Sipariş Teşviki', trigger: '21 gün + 2. sipariş yok', msgs: '3 (14 gün)', ch: 'E-posta, SMS', or: '%40-50', cr: '%20-28', rev: '2. sipariş oranı x2' },
                  { id: 6, name: 'Cross-sell / Upsell', trigger: 'Satın alma sonrası 5-14 gün', msgs: '3 (10 gün)', ch: 'E-posta', or: '%30-40', cr: '%5-8', rev: 'AOV +%20-30' },
                  { id: 7, name: 'Replenishment', trigger: 'Ürün ömür döngüsü eşiği', msgs: '2 (7 gün)', ch: 'E-posta, Push', or: '%35-45', cr: '%10-15', rev: 'Repeat rate +%12' },
                  { id: 8, name: 'Win-Back (60 gün)', trigger: '60 gün inaktif', msgs: '4 (21 gün)', ch: 'E-posta, SMS', or: '%25-35', cr: '%8-15', rev: 'Churn azaltma %15-25' },
                  { id: 9, name: 'Win-Back (90 gün)', trigger: '90 gün inaktif', msgs: '3 (14 gün)', ch: 'E-posta, SMS', or: '%15-25', cr: '%5-10', rev: 'Son şans kurtarma %8-12' },
                  { id: 10, name: 'Doğum Günü', trigger: 'Doğum gününden 3 gün önce', msgs: '3 (10 gün)', ch: 'E-posta, SMS', or: '%55-65', cr: '%8-12', rev: 'AOV +%25 (hediye motivasyonu)' },
                  { id: 11, name: 'Fiyat Düşüşü Bildirimi', trigger: 'Wishlist/viewed ürün fiyatı düşünce', msgs: '2 (24 saat)', ch: 'E-posta, Push', or: '%50-60', cr: '%12-18', rev: 'Wishlist dönüşüm x3' },
                  { id: 12, name: 'Back in Stock', trigger: 'Haber ver + stok girişi', msgs: '3 (48 saat)', ch: 'E-posta, SMS, Push', or: '%60-70', cr: '%15-25', rev: 'Kaçırılan satış recovery' },
                  { id: 13, name: 'VIP Upgrade Teşviki', trigger: 'Sonraki seviyeye %80 yaklaşma', msgs: '3 (21 gün)', ch: 'E-posta', or: '%40-50', cr: '%15-20', rev: 'LTV +%30 (tier upgrade)' },
                  { id: 14, name: 'Referral Programı', trigger: 'NPS 9-10 + 2+ sipariş', msgs: '2 (sürekli)', ch: 'E-posta', or: '%35-45', cr: '%5-8 referral', rev: 'CAC -₺50/yeni müşteri' },
                  { id: 15, name: 'Sezon Geçişi', trigger: 'Mart/Eylül sezon değişimi', msgs: '3 (14 gün)', ch: 'E-posta', or: '%30-40', cr: '%4-6', rev: 'Sezonsal satış +%15' },
                  { id: 16, name: 'Km/Kullanım Takibi', trigger: 'Tahmini ayakkabı 300+ km', msgs: '2 (14 gün)', ch: 'E-posta', or: '%45-55', cr: '%12-18', rev: 'Yenileme satış hedefli' },
                  { id: 17, name: 'Sadakat Puan Hatırlatma', trigger: 'Puan bakiyesi ≥ eşik değeri', msgs: '1 (aylık)', ch: 'E-posta, Push', or: '%35-45', cr: '%8-12', rev: 'Puan kullanım ve ziyaret artışı' },
                  { id: 18, name: 'Re-Permission', trigger: '120+ gün inaktif, e-posta teslim sorunu', msgs: '2 (7 gün)', ch: 'E-posta', or: '%10-15', cr: 'Liste temizliği', rev: 'Deliverability iyileştirme' },
                ].map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 font-mono text-gray-400">{r.id}</td>
                    <td className="py-3 px-4 font-bold text-gray-900 whitespace-nowrap">{r.name}</td>
                    <td className="py-3 px-4 text-gray-600 text-xs">{r.trigger}</td>
                    <td className="py-3 px-4 font-mono text-center">{r.msgs}</td>
                    <td className="py-3 px-4 text-gray-600 text-xs">{r.ch}</td>
                    <td className="py-3 px-4 font-mono font-bold text-blue-700 text-center">{r.or}</td>
                    <td className="py-3 px-4 font-mono font-bold text-emerald-700 text-center">{r.cr}</td>
                    <td className="py-3 px-4 text-gray-600 text-xs">{r.rev}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Genel KPI Hedefleri */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider">Genel CRM KPI Hedefleri — Yıllık</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {[
                { label: 'E-posta Gelir Payı', current: '%8-10', target: '%20-25', icon: '📧' },
                { label: 'SMS Gelir Payı', current: '%1-2', target: '%5-8', icon: '📱' },
                { label: '2. Sipariş Oranı (60 gün)', current: '%10-12', target: '%25-30', icon: '🔄' },
                { label: 'Repeat Purchase Rate', current: '%15-18', target: '%35-40', icon: '💎' },
                { label: 'Average Order Value', current: '₺3.500', target: '₺4.500+', icon: '' },
                { label: 'Customer Lifetime Value', current: '₺8.000', target: '₺18.000+', icon: '📈' },
                { label: 'Cart Abandon Recovery', current: '%3-5', target: '%12-15', icon: '🛍️' },
                { label: 'NPS Skoru', current: '7.2', target: '8.5+', icon: '' },
                { label: 'E-posta Open Rate', current: '%18-22', target: '%35-40', icon: '📬' },
                { label: 'E-posta Click Rate', current: '%2-3', target: '%5-7', icon: '👆' },
                { label: 'Churn Rate (yıllık)', current: '%70-75', target: '%50-55', icon: '⚠️' },
                { label: 'Referral Oranı', current: '<%1', target: '%5-8', icon: '' },
              ].map((kpi, i) => (
                <div key={i} className="rounded-xl border p-4 hover:shadow-md transition-shadow">
                  <div className="mb-2">
                    <p className="text-xs font-bold text-gray-500 uppercase">{kpi.label}</p>
                  </div>
                  <div className="flex items-end gap-3">
                    <div>
                      <p className="text-[10px] text-gray-400">Mevcut</p>
                      <p className="text-sm font-mono text-gray-400 line-through">{kpi.current}</p>
                    </div>
                    <div className="text-emerald-600">
                      <span className="text-lg">→</span>
                    </div>
                    <div>
                      <p className="text-[10px] text-emerald-600 font-bold">Hedef</p>
                      <p className="text-lg font-black text-emerald-700">{kpi.target}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CRM Teknoloji Stack */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider">Önerilen CRM Teknoloji Stack</h3>
            <div className="overflow-x-auto rounded-xl border">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                    <th className="py-3 px-4 text-left font-semibold">Katman</th>
                    <th className="py-3 px-4 text-left font-semibold">Araç / Platform</th>
                    <th className="py-3 px-4 text-left font-semibold">Kullanım Alanı</th>
                    <th className="py-3 px-4 text-left font-semibold">Alternatif</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { layer: 'ESP (E-posta)', tool: 'Klaviyo', usage: 'E-posta otomasyonları, segmentasyon, A/B test, flow builder', alt: 'Mailchimp, Brevo (Sendinblue)' },
                    { layer: 'SMS', tool: 'Klaviyo SMS / Netgsm', usage: 'Transactional + marketing SMS, opt-in yönetimi', alt: 'Twilio, İleti Merkezi' },
                    { layer: 'CDP', tool: 'Segment / Klaviyo CDP', usage: 'Veri birleştirme, unified profile, event tracking', alt: 'RudderStack, mParticle' },
                    { layer: 'Push Notification', tool: 'OneSignal', usage: 'Web + mobil push, in-app mesajlaşma', alt: 'Firebase Cloud Messaging' },
                    { layer: 'Analytics', tool: 'GA4 + Glowytics', usage: 'Davranış analizi, attribution, funnel analizi', alt: 'Mixpanel, Amplitude' },
                    { layer: 'Review & UGC', tool: 'Judge.me / Yotpo', usage: 'Ürün yorumları, fotoğraf UGC, widget', alt: 'Stamped.io, Trustpilot' },
                    { layer: 'Loyalty', tool: 'Smile.io / Custom', usage: 'Puan sistemi, seviye yönetimi, referral', alt: 'LoyaltyLion, Yotpo Loyalty' },
                    { layer: 'Personalization', tool: 'Klaviyo + Custom ML', usage: 'Ürün önerileri, dinamik içerik, A/B test', alt: 'Dynamic Yield, Nosto' },
                  ].map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 font-bold text-gray-900 whitespace-nowrap">{r.layer}</td>
                      <td className="py-3 px-4 font-medium text-emerald-700">{r.tool}</td>
                      <td className="py-3 px-4 text-gray-600">{r.usage}</td>
                      <td className="py-3 px-4 text-gray-400">{r.alt}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* CRM Disclaimer */}
          <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
            <p className="text-xs text-emerald-800">
              <span className="font-bold">CRM Stratejisi Notu:</span> Bu doküman, High5'ın mevcut müşteri tabanı ve premium spor perakende sektörü benchmark'larına dayalı kapsamlı bir CRM yol haritasıdır.
              Otomasyon akışları, frekans optimizasyonu ve segment tanımları ilk 90 günlük pilot süreçte A/B testleriyle doğrulanacak ve optimize edilecektir.
              KVKK uyumu, açık rıza yönetimi ve opt-out süreçleri her kanal için ayrı ayrı yapılandırılmalıdır.
            </p>
          </div>
        </div>
      </div>

      {/* ───────── 9. OMNICHANNEL STRATEGY ───────── */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b bg-gradient-to-r from-indigo-600 to-purple-600">
          <h2 className="text-lg font-bold text-white">9. Omnichannel Strategy — Global Best Practices for High5 Physical Retail</h2>
          <p className="text-xs text-indigo-100 mt-1">Bridging online excellence with world-class in-store experience</p>
        </div>
        <div className="p-6 space-y-8">

          {/* Executive Summary */}
          <div className="p-5 bg-indigo-50 rounded-xl border border-indigo-100">
            <h3 className="text-sm font-bold text-indigo-900 mb-2">EXECUTIVE SUMMARY</h3>
            <p className="text-sm text-indigo-800 leading-relaxed">
              As High5 prepares to open physical retail stores, the opportunity is not to simply "add stores" but to build a <strong>unified commerce ecosystem</strong> where
              online and offline channels amplify each other. Omnichannel customers have <strong>30% higher lifetime value</strong> than single-channel customers, and brands with
              robust omnichannel engagement retain <strong>89% of customers</strong> vs. 33% for weak omnichannel brands. The following framework is built on proven strategies from
              Nike, Zara/Inditex, Nordstrom, Sephora, and Target — adapted specifically for a premium athletic retail brand.
            </p>
          </div>

          {/* 9.1 Ship-from-Store */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">1</span>
              Ship-from-Store — Every Store is a Micro-Warehouse
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="rounded-lg border p-5 space-y-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Why It Matters</p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>· <strong>Target</strong> fulfills 95% of all orders through its ~2,000 store locations after a $3B investment</li>
                  <li>· Over 80% of online orders ship same-day, with 80% of same-day deliveries arriving within 3 hours</li>
                  <li>· Fulfillment costs dropped ~40% when shifting to store-as-hub model</li>
                  <li>· Ship-from-store can be <strong>30-50% more cost-efficient</strong> than shipping from a central DC (Bain)</li>
                  <li>· <strong>Zara</strong> treats its website as "just another interface into the same inventory and logistics brain"</li>
                </ul>
              </div>
              <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-5 space-y-3">
                <p className="text-xs font-bold text-indigo-700 uppercase tracking-wider">High5 Recommendation</p>
                <ul className="space-y-2 text-sm text-indigo-800">
                  <li>· Design every new store as a <strong>dual-purpose location</strong>: retail showroom + micro-fulfillment center</li>
                  <li>· Size backrooms ~30% larger than pure-retail design to accommodate packing/shipping stations</li>
                  <li>· Implement an Order Management System (OMS) that automatically routes orders to the <strong>nearest store with stock</strong></li>
                  <li>· Start with high-volume locations in areas furthest from the main warehouse</li>
                  <li>· Automate shipping label generation and carrier selection per order</li>
                </ul>
              </div>
            </div>
          </div>

          {/* 9.2 Real-Time Inventory Visibility */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">2</span>
              Real-Time Inventory Visibility — See Before You Buy
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="rounded-lg border p-5 space-y-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Industry Leaders</p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>· <strong>Nike</strong> has embedded RAIN RFID labels into hundreds of millions of products — achieving <strong>95-99% inventory accuracy</strong> (vs. ~65% with barcodes)</li>
                  <li>· Customers see real-time stock at nearby stores via the Nike App before ordering</li>
                  <li>· <strong>Zara</strong> streams RFID data from 6,000+ stores to a central data hub operating 24/7</li>
                  <li>· Zara's "Store Mode" lets customers check real-time inventory at any location via the app</li>
                  <li>· 85% of retailers with full RFID adoption use it to enable BOPIS and ship-from-store</li>
                </ul>
              </div>
              <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-5 space-y-3">
                <p className="text-xs font-bold text-indigo-700 uppercase tracking-wider">High5 Recommendation</p>
                <ul className="space-y-2 text-sm text-indigo-800">
                  <li>· Implement <strong>RFID tagging from the first SKU</strong> in the first store — cost is under $0.10/tag at volume</li>
                  <li>· Embed RFID tags in shoe boxes or tongue labels</li>
                  <li>· Show on the website/app: "Available at [Store Name] — Ready for pickup in 1 hour"</li>
                  <li>· This is <strong>foundational</strong> — without inventory accuracy, BOPIS, ship-from-store, and endless aisle all fail</li>
                  <li>· RFID retail software market will more than triple between 2025-2030, reaching $1B+</li>
                </ul>
              </div>
            </div>

            {/* RFID Tech Stack */}
            <div className="mt-4 overflow-x-auto rounded-xl border">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                    <th className="py-3 px-4 text-left font-semibold">Layer</th>
                    <th className="py-3 px-4 text-left font-semibold">Components</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { layer: 'Item-Level Tagging', comp: 'RAIN RFID tags on every SKU (embedded in packaging or hangtag)' },
                    { layer: 'In-Store Hardware', comp: 'Fixed RFID readers at entrances/exits, handheld scanners, overhead readers for stockroom' },
                    { layer: 'Middleware', comp: 'RFID event processing engine, deduplication, real-time data pipeline' },
                    { layer: 'Central Platform', comp: 'Cloud-based inventory management with API-first architecture' },
                    { layer: 'Consumer-Facing', comp: 'App/website integration showing real-time store-level stock availability' },
                  ].map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 font-bold text-gray-900 whitespace-nowrap">{r.layer}</td>
                      <td className="py-3 px-4 text-gray-600">{r.comp}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 9.3 BOPIS & BORIS */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">3</span>
              BOPIS & BORIS — The Two Pillars of Omnichannel Convenience
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* BOPIS */}
              <div className="rounded-lg border p-5 space-y-3">
                <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider">BOPIS — Buy Online, Pick Up In Store</p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>· US click-and-collect sales: <strong>$177.9B in 2026</strong> (+15.3% YoY)</li>
                  <li>· On track to become a <strong>$500B+ market by 2033</strong></li>
                  <li>· <strong>85% of BOPIS shoppers</strong> make an additional in-store purchase during pickup</li>
                  <li>· 47% make impulse buys — +25% rise in basket size</li>
                  <li>· <strong>Nordstrom</strong>: BOPIS is 15%+ of all digital orders, pickup within 1 hour</li>
                  <li>· Retailers with 3+ channels achieve <strong>287% higher purchase rates</strong></li>
                </ul>
              </div>
              {/* BORIS */}
              <div className="rounded-lg border p-5 space-y-3">
                <p className="text-xs font-bold text-blue-700 uppercase tracking-wider">BORIS — Buy Online, Return In Store</p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>· <strong>70% of online purchases</strong> returned in-store at leading retailers</li>
                  <li>· <strong>85% of in-store return visits</strong> resulted in additional purchases</li>
                  <li>· Up to <strong>70% of in-store returners</strong> buy something else before leaving</li>
                  <li>· Customer satisfaction increased <strong>20%</strong> after implementing BORIS</li>
                  <li>· 68% of retailers saw a <strong>decrease in fraudulent returns</strong></li>
                  <li>· For sneakers, <strong>sizing is the #1 return reason</strong> — BORIS turns a return into a size exchange + upsell</li>
                </ul>
              </div>
            </div>
            <div className="mt-4 p-4 bg-indigo-50 rounded-xl border border-indigo-100">
              <p className="text-xs text-indigo-800">
                <span className="font-bold">High5 Priority:</span> BORIS is arguably <strong>more important than BOPIS</strong> for a sneaker brand. Every store should accept online returns with
                instant refund/exchange. The associate sees the customer's online order history, processes the return, and immediately suggests an alternative size or model.
                The "return trip" becomes a <strong>sales opportunity</strong>.
              </p>
            </div>
          </div>

          {/* 9.4 Unified Customer Profile */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">4</span>
              Unified Customer Profile — One Customer, One Identity, Every Channel
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="rounded-lg border p-5 space-y-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">The Gold Standard: Sephora</p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>· Merged digital and retail teams in 2017 to build unified customer profiles</li>
                  <li>· <strong>Beauty Insider program</strong>: ~25 million members</li>
                  <li>· Loyalty members account for <strong>up to 80% of total sales</strong></li>
                  <li>· App users spend <strong>2x more annually</strong> and purchase 2x as frequently</li>
                  <li>· Three tiers (Insider, VIB, Rouge) create aspiration and engagement</li>
                  <li>· In-store scanning of loyalty card links every touchpoint to the same profile</li>
                </ul>
              </div>
              <div className="rounded-lg border p-5 space-y-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Business Impact</p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>· Personalization generates <strong>40% more revenue</strong> than competitors (McKinsey)</li>
                  <li>· Unified profiles drive <strong>150% YoY omnichannel GMV growth</strong> (Shopify data)</li>
                  <li>· Omnichannel customers have <strong>30% higher LTV</strong> than single-channel</li>
                  <li>· Brands with strong omnichannel retain <strong>89%</strong> vs. 33% for weak brands</li>
                  <li>· Customers with high-quality omnichannel experience are <strong>3.6x more likely</strong> to make additional purchases</li>
                </ul>
              </div>
            </div>
            <div className="mt-4 p-4 bg-indigo-50 rounded-xl border border-indigo-100">
              <p className="text-xs text-indigo-800">
                <span className="font-bold">High5:</span> Build the unified profile from day one. The High5 app/account should be the single ID. When someone with an online account walks into a
                store, scanning a QR code from their app should instantly pull up: <strong>purchase history, wishlist, size preferences, and loyalty tier</strong>. This single profile
                is the backbone of everything else.
              </p>
            </div>
          </div>

          {/* 9.5 Endless Aisle */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">5</span>
              Endless Aisle — The Store Carries the Entire Catalog
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="rounded-lg border p-5 space-y-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">How It Works</p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>· Customer's desired size/color not in stock? Order from an <strong>in-store kiosk or tablet</strong> for home delivery</li>
                  <li>· <strong>Staples</strong>: Endless aisle kiosks give access to 100,000+ items vs. ~8,000 in-store</li>
                  <li>· QR codes on shelves link to reviews, alternate colorways, and size recommendations</li>
                  <li>· Interface must be intuitive with minimal learning curve — also mobile-accessible</li>
                  <li>· Show real-time availability at other nearby stores (customer might prefer to drive)</li>
                </ul>
              </div>
              <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-5 space-y-3">
                <p className="text-xs font-bold text-indigo-700 uppercase tracking-wider">High5 Recommendation</p>
                <ul className="space-y-2 text-sm text-indigo-800">
                  <li>· A physical store can only display <strong>~200-300 SKUs per wall</strong></li>
                  <li>· With endless aisle, the store effectively carries the <strong>entire High5 catalog</strong></li>
                  <li>· Place <strong>tablets on each display wall</strong>: "Don't see your size? Order it here for free delivery"</li>
                  <li>· Every "out of stock" moment becomes a <strong>saved sale</strong> rather than a walkout</li>
                  <li>· Integrate with the same OMS/inventory system as the e-commerce site</li>
                </ul>
              </div>
            </div>
          </div>

          {/* 9.6 Clienteling & Personalization */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">6</span>
              Clienteling & In-Store Personalization
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div className="rounded-lg border p-5 space-y-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Nike House of Innovation</p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>· <strong>Scan to Try</strong>: Customer scans a barcode via Nike App, selects size, item is brought to a fitting room</li>
                  <li>· <strong>Shop the Look</strong>: Scan QR on a mannequin to see the entire outfit, order pieces to fitting room</li>
                  <li>· <strong>Instant Checkout</strong>: Scan and pay via app — no checkout line needed</li>
                  <li>· Geo-fencing activates store-specific features when shoppers enter</li>
                </ul>
              </div>
              <div className="rounded-lg border p-5 space-y-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Sephora & Nordstrom</p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>· Associates access customer purchase history, preferences, and skin type via tablet</li>
                  <li>· Personalized product suggestions and post-visit follow-ups</li>
                  <li>· <strong>Nordstrom's "Store Mode"</strong>: book appointments with personal shoppers via mobile app</li>
                  <li>· Customers willing to pay <strong>up to 16% more</strong> for customized shopping experiences</li>
                </ul>
              </div>
              <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-5 space-y-3">
                <p className="text-xs font-bold text-indigo-700 uppercase tracking-wider">High5 Recommendation</p>
                <ul className="space-y-2 text-sm text-indigo-800">
                  <li>· Equip every associate with a <strong>clienteling tablet</strong></li>
                  <li>· When a loyalty member identifies themselves, the associate sees: past purchases, wishlist, size preferences</li>
                  <li>· AI-generated recommendations: "Customers who bought X also love Y"</li>
                  <li>· Transform associates from stock checkers into <strong>personal sneaker consultants</strong></li>
                </ul>
              </div>
            </div>
          </div>

          {/* 9.7 Click & Reserve / Try Before You Buy */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">7</span>
              Click & Reserve — Try Before You Buy
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="rounded-lg border p-5 space-y-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Consumer Demand</p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>· <strong>1 in 4 shoppers</strong> now prefers to test products before buying (18-31% adoption by market)</li>
                  <li>· <strong>50% of consumers</strong> find scheduling in-store shopping appointments relevant</li>
                  <li>· <strong>LIVELY</strong>: Appointment-based "fit sessions" generate ~30% of in-store revenue</li>
                  <li>· Appointment shopping: higher conversion, pre-prepared fitting rooms, 1-on-1 attention</li>
                  <li>· Reduces crowd anxiety for premium shoppers</li>
                </ul>
              </div>
              <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-5 space-y-3">
                <p className="text-xs font-bold text-indigo-700 uppercase tracking-wider">High5 "Reserve & Try"</p>
                <ul className="space-y-2 text-sm text-indigo-800">
                  <li>· Customer selects up to <strong>5 pairs</strong> on the website/app</li>
                  <li>· Chooses a store and books a <strong>30-minute slot</strong></li>
                  <li>· On arrival, selections are already in a fitting area with their name</li>
                  <li>· An associate is assigned for personal service</li>
                  <li>· Powerful for <strong>new sneaker drops</strong> — try in person before committing</li>
                  <li>· Creates a <strong>VIP-like experience</strong> that differentiates from commodity retail</li>
                </ul>
              </div>
            </div>
          </div>

          {/* 9.8 Unified Pricing, Promotions & Loyalty */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">8</span>
              Unified Pricing, Promotions & Loyalty
            </h3>
            <div className="rounded-lg border p-5 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Core Principle</p>
                  <p className="text-sm text-gray-700">Same product, same price whether buying online, in-app, or in any store. Loyalty points earned/redeemed identically across all channels.
                  Zero channel conflict, maximum trust.</p>
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Industry Examples</p>
                  <ul className="space-y-1.5 text-sm text-gray-700">
                    <li>· <strong>Target Circle</strong>: 1% back on every purchase across all channels</li>
                    <li>· <strong>Adidas</strong>: Growing membership from 150M to ~500M via unified DTC loyalty</li>
                    <li>· <strong>Sephora</strong>: Beauty Insider points accrue and redeem identically everywhere</li>
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-bold text-indigo-700 uppercase tracking-wider mb-2">High5 Policy</p>
                  <ul className="space-y-1.5 text-sm text-indigo-800">
                    <li>· <strong>"One price everywhere"</strong> from launch</li>
                    <li>· Same promotions online and in-store</li>
                    <li>· Member-exclusive early access to drops applies uniformly</li>
                    <li>· Online deals also available in-store when showing the app</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* 9.9 Last-Mile Innovation */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">9</span>
              Last-Mile Innovation — Get Your Kicks in 2 Hours
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              {[
                { label: 'Same-Day Delivery', stat: '99% of retailers', sub: 'plan to offer by 2025' },
                { label: 'Locker Market', stat: '$824.9M by 2035', sub: '10% CAGR growth' },
                { label: 'Micro-Fulfillment', stat: '$10B+ by 2026', sub: '24.3% annual growth' },
                { label: 'Delivery Speed', stat: '30-50% faster', sub: 'ship-from-store vs. central DC' },
              ].map((item, i) => (
                <div key={i} className="rounded-xl border p-4 text-center">
                  <p className="text-xs text-gray-500 mb-1">{item.label}</p>
                  <p className="text-lg font-black text-indigo-700">{item.stat}</p>
                  <p className="text-[10px] text-gray-400">{item.sub}</p>
                </div>
              ))}
            </div>
            <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100">
              <p className="text-xs text-indigo-800">
                <span className="font-bold">High5:</span> Partner with <strong>gig-economy delivery platforms</strong> (Getir, Trendyol Express, Yemeksepeti Banabi, or local couriers)
                to offer 2-hour delivery from the nearest store. Install <strong>smart lockers</strong> in high-traffic areas (malls, transit hubs, universities) for 24/7 pickup.
                The <strong>"get your new kicks in 2 hours"</strong> messaging is powerful for a sneaker brand.
              </p>
            </div>
          </div>

          {/* 9.10 Data & Analytics Integration */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">10</span>
              Data & Analytics — Measuring the Halo Effect
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="rounded-lg border p-5 space-y-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">The Store Halo Effect</p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>· After an online transaction, the average halo consumer makes <strong>2.1 in-store transactions within 15 days</strong></li>
                  <li>· Brands can unlock <strong>42% of unmeasured sales</strong> by including all platforms in attribution</li>
                  <li>· TikTok drives <strong>2.6x more offline conversions</strong> than other media channels</li>
                  <li>· Mall traffic grew ~1.8% YoY in 2025, with visit durations rising 3.3%</li>
                </ul>
              </div>
              <div className="rounded-lg border p-5 space-y-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Unified Attribution Framework</p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>· <strong>Online-to-Offline</strong>: Did an ad click lead to a store visit within 7 days?</li>
                  <li>· <strong>Offline-to-Online</strong>: Did a store visit lead to an online purchase within 15 days?</li>
                  <li>· <strong>Cross-Channel LTV</strong>: Total customer value across all touchpoints</li>
                  <li>· <strong>Incremental Lift</strong>: True incremental sales per channel, accounting for cannibalization</li>
                </ul>
              </div>
            </div>
            <div className="mt-4 p-4 bg-indigo-50 rounded-xl border border-indigo-100">
              <p className="text-xs text-indigo-800">
                <span className="font-bold">High5:</span> Deploy <strong>RetailNext or V-Count sensors</strong> in every store from day one. Integrate foot traffic data with Glowytics
                to build a unified attribution dashboard. Measure the "store halo" — when a new store opens, track the change in online orders from that zip code in the
                90 days before vs. after. This data is critical for justifying further store expansion.
              </p>
            </div>
          </div>

          {/* 9.11 Social Commerce meets Physical */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">11</span>
              Social Commerce Meets Physical Retail
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="rounded-lg border p-5 space-y-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">The Opportunity</p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>· US social commerce: <strong>$87B in 2025</strong>, will surpass $100B in 2026</li>
                  <li>· TikTok Shop: nearly <strong>20% of social commerce</strong> in 2025</li>
                  <li>· <strong>2 in 5 users</strong> who discovered a product on TikTok went to a physical store</li>
                  <li>· <strong>73% of Gen Z</strong> say social media is their main source for discovering new products</li>
                  <li>· TikTok livestreams drove <strong>84% YoY sales growth</strong> during BFCM 2025</li>
                </ul>
              </div>
              <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-5 space-y-3">
                <p className="text-xs font-bold text-indigo-700 uppercase tracking-wider">High5 "Content Corner"</p>
                <ul className="space-y-2 text-sm text-indigo-800">
                  <li>· Design a <strong>photogenic, branded area</strong> in every store with great lighting and an Instagram/TikTok-worthy backdrop</li>
                  <li>· Encourage customers to share unboxing or try-on moments</li>
                  <li>· Run monthly <strong>in-store live shopping events</strong> streamed on TikTok/Instagram</li>
                  <li>· Viewers buy directly through the stream or reserve for in-store pickup</li>
                  <li>· Offer <strong>bonus loyalty points</strong> for customers who post with #High5 and tag the brand</li>
                  <li>· For a sneaker brand targeting Gen Z — this is <strong>not optional, it is essential</strong></li>
                </ul>
              </div>
            </div>
          </div>

          {/* 9.12 Sustainability */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold">12</span>
              Sustainability — Ship-from-Store as a Green Strategy
            </h3>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                  <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-2">Environmental Benefits</p>
                  <ul className="space-y-1.5 text-sm text-emerald-800">
                    <li>· Ship-from-store <strong>significantly reduces delivery distances</strong> and carbon emissions</li>
                    <li>· 30-50% more cost-efficient = proportionally lower carbon footprint</li>
                    <li>· <strong>73% of UK consumers</strong> want orders shipped from the nearest store</li>
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-2">BORIS Sustainability</p>
                  <ul className="space-y-1.5 text-sm text-emerald-800">
                    <li>· In-store returns <strong>eliminate reverse logistics chain</strong></li>
                    <li>· Local return processed, restocked, resold from same location</li>
                    <li>· Electric vehicle fleets for last-mile delivery</li>
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-2">High5 Messaging</p>
                  <ul className="space-y-1.5 text-sm text-emerald-800">
                    <li>· "We ship from the store nearest to you, <strong>reducing delivery distances by up to 80%</strong>"</li>
                    <li>· Display <strong>estimated carbon savings</strong> at checkout</li>
                    <li>· Resonates strongly with Gen Z (sustainability = top-3 purchase factor)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Technology Stack Summary */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider">Recommended Technology Stack</h3>
            <div className="overflow-x-auto rounded-xl border">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                    <th className="py-3 px-4 text-left font-semibold">System</th>
                    <th className="py-3 px-4 text-left font-semibold">Purpose</th>
                    <th className="py-3 px-4 text-left font-semibold">Key Vendors (2025-2026)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { sys: 'OMS (Order Management)', purpose: 'Order routing, inventory pooling, fulfillment orchestration', vendors: 'Manhattan Associates, Fluent Commerce, Shopify OMS' },
                    { sys: 'POS (Point of Sale)', purpose: 'In-store transactions, unified with online', vendors: 'Shopify POS, Lightspeed, Square' },
                    { sys: 'CDP (Customer Data Platform)', purpose: 'Unified customer profiles, segmentation', vendors: 'Segment, mParticle, Bloomreach' },
                    { sys: 'RFID Platform', purpose: 'Item-level inventory tracking', vendors: 'Impinj (RAIN RFID), Zebra, SML' },
                    { sys: 'Clienteling App', purpose: 'Associate-facing customer insights', vendors: 'Endear, Tulip, Salesforce Clienteling' },
                    { sys: 'Foot Traffic Analytics', purpose: 'Store performance, conversion, heatmaps', vendors: 'RetailNext, V-Count, Aislelabs' },
                    { sys: 'Loyalty Engine', purpose: 'Points, tiers, cross-channel rewards', vendors: 'Open Loyalty, Yotpo, LoyaltyLion' },
                    { sys: 'Promotion Engine', purpose: 'Unified pricing/promotions across channels', vendors: 'Talon.One, SAP OPP, custom-built' },
                    { sys: 'Last-Mile Delivery', purpose: 'Same-day, express, locker network', vendors: 'Getir, Trendyol Express, local couriers' },
                    { sys: 'Analytics / Attribution', purpose: 'Cross-channel measurement, halo effect', vendors: 'GA4 + Glowytics, Prescient AI, Fospha' },
                  ].map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 font-bold text-gray-900 whitespace-nowrap">{r.sys}</td>
                      <td className="py-3 px-4 text-gray-600">{r.purpose}</td>
                      <td className="py-3 px-4 text-indigo-700 font-medium">{r.vendors}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Key Metrics */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider">Key Metrics to Track from Day One</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {[
                { label: 'BOPIS Adoption Rate', target: '15%+ of digital orders', benchmark: 'Nordstrom benchmark' },
                { label: 'Pickup Additional Purchase', target: '47-85%', benchmark: 'Industry average' },
                { label: 'Ship-from-Store Share', target: '80%+ of total orders', benchmark: 'Target benchmark' },
                { label: 'Inventory Accuracy (RFID)', target: '95-99%', benchmark: 'Nike / Zara level' },
                { label: 'Omnichannel LTV Uplift', target: '+30% vs. single-channel', benchmark: 'Industry average' },
                { label: 'Customer Retention', target: '89%', benchmark: 'vs. 33% weak omnichannel' },
                { label: 'Store Halo Effect', target: '2.1 extra transactions', benchmark: 'Within 15 days of online order' },
                { label: 'BORIS Conversion', target: 'Up to 70%', benchmark: 'Return visit -> new purchase' },
                { label: 'Loyalty Revenue Share', target: '80%', benchmark: 'Sephora benchmark' },
                { label: 'Avg. Delivery Time', target: '30-50% faster', benchmark: 'Ship-from-store vs. central DC' },
              ].map((m, i) => (
                <div key={i} className="rounded-xl border p-3 hover:shadow-md transition-shadow">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">{m.label}</p>
                  <p className="text-lg font-black text-indigo-700">{m.target}</p>
                  <p className="text-[10px] text-gray-400">{m.benchmark}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Phased Implementation Roadmap */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider">Phased Implementation Roadmap</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  phase: 'Phase 1',
                  title: 'Foundation',
                  timing: 'Pre-Store Opening',
                  color: 'bg-blue-600',
                  items: [
                    'RFID tagging on all products',
                    'Unified customer profile / CDP',
                    'OMS with ship-from-store capability',
                    'Loyalty program launch (app-based)',
                    'Unified pricing engine',
                  ]
                },
                {
                  phase: 'Phase 2',
                  title: 'Store Launch',
                  timing: 'Month 1-3',
                  color: 'bg-indigo-600',
                  items: [
                    'BOPIS and BORIS from day one',
                    'Curbside pickup',
                    'Endless aisle tablets on every wall',
                    'Foot traffic sensors installed',
                    'Associate clienteling tablets',
                  ]
                },
                {
                  phase: 'Phase 3',
                  title: 'Enhancement',
                  timing: 'Month 3-6',
                  color: 'bg-purple-600',
                  items: [
                    'Click & Reserve / appointment shopping',
                    'Same-day delivery via partners',
                    'In-store content creation areas',
                    'TikTok/IG live shopping from stores',
                    'Smart locker partnerships',
                  ]
                },
                {
                  phase: 'Phase 4',
                  title: 'Optimization',
                  timing: 'Month 6-12',
                  color: 'bg-pink-600',
                  items: [
                    'AI-powered order routing optimization',
                    'Predictive inventory per store',
                    'Proximity-based push notifications',
                    'Halo effect measurement & attribution',
                    'Carbon-savings display at checkout',
                  ]
                },
              ].map((p, i) => (
                <div key={i} className="rounded-xl border overflow-hidden">
                  <div className={`${p.color} px-4 py-3 text-white`}>
                    <p className="text-xs font-bold uppercase">{p.phase}</p>
                    <p className="text-sm font-bold">{p.title}</p>
                    <p className="text-[10px] opacity-80">{p.timing}</p>
                  </div>
                  <div className="p-4">
                    <ul className="space-y-2">
                      {p.items.map((item, j) => (
                        <li key={j} className="text-xs text-gray-700 flex items-start gap-2">
                          <span className="text-indigo-500 mt-0.5">&#10003;</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Omnichannel Disclaimer */}
          <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100">
            <p className="text-xs text-indigo-800">
              <span className="font-bold">Sources & References:</span> Target Corporate (2025), Nike/Impinj RFID Case Study, Zara/Inditex Digital Transformation Report,
              Sephora Beauty Insider Program, Nordstrom Omnichannel Strategy, Bain & Company Ship-from-Store Analysis, Capital One Shopping BOPIS Statistics (2025),
              Shopify Unified Commerce Report, McKinsey Personalization Report, TikTok Shop Social Commerce Data. All statistics represent 2025-2026 industry benchmarks
              and may vary by market. This framework should be adapted to local market conditions in Turkey.
            </p>
          </div>

        </div>
      </div>

    </div>
  );
}
